import SearchInput from '../components/ui/SearchInput';
import { formatDate as format, normalizeSearch, formatTime, formatName, formatPhone } from '../lib/format';
import SectionCard from '../components/ui/SectionCard';
import StatCard from '../components/ui/StatCard';
import PageContent from '../components/ui/PageContent';
import PageHeader from '../components/ui/PageHeader';
import StatusBadge from '../components/ui/StatusBadge';
import { TableEmpty, Table, TableRow, TableHeaderCell, TableCell, TableHead } from '../components/ui/Table';
import Textarea from '../components/ui/Textarea';
import FieldLabel from '../components/ui/FieldLabel';
import UiCheckbox from '../components/ui/Checkbox';
import UiSelect from '../components/ui/Select';
import UiInput from '../components/ui/Input';
import Button from '../components/ui/Button';
import FilterChip from '../components/ui/FilterChip';
import LocationSearchInput from '../components/LocationSearchInput';
import DraftTextarea from '../components/DraftTextarea';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import useTableSort from "../components/useTableSort";
import { getDropdownLabel, getDropdownValue, postWithAuth, randomString, sortAlphabetically } from "../common";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Dropdown from "../components/Dropdown";
import MyCalendar from "../components/Calendar";
import Input from "../components/Input";
import MyDropzone from "../components/MyDropzone";
import MyDropzoneMulti from "../components/MyDropzoneMulti";
import { isSameMonth, isSameDay, addDays } from 'date-fns';
import ModalComponent from '../components/Modal';
import AllocationPdfGenerator from "../components/AllocationPdfGenerator";
import ScopeOfWorks from "../components/ScopeOfWorks";
import { googleApiKey } from "../constants";
import Checkbox from "../components/Checkbox";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Section from "../components/Section";
import { Autocomplete } from "@react-google-maps/api";
import AllocationMarkerMap from "../components/AllocationMarkerMap";
import { CalendarIcon, ClockIcon, PlusIcon, Building2Icon, MapPinIcon, UsersIcon, TrashIcon, PhoneIcon, MessageSquareIcon, SmsIcon, DownloadIcon, CheckIcon } from "../components/NavIcons";
import CompactSuccessModal from "../components/CompactSuccessModal";
import LoadingPopup from "../components/LoadingPopup";
import { resolveStorageFileUrl } from "../storageUpload";
import { describeMessageResult, hasApp, MESSAGE_LIMIT, reachedAnyone } from '../utils/workerMessaging';
import { businessWallDateToDate, businessWallDateToIso, toBusinessWallDate } from '../utils/businessTime';
const formatLeaveDate = (date, includeYear = false) =>
    format(date, includeYear ? 'dd MMM yyyy' : 'dd MMM')

const formatLeavePeriod = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const crossesYear = start.getFullYear() !== end.getFullYear()
    return `${formatLeaveDate(start, crossesYear)} to\n${formatLeaveDate(end, crossesYear)}`
}

const formatAllocationCardAddress = value => {
    let address = String(value || '').trim()
    if (!address) return ''
    const statePattern = '(?:NSW|VIC|QLD|SA|WA|TAS|NT|ACT|New South Wales|Victoria|Queensland|South Australia|Western Australia|Tasmania|Northern Territory|Australian Capital Territory)'
    const removableSuffix = new RegExp(`(?:,\\s*|\\s+)(?:Australia|${statePattern}|\\d{4})\\s*$`, 'i')
    while (removableSuffix.test(address)) {
        address = address.replace(removableSuffix, '').trim()
    }
    return address.replace(/,\s*$/, '')
}

const getDefaultWorkerCapacity = (worker) => {
    const workerTypesValue = Array.isArray(worker?.workerType) ? worker.workerType : []
    return workerTypesValue.length === 1 ? workerTypesValue[0] : ''
}

const syncWorkerAssignments = (selectedWorkerIds, workers, currentAssignments = []) => {
    const assignmentMap = new Map((currentAssignments || []).map(item => [item.workerId, item.capacity || '']))
    return selectedWorkerIds.map(workerId => {
        const worker = workers.find(item => item.id === workerId)
        const workerTypesValue = Array.isArray(worker?.workerType) ? worker.workerType : []
        const fallbackCapacity = workerTypesValue.length === 1 ? workerTypesValue[0] : ''
        const selectedCapacity = assignmentMap.get(workerId) || fallbackCapacity
        return {
            workerId,
            capacity: workerTypesValue.includes(selectedCapacity) ? selectedCapacity : fallbackCapacity,
        }
    })
}

const getDocumentFileName = (picture) => {
    const rawUrl = typeof picture === 'string' ? picture : picture?.url
    if (!rawUrl) return 'Document'
    try {
        const withoutQuery = rawUrl.split('?')[0]
        const lastSegment = withoutQuery.split('/').pop() || 'Document'
        return decodeURIComponent(lastSegment)
    } catch (e) {
        return 'Document'
    }
}

const getDocumentDisplayName = (row) => {
    const explicitName = String(row?.name || '').trim()
    if (explicitName) return explicitName
    return getDocumentFileName(row?.picture || row)
}

const getGooglePlaceResolvedAddress = (place) => {
    if (!place) return ''
    const formattedAddress = String(place.formatted_address || '').trim()
    if (formattedAddress) return formattedAddress
    const vicinity = String(place.vicinity || '').trim()
    if (vicinity) return vicinity
    const placeName = String(place.name || '').trim()
    return placeName
}

const reverseGeocodeMapCoordinates = coordinates => new Promise((resolve, reject) => {
    if (!hasValidCoordinates(coordinates)) {
        reject(new Error('Invalid marker coordinates'))
        return
    }
    const Geocoder = window.google?.maps?.Geocoder
    if (!Geocoder) {
        reject(new Error('Google geocoding is unavailable'))
        return
    }
    const [lat, lng] = coordinates.map(Number)
    new Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
        const address = String(results?.[0]?.formatted_address || '').trim()
        if (status === 'OK' && address) {
            resolve(address)
            return
        }
        reject(new Error(status || 'Address not found'))
    })
})

const buildEmptyMultiDaySchedule = () => ([
    { dayIndex: 1, label: 'Monday', enabled: false, time: '', manuallySet: false },
    { dayIndex: 2, label: 'Tuesday', enabled: false, time: '', manuallySet: false },
    { dayIndex: 3, label: 'Wednesday', enabled: false, time: '', manuallySet: false },
    { dayIndex: 4, label: 'Thursday', enabled: false, time: '', manuallySet: false },
    { dayIndex: 5, label: 'Friday', enabled: false, time: '', manuallySet: false },
    { dayIndex: 6, label: 'Saturday', enabled: false, time: '', manuallySet: false },
    { dayIndex: 0, label: 'Sunday', enabled: false, time: '', manuallySet: false },
])

const buildAllocationMultiDayEditorState = (allocation, rows = [], { autoLoadGroup = false } = {}) => {
    const sourceRows = Array.isArray(rows) ? rows : []
    const groupRows = autoLoadGroup && allocation?.groupId
        ? sourceRows.filter(row => row?.groupId === allocation.groupId)
        : []
    const sortedRows = [...(groupRows.length > 0 ? groupRows : [allocation])].sort((a, b) => new Date(a?.date || 0) - new Date(b?.date || 0))
    const startRow = sortedRows[0] || allocation
    const endRow = sortedRows[sortedRows.length - 1] || allocation
    const nextSchedule = buildEmptyMultiDaySchedule()

    sortedRows.forEach(row => {
        const rowDate = toBusinessWallDate(row?.date)
        if (!rowDate) return
        if (Number.isNaN(rowDate.getTime())) return
        const timeValue = `${String(rowDate.getHours()).padStart(2, '0')}:${String(rowDate.getMinutes()).padStart(2, '0')}`
        const scheduleRow = nextSchedule.find(item => item.dayIndex === rowDate.getDay())
        if (!scheduleRow || scheduleRow.enabled) return
        scheduleRow.enabled = true
        scheduleRow.time = timeValue
        scheduleRow.manuallySet = true
    })

    const isMultiDay = autoLoadGroup && (sortedRows.length > 1 || (!!allocation?.groupId && !!allocation?.groupStartDate && !!allocation?.groupEndDate))
    return {
        isMultiDay,
        startDate: isMultiDay
            ? (toBusinessWallDate(startRow?.date) || toBusinessWallDate(allocation?.date) || new Date())
            : (toBusinessWallDate(allocation?.date) || new Date()),
        endDate: isMultiDay ? (toBusinessWallDate(endRow?.date) || '') : '',
        schedule: isMultiDay ? nextSchedule : buildEmptyMultiDaySchedule(),
    }
}

const normalizeDocumentArray = (value) => Array.isArray(value) ? value.filter(Boolean) : value ? [value] : []
const getAttachmentUrl = (row) => (
    row?.url
    || row?.filePath
    || row?.storagePath
    || row?.path
    || row?.fullPath
    || row?.picture?.url
    || row?.picture?.filePath
    || row?.picture?.storagePath
    || row?.picture?.path
    || row?.picture?.fullPath
    || ''
)

const mapSiteDocumentToAllocationDocument = (row) => ({
    ...row.picture,
    name: getDocumentDisplayName(row),
    note: row.note || '',
    date: row.date || '',
    source: 'site',
    siteDocumentUrl: row.picture?.url || '',
})

const hasAcceptedShift = (status) => {
    const normalizedStatus = String(status || '').toLowerCase()
    return normalizedStatus === 'accepted' || normalizedStatus === 'completed'
}

const allocationNeedsAttention = (allocation = {}) => {
    const assignedWorkersCount = (allocation.chosenWorkers || []).length
    const requiredWorkersCount = Number(allocation.requiredWorkers) || 0
    return !!(allocation.isIncomplete || allocation.showAsIncomplete || allocation.assetRequired || (requiredWorkersCount > 0 && assignedWorkersCount !== requiredWorkersCount))
}

const hasValidCoordinates = (value) => Array.isArray(value) && value.length >= 2 && Number.isFinite(Number(value[0])) && Number.isFinite(Number(value[1]))

const buildGoogleMapsLink = (coordinates = []) => hasValidCoordinates(coordinates)
    ? `https://www.google.com/maps?q=${Number(coordinates[0])},${Number(coordinates[1])}`
    : ''

const getMapCenter = (coordinates = []) => hasValidCoordinates(coordinates)
    ? { lat: Number(coordinates[0]), lng: Number(coordinates[1]) }
    : null

const buildMarkerCoordinateKey = (coordinates = []) => hasValidCoordinates(coordinates)
    ? `${Number(coordinates[0]).toFixed(6)},${Number(coordinates[1]).toFixed(6)}`
    : ''

const areCoordinatesEqual = (first = [], second = []) => {
    const firstKey = buildMarkerCoordinateKey(first)
    return !!firstKey && firstKey === buildMarkerCoordinateKey(second)
}

const getActivityActionLabel = (type) => {
    if (type === 'timesheet-signoff') return 'Timesheet Activity'
    if (type === 'created') return 'Created'
    if (type === 'updated') return 'Updated'
    if (type === 'document-added') return 'Document Added'
    if (type === 'document-deleted') return 'Document Deleted'
    return 'Message'
}

const AllocationCalendarIcon = () => <CalendarIcon style={{ width: 20, height: 20 }} />
const AllocationViewIcon = ({ type }) => {
    if (type === 'next') return <svg viewBox='0 0 24 24' aria-hidden='true'><path d='M5 12h14M13 6l6 6-6 6' /></svg>
    if (type === 'custom') return <svg viewBox='0 0 24 24' aria-hidden='true'><path d='M4 7h10M18 7h2M4 17h2M10 17h10M14 4v6M8 14v6' /></svg>
    if (type === 'today') return <svg viewBox='0 0 24 24' aria-hidden='true'><rect x='3' y='5' width='18' height='16' rx='2' /><path d='M16 3v4M8 3v4M3 10h18M8 14h3v3H8z' /></svg>
    return <svg viewBox='0 0 24 24' aria-hidden='true'><rect x='3' y='5' width='18' height='16' rx='2' /><path d='M16 3v4M8 3v4M3 10h18' /></svg>
}

const BufferedTextarea = ({ value, onCommit, onDraftChange, counter = false, ...props }) => {
    const [draft, setDraft] = useState(value || '')
    useEffect(() => setDraft(value || ''), [value])
    const textarea = <Textarea
        {...props}
        value={draft}
        onChange={event => {
            setDraft(event.target.value)
            onDraftChange?.(event.target.value)
        }}
        onBlur={() => {
            if (draft !== value) onCommit(draft)
        }}
    />
    // The count lives here with the draft, so typing re-renders only this box rather
    // than the whole page -- which is the reason this textarea is buffered at all.
    return counter ? <>{textarea}<p className='sendSmsCounter' aria-live='polite'>{draft.length}/{props.maxLength}</p></> : textarea
}

const Allocations = () => {

    const [loading, setLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [uploadingDocument, setUploadingDocument] = useState(false)
    const [isAdding, setIsAdding] = useState(false)
    const [allocations, setAllocations] = useState([])
    const [cancelledAllocations, setCancelledAllocations] = useState([])
    const [incompleteAllocations, setIncompleteAllocations] = useState([])
    const [clients, setClients] = useState([])
    const [sites, setSites] = useState([])
    const [workers, setWorkers] = useState([])
    const [referenceDataLoaded, setReferenceDataLoaded] = useState({ clients: false, sites: false, workers: false })
    const [assets, setAssets] = useState([])
    const [futureStatsDate] = useState(new Date())
    const [workerLeaves, setWorkerLeaves] = useState([])
    const [timeSheets, setTimeSheets] = useState([])

    const [editId, setEditId] = useState('')
    const [editIncompleteId, setEditIncompleteId] = useState('')
    const [formType, setFormType] = useState('allocation')
    const [chosenClient, setChosenClient] = useState('')
    const [chosenSite, setChosenSite] = useState('')
    const [chosenWorkers, setChosenWorkers] = useState([])
    const [chosenWorkerAssignments, setChosenWorkerAssignments] = useState([])
    const [chosenAssets, setChosenAssets] = useState([])
    const [teamLeaders, setTeamLeaders] = useState([])
    useEffect(() => {
        const workerIds = new Set(chosenWorkers.map(worker => worker?.value || worker))
        setTeamLeaders(previous => {
            const remaining = previous.filter(leader => workerIds.has(leader?.value || leader))
            return remaining.length === previous.length ? previous : remaining
        })
    }, [chosenWorkers])
    const [date, setDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [isMultiDay, setIsMultiDay] = useState(false)
    const [signsDroppedToSite, setSignsDroppedToSite] = useState(false)
    const [signsOnsiteAlready, setSignsOnsiteAlready] = useState(false)
    const [initialSignsDroppedToSite, setInitialSignsDroppedToSite] = useState(false)
    const [assetRequired, setAssetRequired] = useState(false)
    const [requiredWorkers, setRequiredWorkers] = useState('')
    const [scopeOfWorks, setScopeOfWorks] = useState('')
    const [comments, setComments] = useState('')
    const adminNotesDraftRef = useRef('')
    const [adminNotesSeed, setAdminNotesSeed] = useState({ value: '', revision: 0 })
    const setAdminNotes = value => {
        adminNotesDraftRef.current = value
        setAdminNotesSeed(previous => ({ value, revision: previous.revision + 1 }))
    }
    const [pendingAllocation, setPendingAllocation] = useState(false)
    const [notifyWorkersBySms, setNotifyWorkersBySms] = useState(false)
    const [document, setDocument] = useState([])
    const [selectedSiteDocumentUrls, setSelectedSiteDocumentUrls] = useState([])
    const [dropMapMarker, setDropMapMarker] = useState(false)
    const [mapMarkerName, setMapMarkerName] = useState('')
    const [mapMarkerCoordinates, setMapMarkerCoordinates] = useState([])
    const [mapMarkerLink, setMapMarkerLink] = useState('')
    const [mapMarkerSearchAddress, setMapMarkerSearchAddressState] = useState({ value: '' })
    const setMapMarkerSearchAddress = value => setMapMarkerSearchAddressState({ value })
    const [mapMarkerCenter, setMapMarkerCenter] = useState(null)
    const [mapMarkerAddress, setMapMarkerAddress] = useState('')
    const [selectedSavedMarkerKey, setSelectedSavedMarkerKey] = useState('')
    const [mapViewMode, setMapViewMode] = useState('roadmap')
    const [savedLocationMarkers, setSavedLocationMarkers] = useState([])
    const [savingMapMarker, setSavingMapMarker] = useState(false)
    const [showAddAsset, setShowAddAsset] = useState(false)
    const [scopePhrases, setScopePhrases] = useState([])

    const [pickAsset, setPickAsset] = useState('')
    const [pickWorker, setPickWorker] = useState('')
    const [weekAllocationSearch, setWeekAllocationSearch] = useState('')
    const [cancelledAllocationSearch, setCancelledAllocationSearch] = useState('')
    const [cancelledFilterStartDate, setCancelledFilterStartDate] = useState('')
    const [cancelledFilterEndDate, setCancelledFilterEndDate] = useState('')
    const [cancelledPage, setCancelledPage] = useState(1)
    const [confirmMessage, setConfirmMessage] = useState(null)
    const [deleteAllocationPrompt, setDeleteAllocationPrompt] = useState(null)
    const [deleteAllocationComment, setDeleteAllocationComment] = useState('')
    const [successMessage, setSuccessMessage] = useState(null)
    const showSuccess = (message) => setSuccessMessage({ title: 'Success', message })
    const [allocationViewTab, setAllocationViewTab] = useState('next')
    const [availableWorkerType, setAvailableWorkerType] = useState('On Leave')
    const [availableWorkerSearch, setAvailableWorkerSearch] = useState('')
    const [activeOverviewSection, setActiveOverviewSection] = useState('allocations')
    const [customAllocationDate, setCustomAllocationDate] = useState('')
    const [downloadAllocation, setDownloadAllocation] = useState(null)
    const [downloadAllocationTimeSheets, setDownloadAllocationTimeSheets] = useState([])
    const [downloadQueue, setDownloadQueue] = useState([])
    const [downloadMode, setDownloadMode] = useState('multiple')
    const [combinedPdfBuffers, setCombinedPdfBuffers] = useState([])
    const [downloadChoiceAllocations, setDownloadChoiceAllocations] = useState([])
    const [selectedAllocationIds, setSelectedAllocationIds] = useState([])
    const [showSendMessage, setShowSendMessage] = useState(null)
    const [showPreviousWorkersSms, setShowPreviousWorkersSms] = useState(false)
    const [previousWorkersSmsMessage, setPreviousWorkersSmsMessage] = useState('')
    const [selectedPreviousWorkerIds, setSelectedPreviousWorkerIds] = useState([])
    const [pendingPreviousWorkersSmsLogs, setPendingPreviousWorkersSmsLogs] = useState([])
    const [showContactAllocation, setShowContactAllocation] = useState(null)
    const [viewAllocation, setViewAllocation] = useState(null)
    const [previewDocument, setPreviewDocument] = useState(null)
    const [message, setMessage] = useState('')
    const [extraNumbers, setExtraNumbers] = useState('')
    const [additionalSmsNumbers, setAdditionalSmsNumbers] = useState([])
    const [isMobileViewport, setIsMobileViewport] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 700 : false)
    const [smsLogs, setSmsLogs] = useState([])
    const [activityLogs, setActivityLogs] = useState([])
    const [currentAllocationNumber, setCurrentAllocationNumber] = useState('')
    const [loadedAllocationRecord, setLoadedAllocationRecord] = useState(null)
    const [pastStartDate, setPastStartDate] = useState('')
    const [pastEndDate, setPastEndDate] = useState('')
    const [pastSite, setPastSite] = useState('')
    const [futureFilterDate, setFutureFilterDate] = useState('')
    const [futureFilterClient, setFutureFilterClient] = useState('')
    const [futureFilterSite, setFutureFilterSite] = useState('')
    const [futureFilterWorker, setFutureFilterWorker] = useState('')
    const [futurePageStartDate, setFuturePageStartDate] = useState('')
    const [futurePageEndDate, setFuturePageEndDate] = useState('')
    const [futurePageSearch, setFuturePageSearch] = useState('')
    const [futurePage, setFuturePage] = useState(1)
    const [incompleteFilterStartDate, setIncompleteFilterStartDate] = useState('')
    const [incompleteFilterEndDate, setIncompleteFilterEndDate] = useState('')
    const [incompleteFilterSearch, setIncompleteFilterSearch] = useState('')
    const [incompletePage, setIncompletePage] = useState(1)
    const [pastSearch, setPastSearch] = useState('')
    const [pastFilterClient, setPastFilterClient] = useState('')
    const [pastFilterWorker, setPastFilterWorker] = useState('')
    const [pastPage, setPastPage] = useState(1)
    const [multiDaySchedule, setMultiDaySchedule] = useState(buildEmptyMultiDaySchedule)

    useEffect(() => {
        setIncompletePage(1)
    }, [incompleteFilterStartDate, incompleteFilterEndDate, incompleteFilterSearch])

    useEffect(() => {
        setFuturePage(1)
    }, [futurePageStartDate, futurePageEndDate, futurePageSearch])

    useEffect(() => {
        setCancelledPage(1)
    }, [cancelledAllocationSearch, cancelledFilterStartDate, cancelledFilterEndDate])

    useEffect(() => {
        setPastPage(1)
    }, [pastStartDate, pastEndDate, pastFilterClient, pastFilterWorker, pastSite, pastSearch])

    const userInfo = useSelector((state) => state.user.user)
    const activeOrg = useSelector((state) => state.user.activeOrg)
    const activeOrgInfo = useSelector((state) => state.user.activeOrgInfo)
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const lastOpenedRef = useRef({ id: null, type: null })
    const saveInFlightRef = useRef(false)
    // The record whose activity/SMS logs are on screen. Log replies for any other
    // record (the admin switched quickly) are dropped.
    const logsAllocationIdRef = useRef('')
    const markerSearchBoxRef = useRef(null)
    const markerGeocodeRequestRef = useRef(0)
    const scopeOfWorksDraftRef = useRef(scopeOfWorks)
    const mapMarkerNameDraftRef = useRef(mapMarkerName)
    const messageDraftRef = useRef(message)
    const deleteAllocationCommentDraftRef = useRef(deleteAllocationComment)

    useEffect(() => { scopeOfWorksDraftRef.current = scopeOfWorks }, [scopeOfWorks])
    useEffect(() => { mapMarkerNameDraftRef.current = mapMarkerName }, [mapMarkerName])
    useEffect(() => { messageDraftRef.current = message }, [message])
    useEffect(() => { deleteAllocationCommentDraftRef.current = deleteAllocationComment }, [deleteAllocationComment])
    const [moveCompletedAllocationPromptOpen, setMoveCompletedAllocationPromptOpen] = useState(false)

    const openPreviewDocumentModal = async (row, nameOverride = '') => {
        const label = nameOverride || getDocumentDisplayName(row)
        setLoading(true)
        try {
            const resolvedUrl = await resolveStorageFileUrl(row?.picture || row, activeOrg?.id || '')
            if (!resolvedUrl) {
                throw new Error('File could not be opened.')
            }
            setPreviewDocument({ url: resolvedUrl, name: label })
        } catch (e) {
            toast.error(e.message || 'Failed to open document')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        const requestedSection = location.state?.activeOverviewSection || searchParams.get('section')
        if (['allocations', 'incomplete', 'future', 'past', 'cancelled'].includes(requestedSection)) {
            setActiveOverviewSection(requestedSection)
        }
    }, [location.state, searchParams])

    useEffect(() => {
        if (activeOrg) {
            const loadPageData = async () => {
                setInitialLoading(true)
                setReferenceDataLoaded({ clients: false, sites: false, workers: false })
                setClients([])
                setSites([])
                setWorkers([])
                try {
                    await Promise.allSettled([
                        fetchAllocations(),
                        fetchCancelledAllocations(),
                        fetchIncompleteAllocations(),
                        fetchClients(),
                        fetchWorkers(),
                        fetchWorkerLeaves(),
                        fetchAssets(),
                        fetchSites(),
                        fetchTimeSheet(),
                        fetchScopePhrases(),
                    ])
                    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
                } finally {
                    setInitialLoading(false)
                }
            }
            loadPageData()
        }
    }, [activeOrg])

    useEffect(() => {
        const state = location.state || {}
        const openAllocationIdFromQuery = searchParams.get('allocationId') || ''
        const openIncompleteAllocationIdFromQuery = searchParams.get('incompleteAllocationId') || ''
        if (!referenceDataLoaded.clients || !referenceDataLoaded.sites || !referenceDataLoaded.workers) return

        const openAllocationId = state.openAllocationId || openAllocationIdFromQuery
        const openIncompleteAllocationId = state.openIncompleteAllocationId || openIncompleteAllocationIdFromQuery
        const openSmsAllocationId = state.openSmsAllocationId || ''
        const openViewAllocationId = state.openViewAllocationId || ''
        if (!openAllocationId && !openIncompleteAllocationId && !openSmsAllocationId && !openViewAllocationId) return

        if (openViewAllocationId && (allocations.length > 0 || incompleteAllocations.length > 0)) {
            if (lastOpenedRef.current.id === openViewAllocationId && lastOpenedRef.current.type === 'view') {
                return
            }
            const viewSource = state.openViewIncomplete ? incompleteAllocations : allocations
            const allocation = viewSource.find(item => item.id === openViewAllocationId)
            if (allocation) {
                resetAllocationLogs(allocation.id)
                setViewAllocation({
                    ...allocation,
                    isIncomplete: !!state.openViewIncomplete,
                    client: getDropdownLabel(allocation.chosenClient, clients.map(item => ({ label: item.name, value: item.id }))),
                    site: getDropdownLabel(allocation.chosenSite, sites.map(item => ({ label: item.name, value: item.id }))),
                })
                fetchAllocationActivityLogs(allocation.id)
                if (!state.openViewIncomplete) fetchSmsLogs(allocation.id)
                lastOpenedRef.current = { id: openViewAllocationId, type: 'view' }
            }
        } else if (openSmsAllocationId && (allocations.length > 0 || incompleteAllocations.length > 0)) {
            if (lastOpenedRef.current.id === openSmsAllocationId && lastOpenedRef.current.type === 'sms') {
                return
            }
            const smsSource = state.openSmsIncomplete ? incompleteAllocations : allocations
            const allocation = smsSource.find(item => item.id === openSmsAllocationId)
            if (allocation) {
                setShowSendMessage({
                    ...allocation,
                    client: getDropdownLabel(allocation.chosenClient, clients.map(item => ({ label: item.name, value: item.id }))),
                    site: getDropdownLabel(allocation.chosenSite, sites.map(item => ({ label: item.name, value: item.id }))),
                })
                lastOpenedRef.current = { id: openSmsAllocationId, type: 'sms' }
            }
        } else if (openIncompleteAllocationId && (incompleteAllocations.length > 0 || state.editAllocationRecord)) {
            if (lastOpenedRef.current.id === openIncompleteAllocationId && lastOpenedRef.current.type === 'incomplete') {
                return
            }
            const allocation = state.editIncomplete && state.editAllocationRecord
                ? state.editAllocationRecord
                : incompleteAllocations.find(item => item.id === openIncompleteAllocationId || item.allocationId === openIncompleteAllocationId)
            if (allocation) {
                handleViewIncomplete(allocation)
                lastOpenedRef.current = { id: openIncompleteAllocationId, type: 'incomplete' }
            }
        } else if (openAllocationId && (allocations.length > 0 || state.editAllocationRecord)) {
            if (lastOpenedRef.current.id === openAllocationId && lastOpenedRef.current.type === 'allocation') {
                return
            }
            const allocation = !state.editIncomplete && state.editAllocationRecord
                ? state.editAllocationRecord
                : allocations.find(item => item.id === openAllocationId || item.allocationId === openAllocationId)
            if (allocation) {
                handleView(allocation)
                lastOpenedRef.current = { id: openAllocationId, type: 'allocation' }
            }
        }
    }, [location.state, searchParams, clients, sites, workers, allocations, incompleteAllocations, referenceDataLoaded])

    useEffect(() => {
        const state = location.state || {}
        if (!state.openNewAllocation) return

        setIsAdding(true)
        setEditId('')
        setEditIncompleteId('')
        setFormType('allocation')
        clearFields()
        lastOpenedRef.current = { id: null, type: null }

        navigate(location.pathname, { replace: true, state: {} })
    }, [location.pathname, location.state, navigate])

    useEffect(() => {
        if (!date) return
        const defaultTime = format(date, 'HH:mm')
        setMultiDaySchedule(prev => prev.map(day => ({
            ...day,
            time: day.enabled ? (day.manuallySet ? day.time : defaultTime) : '',
        })))
    }, [date])

    useEffect(() => {
        if (!activeOrg?.id || !chosenClient?.value || !chosenSite?.value) {
            setSavedLocationMarkers([])
            return
        }
        fetchLocationMarkers(chosenClient.value, chosenSite.value)
    }, [activeOrg?.id, chosenClient?.value, chosenSite?.value])

    useEffect(() => {
        setChosenWorkerAssignments(prev => syncWorkerAssignments(chosenWorkers.map(worker => worker.value), workers, prev))
    }, [chosenWorkers, workers])

    const toggleMultiDaySchedule = (dayIndex) => {
        const selectedStartDate = date ? new Date(date) : null
        const selectedStartTime = selectedStartDate && !Number.isNaN(selectedStartDate.getTime())
            ? `${String(selectedStartDate.getHours()).padStart(2, '0')}:${String(selectedStartDate.getMinutes()).padStart(2, '0')}`
            : ''
        setMultiDaySchedule(prev => prev.map(item => {
            if (item.dayIndex !== dayIndex) return item
            const nextEnabled = !item.enabled
            const nextTime = nextEnabled ? (item.time || selectedStartTime || '07:00') : ''
            return { ...item, enabled: nextEnabled, time: nextTime, manuallySet: false }
        }))
    }

    const getAllocationContactRows = (allocation) => {
        const chosenWorkerIds = Array.isArray(allocation?.chosenWorkers) ? allocation.chosenWorkers : []
        const teamLeaderIds = (allocation?.teamLeaders || []).map(item => item?.value || item)
        return chosenWorkerIds.map(workerId => {
            const worker = workers.find(row => row.id === workerId)
            return {
                id: workerId,
                name: worker ? formatName(`${worker.firstName || ''} ${worker.lastName || ''}`).trim() || '-' : '-',
                contactNo: worker?.contactNo || '',
                // null: not in this page's worker list (archived or awaiting review).
                // The server still tries them, so they are not shown as unreachable.
                inApp: worker ? hasApp(worker) : null,
                isTeamLeader: teamLeaderIds.includes(workerId),
            }
        })
    }

    const copyContactNumber = async (contactNo) => {
        if (!contactNo) return
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(contactNo)
            } else {
                const textarea = document.createElement('textarea')
                textarea.value = contactNo
                textarea.style.position = 'fixed'
                textarea.style.opacity = '0'
                document.body.appendChild(textarea)
                textarea.select()
                document.execCommand('copy')
                document.body.removeChild(textarea)
            }
            showSuccess('Mobile number copied')
        } catch (e) {
            toast.error('Failed to copy mobile number')
        }
    }

    const buildMultiDayAllocationDates = (startDateValue, endDateValue, schedule = []) => {
        if (!startDateValue || !endDateValue) return []
        const start = new Date(startDateValue)
        const end = new Date(endDateValue)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return []

        const scheduleMap = new Map(
            schedule
                .filter(day => day.enabled)
                .map(day => [day.dayIndex, day.time])
        )
        const defaultHours = start.getHours()
        const defaultMinutes = start.getMinutes()
        const dates = []

        const cursor = new Date(start)
        cursor.setHours(0, 0, 0, 0)
        const endDay = new Date(end)
        endDay.setHours(0, 0, 0, 0)

        while (cursor <= endDay) {
            const timeValue = scheduleMap.get(cursor.getDay())
            if (timeValue !== undefined) {
                const [hourPart, minutePart] = String(timeValue).split(':')
                const hours = Number(hourPart)
                const minutes = Number(minutePart)
                const finalHours = Number.isFinite(hours) ? hours : defaultHours
                const finalMinutes = Number.isFinite(minutes) ? minutes : defaultMinutes

                const allocationDate = new Date(cursor)
                allocationDate.setHours(finalHours, finalMinutes, 0, 0)
                dates.push(businessWallDateToIso(allocationDate))
            }
            cursor.setDate(cursor.getDate() + 1)
        }

        return dates
    }

    useEffect(() => {
        if (!showSendMessage) {
            setMessage('')
            setExtraNumbers('')
        }
    }, [showSendMessage])

    useEffect(() => {
        const handleResize = () => {
            setIsMobileViewport(window.innerWidth <= 700)
        }

        handleResize()
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const fetchAllocations = async () => {

        try {

            const resp = await postWithAuth('fetchAllocations', { orgId: activeOrg.id, listOnly: true })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setAllocations(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchIncompleteAllocations = async () => {

        try {
            const resp = await postWithAuth('fetchIncompleteAllocations', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setIncompleteAllocations(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchCancelledAllocations = async () => {
        try {
            const resp = await postWithAuth('fetchCancelledAllocations', { orgId: activeOrg.id })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            setCancelledAllocations(resp?.data || [])
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchClients = async () => {

        try {

            const resp = await postWithAuth('fetchClients', { listOnly: true, orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (!Array.isArray(resp?.data)) throw new Error('Unable to load clients.')
            setClients(resp.data)
            setReferenceDataLoaded(current => ({ ...current, clients: true }))
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchSites = async () => {

        try {

            const resp = await postWithAuth('fetchSites', { listOnly: true, orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (!Array.isArray(resp?.data)) throw new Error('Unable to load sites.')
            setSites(resp.data)
            setReferenceDataLoaded(current => ({ ...current, sites: true }))
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchWorkers = async () => {

        try {

            const resp = await postWithAuth('fetchWorkers', { selectionOnly: true, orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (!Array.isArray(resp?.data)) throw new Error('Unable to load workers.')
            setWorkers(resp.data)
            setReferenceDataLoaded(current => ({ ...current, workers: true }))
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchWorkerLeaves = async () => {

        try {

            const resp = await postWithAuth('fetchWorkerLeaves', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setWorkerLeaves(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchTimeSheet = async () => {

        try {

            const resp = await postWithAuth('fetchTimeSheet', { orgId: activeOrg.id, statusOnly: true })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setTimeSheets(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const [fetchingDownload, setFetchingDownload] = useState(false)
    const fetchAllocationTimeSheets = async (allocationId) => {
        setFetchingDownload(true)
        try {
        const resp = await postWithAuth('fetchTimeSheet', { orgId: activeOrg.id, allocationId })
        if (resp && resp.err) {
            throw new Error(resp.err)
        }
        return resp?.data || []
        } finally { setFetchingDownload(false) }
    }

    const startAllocationDownloadQueue = async (queue, mode = 'multiple') => {
        if (!queue.length) {
            setDownloadQueue([])
            setDownloadAllocation(null)
            setDownloadAllocationTimeSheets([])
            return
        }

        try {
            const orderedQueue = [...queue].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0))
            const detailedTimeSheets = await fetchAllocationTimeSheets(orderedQueue[0].id)
            setDownloadMode(mode)
            setCombinedPdfBuffers([])
            setDownloadQueue(orderedQueue)
            setDownloadAllocationTimeSheets(detailedTimeSheets)
            setDownloadAllocation(orderedQueue[0])
        } catch (e) {
            toast.error(e.message)
            setDownloadQueue([])
            setDownloadAllocation(null)
            setDownloadAllocationTimeSheets([])
        }
    }

    const downloadCombinedPdf = async (buffers) => {
        const { PDFDocument } = await import('pdf-lib')
        const mergedPdf = await PDFDocument.create()
        for (const [index, buffer] of buffers.entries()) {
            let bytes
            if (buffer instanceof Blob) {
                bytes = new Uint8Array(await buffer.arrayBuffer())
            } else if (buffer instanceof ArrayBuffer) {
                bytes = new Uint8Array(buffer)
            } else if (ArrayBuffer.isView(buffer)) {
                bytes = new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength)
            }
            if (!bytes?.byteLength) throw new Error(`Allocation PDF ${index + 1} was empty.`)
            const sourcePdf = await PDFDocument.load(bytes, { ignoreEncryption: true })
            const pages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices())
            pages.forEach(page => mergedPdf.addPage(page))
        }
        if (mergedPdf.getPageCount() === 0) throw new Error('No PDF pages were generated.')
        const bytes = await mergedPdf.save()
        const blob = new Blob([bytes], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'selected_allocations.pdf'
        link.click()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
    }

    const collectAndAdvanceAllocationDownload = async (buffer) => {
        const nextBuffers = [...combinedPdfBuffers, buffer]
        const remaining = downloadQueue.slice(1)
        setCombinedPdfBuffers(nextBuffers)
        setDownloadQueue(remaining)
        if (!remaining.length) {
            try {
                await downloadCombinedPdf(nextBuffers)
            } catch (error) {
                console.error('Unable to combine allocation PDFs', error)
                toast.error(error?.message || 'Unable to combine the selected allocation PDFs.')
            }
            setDownloadAllocation(null)
            setDownloadAllocationTimeSheets([])
            setCombinedPdfBuffers([])
            return
        }
        try {
            const detailedTimeSheets = await fetchAllocationTimeSheets(remaining[0].id)
            setDownloadAllocationTimeSheets(detailedTimeSheets)
            setDownloadAllocation(remaining[0])
        } catch (error) {
            toast.error(error.message)
            setDownloadAllocation(null)
            setDownloadAllocationTimeSheets([])
        }
    }

    const handleAllocationDownloadError = (error, allocation) => {
        console.error('Unable to generate allocation PDF', allocation?.id || allocation?.allocationId, error)
        toast.error(`Unable to generate allocation ${allocation?.allocationId || allocation?.id || ''} PDF: ${error?.message || 'Unknown error'}`)
        // Separate files: skip this one and carry on with the rest. A combined file
        // cannot be made without every part, so that stops.
        if (downloadMode === 'multiple' && downloadQueue.length > 1) {
            advanceAllocationDownloadQueue()
            return
        }
        setDownloadQueue([])
        setDownloadAllocation(null)
        setDownloadAllocationTimeSheets([])
        setCombinedPdfBuffers([])
    }

    const advanceAllocationDownloadQueue = async () => {
        const remaining = downloadQueue.slice(1)
        setDownloadQueue(remaining)

        if (!remaining.length) {
            setDownloadAllocation(null)
            setDownloadAllocationTimeSheets([])
            return
        }

        try {
            const detailedTimeSheets = await fetchAllocationTimeSheets(remaining[0].id)
            setDownloadAllocationTimeSheets(detailedTimeSheets)
            setDownloadAllocation(remaining[0])
        } catch (e) {
            toast.error(e.message)
            setDownloadQueue([])
            setDownloadAllocation(null)
            setDownloadAllocationTimeSheets([])
        }
    }


    const fetchAssets = async () => {

        try {

            const resp = await postWithAuth('fetchAssets', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setAssets(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchScopePhrases = async () => {
        try {
            const resp = await postWithAuth('fetchScopePhrases', { orgId: activeOrg.id })
            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }
            setScopePhrases(resp?.data || [])
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchLocationMarkers = async (clientId = chosenClient?.value, siteId = chosenSite?.value) => {
        if (!activeOrg?.id || !clientId || !siteId) {
            setSavedLocationMarkers([])
            return
        }
        try {
            const resp = await postWithAuth('fetchLocationMarkers', {
                orgId: activeOrg.id,
                clientId,
                siteId,
            })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            setSavedLocationMarkers(resp?.data || [])
        } catch (e) {
            toast.error(e.message)
        }
    }


    const clientById = useMemo(() => new Map(clients.map(client => [client.id, client])), [clients])
    const siteById = useMemo(() => new Map(sites.map(site => [site.id, site])), [sites])
    const workerById = useMemo(() => new Map(workers.map(worker => [worker.id, worker])), [workers])
    const workerNameById = useMemo(() => new Map(
        workers.map(worker => [worker.id, formatName(`${worker.firstName || ''} ${worker.lastName || ''}`).trim() || 'Unknown worker'])
    ), [workers])
    const decorateAllocationRows = useCallback(rows => rows.map(row => {
        const teamLeaderIds = new Set((row.teamLeaders || []).map(item => item?.value || item))
        return {
            ...row,
            client: clientById.get(row.chosenClient)?.name || '',
            site: siteById.get(row.chosenSite)?.name || '',
            worker: (row.chosenWorkers || [])
                .map(workerId => `${workerNameById.get(workerId) || ''}${teamLeaderIds.has(workerId) ? ' (TL)' : ''}`)
                .filter(Boolean)
                .join(', '),
        }
    }), [clientById, siteById, workerNameById])
    const allocationRows = useMemo(
        () => decorateAllocationRows(allocations),
        [allocations, decorateAllocationRows]
    )
    const cancelledAllocationRows = useMemo(
        () => decorateAllocationRows(cancelledAllocations)
            .sort((a, b) => new Date(b.cancelledAt || 0) - new Date(a.cancelledAt || 0)),
        [cancelledAllocations, decorateAllocationRows]
    )
    const filteredCancelledAllocationRows = useMemo(() => cancelledAllocationRows.filter(row => {
        const allocationDate = new Date(row.date).setHours(0, 0, 0, 0)
        const startDate = cancelledFilterStartDate ? new Date(cancelledFilterStartDate).setHours(0, 0, 0, 0) : null
        const endDate = cancelledFilterEndDate ? new Date(cancelledFilterEndDate).setHours(0, 0, 0, 0) : null
        if ((startDate !== null || endDate !== null) && !Number.isFinite(allocationDate)) return false
        if (startDate !== null && allocationDate < startDate) return false
        if (endDate !== null && allocationDate > endDate) return false
        const query = normalizeSearch(cancelledAllocationSearch.trim())
        if (!query) return true
        return [row.allocationId, row.client, row.site, row.worker, row.cancellationComment]
            .some(value => normalizeSearch(String(value || '')).includes(query))
    }), [cancelledAllocationRows, cancelledFilterStartDate, cancelledFilterEndDate, cancelledAllocationSearch])
    const cancelledPageSize = 20
    const cancelledPageCount = Math.max(1, Math.ceil(filteredCancelledAllocationRows.length / cancelledPageSize))
    const safeCancelledPage = Math.min(cancelledPage, cancelledPageCount)
    const paginatedCancelledAllocationRows = filteredCancelledAllocationRows.slice((safeCancelledPage - 1) * cancelledPageSize, safeCancelledPage * cancelledPageSize)
    const incompleteRows = useMemo(
        () => decorateAllocationRows(incompleteAllocations),
        [incompleteAllocations, decorateAllocationRows]
    )
    const futureAllocationSort = useTableSort(
        isAdding ? [] : allocationRows.filter(r => isSameMonth(new Date(r.date), futureStatsDate)),
        { key: 'date', direction: 'ascending' }
    );
    const pastAllocationSort = useTableSort(
        isAdding ? [] : allocationRows,
        { key: 'date', direction: 'descending' }
    );
    const requiredWorkerOptions = Array.from({ length: 20 }, (_, index) => ({
        label: `${index + 1}`,
        value: index + 1
    }))
    const timeOptions = Array.from({ length: 24 * 4 }, (_, index) => {
        const totalMinutes = index * 15
        const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0')
        const minutes = String(totalMinutes % 60).padStart(2, '0')
        const value = `${hours}:${minutes}`
        return { label: value, value }
    })
    const clientDropdownOptions = useMemo(() => clients
        .map(client => ({ label: client.name, value: client.id }))
        .sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), undefined, { sensitivity: 'base' })), [clients])
    const selectedSite = useMemo(
        () => (chosenSite?.value ? sites.find(site => site.id === chosenSite.value) : null),
        [sites, chosenSite]
    )
    // Auto-enabling the marker must happen when an admin *picks* a site, not whenever
    // selectedSite changes: as an effect it also fired while the edit form was being
    // populated, overwriting the allocation's saved dropMapMarker and blocking Save.
    const handleSiteChange = option => {
        setChosenSite(option)
        const site = option?.value ? sites.find(item => item.id === option.value) : null
        if (String(site?.name || '').trim().toLowerCase() === 'various sites') setDropMapMarker(true)
    }
    const workerDropdownOptions = useMemo(() => workers.map(worker => ({
        label: formatName(`${worker.firstName || ''} ${worker.lastName || ''}`.trim()),
        value: worker.id,
    })).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })), [workers])
    const workerLeavesByWorkerId = useMemo(() => {
        const nextMap = new Map()
        workerLeaves.forEach(leave => {
            if (!leave?.workerId) return
            if (!nextMap.has(leave.workerId)) nextMap.set(leave.workerId, [])
            nextMap.get(leave.workerId).push(leave)
        })
        return nextMap
    }, [workerLeaves])
    const siteDocuments = Array.isArray(selectedSite?.pictures)
        ? selectedSite.pictures.filter(item => item?.picture?.url && item?.includeInNewAllocation)
        : []
    const uploadedAllocationDocuments = normalizeDocumentArray(document).filter(doc => doc?.source !== 'site')
    const previousSiteWorkers = useMemo(() => {
        if (!chosenSite?.value) return []

        const latestByWorkerId = new Map()
            ;[...allocations, ...incompleteAllocations]
                .filter(row => row?.chosenSite === chosenSite.value)
                .sort((a, b) => new Date(b.date || b.createdAt || b.updatedAt || 0) - new Date(a.date || a.createdAt || a.updatedAt || 0))
                .forEach(row => {
                    ; (row.chosenWorkers || []).forEach(workerId => {
                        if (!workerId || latestByWorkerId.has(workerId)) return
                        const worker = workerById.get(workerId)
                        if (!worker) return
                        latestByWorkerId.set(workerId, {
                            id: workerId,
                            name: formatName(`${worker.firstName || ''} ${worker.lastName || ''}`).trim(),
                            inApp: hasApp(worker),
                            lastUsedAt: row.date || row.createdAt || row.updatedAt || '',
                        })
                    })
                })

        return Array.from(latestByWorkerId.values())
            .sort((a, b) => new Date(b.lastUsedAt || 0) - new Date(a.lastUsedAt || 0))
    }, [allocations, incompleteAllocations, workerById, chosenSite])
    const previousAppWorkers = previousSiteWorkers.filter(worker => worker.inApp)
    const acceptedShiftStatusMap = useMemo(() => {
        const nextMap = new Map()
        timeSheets.forEach(sheet => {
            if (!sheet?.allocationId || !sheet?.userId) return
            nextMap.set(`${sheet.allocationId}-${sheet.userId}`, hasAcceptedShift(sheet.status))
        })
        return nextMap
    }, [timeSheets])
    const fullyCompletedShiftKeys = useMemo(() => {
        const nextSet = new Set()
        timeSheets.forEach(sheet => {
            if (!sheet?.allocationId || !sheet?.userId) return
            if (!sheet.clockIn || !sheet.clockOut) return
            nextSet.add(`${sheet.allocationId}-${sheet.userId}`)
        })
        return nextSet
    }, [timeSheets])
    const savedMapMarkers = useMemo(() => {
        if (!chosenClient?.value || !chosenSite?.value) return []

        const latestByName = new Map()

        savedLocationMarkers
            .slice()
            .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
            .forEach(row => {
                const name = String(row.name || '').trim()
                const key = name.toLowerCase()
                if (!key || latestByName.has(key) || !hasValidCoordinates(row.coordinates)) return
                latestByName.set(key, {
                    key,
                    name,
                    coordinates: [Number(row.coordinates[0]), Number(row.coordinates[1])],
                    link: row.link || buildGoogleMapsLink(row.coordinates),
                    address: row.address || '',
                    updatedAt: row.updatedAt || row.createdAt || '',
                    savedId: row.id,
                })
            })

            ;[...allocations, ...incompleteAllocations]
                .filter(row => row?.chosenClient === chosenClient.value && row?.chosenSite === chosenSite.value)
                .filter(row => row?.dropMapMarker && row?.mapMarkerName && hasValidCoordinates(row?.mapMarkerCoordinates))
                .sort((a, b) => new Date(b.updatedAt || b.createdAt || b.date || 0) - new Date(a.updatedAt || a.createdAt || a.date || 0))
                .forEach(row => {
                    const name = String(row.mapMarkerName || '').trim()
                    const key = name.toLowerCase()
                    if (!key || latestByName.has(key)) return
                    latestByName.set(key, {
                        key,
                        name,
                        coordinates: [Number(row.mapMarkerCoordinates[0]), Number(row.mapMarkerCoordinates[1])],
                        link: row.mapMarkerLink || buildGoogleMapsLink(row.mapMarkerCoordinates),
                        address: row.mapMarkerAddress || '',
                        updatedAt: row.updatedAt || row.createdAt || row.date || '',
                    })
                })

        return Array.from(latestByName.values()).sort((a, b) => a.name.localeCompare(b.name))
    }, [allocations, incompleteAllocations, savedLocationMarkers, chosenClient, chosenSite])
    const selectedSavedMarker = selectedSavedMarkerKey
        ? savedMapMarkers.find(marker => marker.key === selectedSavedMarkerKey) || null
        : null

    useEffect(() => {
        const availableUrls = new Set(siteDocuments.map(item => item.picture?.url).filter(Boolean))
        setSelectedSiteDocumentUrls(prev => prev.filter(url => availableUrls.has(url)))
    }, [chosenSite?.value, sites])

    useEffect(() => {
        if (hasValidCoordinates(mapMarkerCoordinates)) {
            setMapMarkerLink(buildGoogleMapsLink(mapMarkerCoordinates))
            setMapMarkerCenter(getMapCenter(mapMarkerCoordinates))
            return
        }
        setMapMarkerLink('')
        if (hasValidCoordinates(selectedSite?.coordinates)) {
            setMapMarkerCenter(getMapCenter(selectedSite.coordinates))
            return
        }
        setMapMarkerCenter(null)
    }, [mapMarkerCoordinates, selectedSite])

    useEffect(() => {
        if (!dropMapMarker) {
            setSelectedSavedMarkerKey('')
            setMapMarkerAddress('')
            return
        }
        if (!selectedSavedMarkerKey) return
        if (!savedMapMarkers.some(marker => marker.key === selectedSavedMarkerKey)) {
            setSelectedSavedMarkerKey('')
        }
    }, [dropMapMarker, savedMapMarkers, selectedSavedMarkerKey])

    useEffect(() => {
        if (!dropMapMarker) {
            setMapMarkerAddress('')
            return
        }
        if (!hasValidCoordinates(mapMarkerCoordinates)) {
            setMapMarkerAddress('')
            return
        }
    }, [dropMapMarker, mapMarkerCoordinates])

    const applySavedMarker = (marker) => {
        if (!marker) return
        setDropMapMarker(true)
        setSelectedSavedMarkerKey(marker.key)
        setMapMarkerName(marker.name || '')
        setMapMarkerCoordinates(marker.coordinates || [])
        setMapMarkerLink(marker.link || buildGoogleMapsLink(marker.coordinates || []))
        const savedMarkerAddress = marker.address || ''
        setMapMarkerSearchAddress(savedMarkerAddress)
        setMapMarkerAddress(savedMarkerAddress)
        setMapMarkerCenter(getMapCenter(marker.coordinates || []) || getMapCenter(selectedSite?.coordinates || []))
    }

    const clearMarkerSelection = () => {
        markerGeocodeRequestRef.current += 1
        setSelectedSavedMarkerKey('')
        setMapMarkerName('')
        setMapMarkerCoordinates([])
        setMapMarkerLink('')
        setMapMarkerSearchAddress('')
        setMapMarkerAddress('')
        setMapMarkerCenter(getMapCenter(selectedSite?.coordinates || []))
    }

    const handleMarkerNameChange = (value) => {
        const capitalizedValue = value
            ? value.replace(/\b[a-z]/g, character => character.toUpperCase())
            : ''
        setMapMarkerName(capitalizedValue)
        if (selectedSavedMarker && capitalizedValue.trim() !== selectedSavedMarker.name) {
            setSelectedSavedMarkerKey('')
        }
    }

    const handleMarkerMapClick = (event) => {
        const lat = event?.latLng?.lat?.()
        const lng = event?.latLng?.lng?.()
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return

        const nextCoordinates = [lat, lng]
        const applyCoordinates = async () => {
            const requestId = markerGeocodeRequestRef.current + 1
            markerGeocodeRequestRef.current = requestId
            setMapMarkerCoordinates(nextCoordinates)
            setMapMarkerCenter({ lat, lng })
            setMapMarkerAddress('')
            setMapMarkerSearchAddress('')
            try {
                const resolvedAddress = await reverseGeocodeMapCoordinates(nextCoordinates)
                if (markerGeocodeRequestRef.current !== requestId) return
                setMapMarkerAddress(resolvedAddress)
                setMapMarkerSearchAddress(resolvedAddress)
            } catch {
                // Save retries geocoding and prevents an addressless marker being persisted.
            }
        }

        if (selectedSavedMarker && !areCoordinatesEqual(nextCoordinates, selectedSavedMarker.coordinates)) {
            setConfirmMessage({
                message: `Update saved marker "${selectedSavedMarker.name}" to this new location?`,
                action: async () => {
                    applyCoordinates()
                }
            })
            return
        }

        applyCoordinates()
    }

    const handleMarkerPlacesChanged = () => {
        const place = markerSearchBoxRef.current?.getPlace?.()
        if (!place) return

        const lat = place?.geometry?.location?.lat?.()
        const lng = place?.geometry?.location?.lng?.()
        const resolvedAddress = getGooglePlaceResolvedAddress(place)

        markerGeocodeRequestRef.current += 1
        setMapMarkerSearchAddress(resolvedAddress)

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return

        const nextCoordinates = [lat, lng]
        setMapMarkerAddress(resolvedAddress)
        setMapMarkerCenter({ lat, lng })

        if (selectedSavedMarker && !areCoordinatesEqual(nextCoordinates, selectedSavedMarker.coordinates)) {
            setConfirmMessage({
                message: `Update saved marker "${selectedSavedMarker.name}" to this new location?`,
                action: async () => {
                    setMapMarkerCoordinates(nextCoordinates)
                    setMapMarkerAddress(resolvedAddress)
                    setMapMarkerCenter({ lat, lng })
                }
            })
            return
        }

        setMapMarkerCoordinates(nextCoordinates)
    }

    const handleSaveMapMarker = async () => {
        const markerName = mapMarkerNameDraftRef.current.trim()
        if (!chosenClient?.value || !chosenSite?.value) {
            toast.error('Choose a client and site before saving a marker')
            return
        }
        if (!markerName) {
            toast.error('Enter a marker name')
            return
        }
        if (!hasValidCoordinates(mapMarkerCoordinates)) {
            toast.error('Choose a location on the map')
            return
        }

        try {
            setSavingMapMarker(true)
            let effectiveMarkerAddress = String(mapMarkerAddress || '').trim()
            if (!effectiveMarkerAddress) {
                effectiveMarkerAddress = await reverseGeocodeMapCoordinates(mapMarkerCoordinates)
                setMapMarkerAddress(effectiveMarkerAddress)
                setMapMarkerSearchAddress(effectiveMarkerAddress)
            }
            const resp = await postWithAuth('saveLocationMarker', {
                id: selectedSavedMarker?.savedId || '',
                orgId: activeOrg.id,
                clientId: chosenClient.value,
                siteId: chosenSite.value,
                name: markerName,
                coordinates: mapMarkerCoordinates,
                link: mapMarkerLink || buildGoogleMapsLink(mapMarkerCoordinates),
                address: effectiveMarkerAddress,
            })
            if (resp?.err) {
                throw new Error(resp.err)
            }
            const savedMarkerKey = markerName.toLowerCase()
            const savedMarker = {
                id: resp?.data?.id || selectedSavedMarker?.savedId || '',
                clientId: chosenClient.value,
                siteId: chosenSite.value,
                name: markerName,
                coordinates: [Number(mapMarkerCoordinates[0]), Number(mapMarkerCoordinates[1])],
                link: mapMarkerLink || buildGoogleMapsLink(mapMarkerCoordinates),
                address: effectiveMarkerAddress,
                updatedAt: new Date().toISOString(),
            }

            setSavedLocationMarkers(previousMarkers => [
                savedMarker,
                ...previousMarkers.filter(marker => String(marker?.name || '').trim().toLowerCase() !== savedMarkerKey),
            ])
            setSelectedSavedMarkerKey(savedMarkerKey)
            showSuccess('Marker saved')
        } catch (e) {
            toast.error(e.message)
        } finally {
            setSavingMapMarker(false)
        }
    }


    const createCollectSignsTask = async () => {
        const dueDate = addDays(new Date(date), 1)
        await postWithAuth('saveTask', {
            chosenClient: chosenClient ? chosenClient.value : '',
            chosenSite: chosenSite ? chosenSite.value : '',
            dueDate: businessWallDateToIso(dueDate),
            comments: 'Collect signs from site',
            completed: false,
            orgId: activeOrg.id,
        })
    }

    const handleSaveAllocation = async (options = {}) => {
        if (saveInFlightRef.current || loading) {
            return
        }
        // Taken before any await (such as looking up a dropped marker's address), so a
        // second click during that wait cannot start a second save. The finally below
        // releases it on every exit.
        saveInFlightRef.current = true

        try {
            const effectiveScopeOfWorks = scopeOfWorksDraftRef.current
            const effectiveMapMarkerName = mapMarkerNameDraftRef.current
            if (!effectiveScopeOfWorks.trim()) {
                toast.error('Scope of works is required')
                return
            }
            const activeEditId = formType === 'allocation' ? editId : ''
            const activeEditIncompleteId = formType === 'incomplete' ? editIncompleteId : ''
            const chosenWorkerIds = new Set(chosenWorkers.map(r => r.value))
            const missingWorkerCapacity = chosenWorkers.find(workerOption => {
                const workerInfo = workers.find(item => item.id === workerOption.value)
                const workerTypeValues = Array.isArray(workerInfo?.workerType) ? workerInfo.workerType : []
                if (workerTypeValues.length <= 1) return false
                const assignment = chosenWorkerAssignments.find(item => item.workerId === workerOption.value)
                return !assignment?.capacity
            })
            const hasInvalidAssetWorker = chosenAssets.some(asset => asset.worker && !chosenWorkerIds.has(asset.worker))
            if (missingWorkerCapacity) {
                toast.error(`Choose a capacity for ${missingWorkerCapacity.label}`)
                saveInFlightRef.current = false
                return
            }
            if (hasInvalidAssetWorker) {
                toast.error('Assign asset to another worker')
                saveInFlightRef.current = false
                return
            }
            const hasInvalidTeamLeader = teamLeaders.some(leader => leader?.value && !chosenWorkerIds.has(leader.value))
            if (hasInvalidTeamLeader) {
                toast.error('Assign team leader to another worker')
                saveInFlightRef.current = false
                return
            }
            if (dropMapMarker && !effectiveMapMarkerName.trim()) {
                toast.error('Marker name is required')
                saveInFlightRef.current = false
                return
            }
            if (dropMapMarker && !hasValidCoordinates(mapMarkerCoordinates)) {
                toast.error('Click on the map to place the marker')
                saveInFlightRef.current = false
                return
            }
            let effectiveMapMarkerAddress = String(mapMarkerAddress || '').trim()
            if (dropMapMarker && !effectiveMapMarkerAddress) {
                try {
                    effectiveMapMarkerAddress = await reverseGeocodeMapCoordinates(mapMarkerCoordinates)
                    setMapMarkerAddress(effectiveMapMarkerAddress)
                    setMapMarkerSearchAddress(effectiveMapMarkerAddress)
                } catch {
                    toast.error('Marker address could not be found. Choose the location using Address Search and try again.')
                    saveInFlightRef.current = false
                    return
                }
            }
            if (isMultiDay) {
                if (!endDate) {
                    toast.error('End date is required for multi-day allocation')
                    saveInFlightRef.current = false
                    return
                }
                if (new Date(endDate) < new Date(date)) {
                    toast.error('End date must be after the start date')
                    saveInFlightRef.current = false
                    return
                }
                const enabledDays = multiDaySchedule.filter(day => day.enabled)
                if (enabledDays.length === 0) {
                    toast.error('Select at least one day for multi-day allocation')
                    saveInFlightRef.current = false
                    return
                }
                const missingTimes = enabledDays.some(day => !day.time)
                if (missingTimes) {
                    toast.error('Provide a start time for each selected day')
                    saveInFlightRef.current = false
                    return
                }
            }
            const movingCompletedAllocationByDayOnly = (() => {
                if (!activeEditId || !loadedAllocationRecord?.id || !loadedAllocationRecord?.date || !date) return false
                const originalWorkerIds = Array.isArray(loadedAllocationRecord.chosenWorkers) ? loadedAllocationRecord.chosenWorkers.filter(Boolean) : []
                if (!originalWorkerIds.length) return false
                const isCompletedAllocation = originalWorkerIds.every(workerId => fullyCompletedShiftKeys.has(`${loadedAllocationRecord.id}-${workerId}`))
                if (!isCompletedAllocation) return false
                const originalDate = new Date(loadedAllocationRecord.date)
                const nextDate = new Date(date)
                if (Number.isNaN(originalDate.getTime()) || Number.isNaN(nextDate.getTime())) return false
                const dayChanged = !isSameDay(originalDate, nextDate)
                const timeUnchanged = originalDate.getHours() === nextDate.getHours() && originalDate.getMinutes() === nextDate.getMinutes()
                return dayChanged && timeUnchanged
            })()
            if (movingCompletedAllocationByDayOnly && !options.moveWorkersDecision) {
                setMoveCompletedAllocationPromptOpen(true)
                return
            }
            setLoading(true)

            const multiDayAllocationDates = isMultiDay && endDate
                ? buildMultiDayAllocationDates(date, endDate, multiDaySchedule)
                : []
            if (isMultiDay && multiDayAllocationDates.length === 0) {
                toast.error('No dates could be generated for the selected multi-day schedule')
                setLoading(false)
                saveInFlightRef.current = false
                return
            }

            const requiredWorkerCount = requiredWorkers?.value || 0
            const shouldSaveWithoutSite = !chosenSite?.value
            const isIncomplete = shouldSaveWithoutSite || (!activeEditId && requiredWorkerCount > 0 && chosenWorkers.length < requiredWorkerCount)
            const selectedSiteDocuments = siteDocuments
                .filter(item => selectedSiteDocumentUrls.includes(item.picture?.url))
                .map(mapSiteDocumentToAllocationDocument)
            const uploadedDocuments = normalizeDocumentArray(document).filter(doc => getAttachmentUrl(doc))
            const payloadDocuments = [
                ...selectedSiteDocuments,
                ...uploadedDocuments.filter(doc => !selectedSiteDocumentUrls.includes(doc.siteDocumentUrl || doc.url)),
            ]
            const payload = {
                id: activeEditId,
                editSourceId: activeEditId || activeEditIncompleteId,
                editSourceType: activeEditId ? 'allocation' : activeEditIncompleteId ? 'incomplete' : '',

                chosenClient: chosenClient.value,
                chosenSite: chosenSite?.value || '',
                chosenWorkers: chosenWorkers.map(r => r.value),
                chosenWorkerAssignments,
                chosenAssets,
                teamLeaders,
                date: businessWallDateToIso(date),
                requiredWorkers: requiredWorkerCount,
                scopeOfWorks: effectiveScopeOfWorks,
                comments,
                adminNotes: adminNotesDraftRef.current,
                pending: !!pendingAllocation,
                notifyWorkersBySms: !!notifyWorkersBySms,
                signsDroppedToSite,
                signsOnsiteAlready,
                assetRequired,
                dropMapMarker: !!dropMapMarker,
                mapMarkerName: dropMapMarker ? effectiveMapMarkerName.trim() : '',
                mapMarkerCoordinates: dropMapMarker ? mapMarkerCoordinates : [],
                mapMarkerLink: dropMapMarker ? buildGoogleMapsLink(mapMarkerCoordinates) : '',
                mapMarkerAddress: dropMapMarker ? effectiveMapMarkerAddress : '',
                document: payloadDocuments,
                dateString: format(businessWallDateToDate(date), 'dd/MMM/yyyy'),
                timeString: formatTime(businessWallDateToDate(date)),
                endDate: isMultiDay && endDate ? businessWallDateToIso(endDate) : '',
                multiDaySchedule: isMultiDay && endDate ? multiDaySchedule.filter(day => day.enabled).map(day => ({
                    dayIndex: day.dayIndex,
                    time: day.time,
                })) : [],
                multiDayAllocationDates,

                orgId: activeOrg.id,
            }

            let savedAllocationId = activeEditId || activeEditIncompleteId || ''
            const movingToIncomplete = (options.moveWorkersDecision === 'no' && activeEditId) || (shouldSaveWithoutSite && activeEditId)
            if (movingToIncomplete) {
                const respDelete = await postWithAuth('deleteAllocation', { id: activeEditId, orgId: activeOrg.id })
                if (respDelete && respDelete.err) {
                    throw new Error(respDelete.err)
                }
                const movedIncompletePayload = {
                    ...payload,
                    id: '',
                    editSourceId: '',
                    editSourceType: '',
                    chosenWorkers: options.moveWorkersDecision === 'no' ? [] : chosenWorkers.map(r => r.value),
                    chosenWorkerAssignments: options.moveWorkersDecision === 'no' ? [] : chosenWorkerAssignments,
                    chosenAssets: options.moveWorkersDecision === 'no' ? (chosenAssets || []).map(asset => ({ ...asset, worker: '' })) : chosenAssets,
                    teamLeaders: options.moveWorkersDecision === 'no' ? [] : teamLeaders,
                }
                const respSaveIncomplete = await postWithAuth('saveIncompleteAllocation', movedIncompletePayload)
                if (respSaveIncomplete && respSaveIncomplete.err) {
                    throw new Error(respSaveIncomplete.err)
                }
            } else if (activeEditIncompleteId && !isIncomplete) {
                await postWithAuth('saveIncompleteAllocation', {
                    ...payload,
                    id: activeEditIncompleteId,
                })
                await postWithAuth('completeIncompleteAllocation', { id: activeEditIncompleteId, orgId: activeOrg.id })
            } else {
                const saveResponse = await postWithAuth(isIncomplete ? 'saveIncompleteAllocation' : 'saveAllocation', {
                    ...payload,
                    id: isIncomplete ? activeEditIncompleteId : activeEditId,
                })
                if (!saveResponse || saveResponse.err) throw new Error(saveResponse?.err || 'Allocation could not be saved.')
                if (!savedAllocationId && typeof saveResponse?.data === 'string' && !/saved/i.test(saveResponse.data)) {
                    savedAllocationId = saveResponse.data
                }
            }

            // The allocation is saved at this point. A failed log must not throw, or the
            // form would stay open as a new record and saving again would duplicate it.
            if (savedAllocationId && pendingPreviousWorkersSmsLogs.length > 0) {
                const logResults = await Promise.allSettled(pendingPreviousWorkersSmsLogs.map(log => postWithAuth('logAllocationSms', {
                    allocationId: savedAllocationId,
                    orgId: activeOrg.id,
                    message: log.message,
                    workerIds: log.workerIds,
                    extraNumbers: log.extraNumbers || [],
                })))
                if (logResults.some(result => result.status === 'rejected' || result.value?.err)) {
                    toast.error('Allocation saved, but the earlier SMS could not be added to its log.')
                }
            }
            setPendingPreviousWorkersSmsLogs([])

            setIsAdding(false)
            setFormType('allocation')
            setEditIncompleteId('')
            setEndDate('')
            setIsMultiDay(false)
            setMultiDaySchedule(prev => prev.map(day => ({ ...day, enabled: false, time: '', manuallySet: false })))
            setMoveCompletedAllocationPromptOpen(false)

            setSuccessMessage({
                title: 'Allocation saved',
                message: 'Allocation information saved successfully.',
            })
            if (signsDroppedToSite && !initialSignsDroppedToSite) {
                try {
                    await createCollectSignsTask()
                } catch (e) {
                    toast.error('Allocation saved but failed to create task')
                }
            }
            if (movingToIncomplete) {
                // The allocation was cancelled and re-saved as incomplete, so all three lists changed.
                fetchAllocations()
                fetchIncompleteAllocations()
                fetchCancelledAllocations()
            } else if (isIncomplete) {
                fetchIncompleteAllocations()
            } else {
                if (activeEditIncompleteId) {
                    fetchIncompleteAllocations()
                }
                fetchAllocations()
            }

        } catch (e) {
            toast.error(e.message)
        } finally {
            setLoading(false)
            saveInFlightRef.current = false
        }
    }


    const loadAllocationDetail = async allocation => {
        if (!allocation?._listOnly && allocation?.document !== undefined && allocation?.adminNotes !== undefined) return allocation
        const response = await postWithAuth('fetchAllocations', { orgId: activeOrg.id, recordId: allocation.id })
        if (response?.err || !response?.data?.[0]) throw new Error(response?.err || 'Allocation could not be loaded.')
        return response.data[0]
    }

    const handleView = async (allocation) => {
        setLoading(true)
        try {
        allocation = await loadAllocationDetail(allocation)
        const multiDayState = buildAllocationMultiDayEditorState(allocation, allocations)
        setLoadedAllocationRecord(allocation)

        setChosenClient(getDropdownValue(allocation.chosenClient, clients.map(r => ({ label: r.name, value: r.id }))))
        setChosenSite(getDropdownValue(allocation.chosenSite, sites.map(r => ({ label: r.name, value: r.id }))))
        setChosenWorkers(getDropdownValue(allocation.chosenWorkers, workers.map(r => ({ label: formatName(r.firstName + ' ' + r.lastName), value: r.id })), true))
        setChosenWorkerAssignments(syncWorkerAssignments(allocation.chosenWorkers || [], workers, allocation.chosenWorkerAssignments || []))
        setChosenAssets(allocation.chosenAssets || [])
        setAssetRequired(!!allocation.assetRequired)
        setTeamLeaders(allocation.teamLeaders || [])
        setDate(multiDayState.startDate)
        setSignsDroppedToSite(!!allocation.signsDroppedToSite)
        setSignsOnsiteAlready(!!allocation.signsOnsiteAlready)
        setInitialSignsDroppedToSite(!!allocation.signsDroppedToSite)
        setRequiredWorkers(getDropdownValue(Number(allocation.requiredWorkers), requiredWorkerOptions))
        setScopeOfWorks(allocation.scopeOfWorks || '')
        setComments(allocation.comments || '')
        setAdminNotes(allocation.adminNotes || allocation.comments || '')
        setPendingAllocation(!!allocation.pending)
        setNotifyWorkersBySms(!!allocation.notifyWorkersBySms)
        setDropMapMarker(!!allocation.dropMapMarker)
        setMapMarkerName(allocation.mapMarkerName || '')
        setMapMarkerCoordinates(hasValidCoordinates(allocation.mapMarkerCoordinates) ? allocation.mapMarkerCoordinates : [])
        setMapMarkerLink(allocation.mapMarkerLink || buildGoogleMapsLink(allocation.mapMarkerCoordinates || []))
        setMapMarkerSearchAddress('')
        setMapMarkerAddress(allocation.mapMarkerAddress || '')
        setSelectedSavedMarkerKey('')
        setMapViewMode('roadmap')
        setMapMarkerCenter(getMapCenter(allocation.mapMarkerCoordinates || []) || getMapCenter(sites.find(site => site.id === allocation.chosenSite)?.coordinates || []))
        setEndDate(multiDayState.endDate)
        setIsMultiDay(multiDayState.isMultiDay)
        setMultiDaySchedule(multiDayState.schedule)
        // setDocument(allocation.document || '')
        if (allocation.document && !allocation.document.url) {
            setDocument((allocation.document || []).filter(doc => doc?.source !== 'site'))
        } else if (allocation.document && allocation.document.url) {
            setDocument(allocation.document.source === 'site' ? [] : [allocation.document])
        } else {
            setDocument([])
        }
        setSelectedSiteDocumentUrls(normalizeDocumentArray(allocation.document).filter(doc => doc?.source === 'site').map(doc => doc.siteDocumentUrl || doc.url).filter(Boolean))
        setCurrentAllocationNumber(allocation.allocationId || allocation.id || '')

        setIsAdding(true)
        setEditId(allocation.id)
        setEditIncompleteId('')
        setFormType('allocation')
        setPendingPreviousWorkersSmsLogs([])
        resetAllocationLogs(allocation.id)
        fetchSmsLogs(allocation.id)
        fetchAllocationActivityLogs(allocation.id)
        } catch (error) {
            toast.error(error.message)
        } finally {
            setLoading(false)
        }
    }

    const handleViewIncomplete = (allocation) => {
        const multiDayState = buildAllocationMultiDayEditorState(allocation, incompleteAllocations)
        setLoadedAllocationRecord(allocation)
        setChosenClient(getDropdownValue(allocation.chosenClient, clients.map(r => ({ label: r.name, value: r.id }))))
        setChosenSite(getDropdownValue(allocation.chosenSite, sites.map(r => ({ label: r.name, value: r.id }))))
        setChosenWorkers(getDropdownValue(allocation.chosenWorkers, workers.map(r => ({ label: formatName(r.firstName + ' ' + r.lastName), value: r.id })), true))
        setChosenWorkerAssignments(syncWorkerAssignments(allocation.chosenWorkers || [], workers, allocation.chosenWorkerAssignments || []))
        setChosenAssets(allocation.chosenAssets || [])
        setAssetRequired(!!allocation.assetRequired)
        setTeamLeaders(allocation.teamLeaders || [])
        setDate(multiDayState.startDate)
        setSignsDroppedToSite(!!allocation.signsDroppedToSite)
        setSignsOnsiteAlready(!!allocation.signsOnsiteAlready)
        setInitialSignsDroppedToSite(!!allocation.signsDroppedToSite)
        setRequiredWorkers(getDropdownValue(Number(allocation.requiredWorkers), requiredWorkerOptions))
        setScopeOfWorks(allocation.scopeOfWorks || '')
        setComments(allocation.comments || '')
        setAdminNotes(allocation.adminNotes || allocation.comments || '')
        setPendingAllocation(!!allocation.pending)
        setNotifyWorkersBySms(!!allocation.notifyWorkersBySms)
        setDropMapMarker(!!allocation.dropMapMarker)
        setMapMarkerName(allocation.mapMarkerName || '')
        setMapMarkerCoordinates(hasValidCoordinates(allocation.mapMarkerCoordinates) ? allocation.mapMarkerCoordinates : [])
        setMapMarkerLink(allocation.mapMarkerLink || buildGoogleMapsLink(allocation.mapMarkerCoordinates || []))
        setMapMarkerSearchAddress('')
        setMapMarkerAddress(allocation.mapMarkerAddress || '')
        setSelectedSavedMarkerKey('')
        setMapViewMode('roadmap')
        setMapMarkerCenter(getMapCenter(allocation.mapMarkerCoordinates || []) || getMapCenter(sites.find(site => site.id === allocation.chosenSite)?.coordinates || []))
        setEndDate(multiDayState.endDate)
        setIsMultiDay(multiDayState.isMultiDay)
        setMultiDaySchedule(multiDayState.schedule)
        // setDocument(allocation.document || '')

        if (allocation.document && !allocation.document.url) {
            setDocument((allocation.document || []).filter(doc => doc?.source !== 'site'))
        } else if (allocation.document && allocation.document.url) {
            setDocument(allocation.document.source === 'site' ? [] : [allocation.document])
        } else {
            setDocument([])
        }
        setSelectedSiteDocumentUrls(normalizeDocumentArray(allocation.document).filter(doc => doc?.source === 'site').map(doc => doc.siteDocumentUrl || doc.url).filter(Boolean))
        setCurrentAllocationNumber(allocation.allocationId || allocation.id || '')

        setIsAdding(true)
        setEditId('')
        setEditIncompleteId(allocation.id)
        setFormType('incomplete')
        setPendingPreviousWorkersSmsLogs([])
        resetAllocationLogs(allocation.id)
        fetchAllocationActivityLogs(allocation.id)
    }

    // const handleMarkIncompleteCompleted = async (allocationId) => {
    //     try {
    //         setLoading(true)
    //         const resp = await postWithAuth('completeIncompleteAllocation', { id: allocationId, orgId: activeOrg.id })

    //         if (resp && resp.err) {
    //             toast.error(resp.err)
    //             setLoading(false)
    //             return
    //         }

    //         toast.success('Allocation marked as completed')
    //         setLoading(false)
    //         fetchIncompleteAllocations()
    //         fetchAllocations()
    //     } catch (e) {
    //         toast.error(e.message)
    //         setLoading(false)
    //     }
    // }

    const openDeleteAllocationPrompt = (allocationId, type = 'allocation') => {
        setDeleteAllocationComment('')
        setDeleteAllocationPrompt({ id: allocationId, type })
    }

    const handleDeleteAllocation = async (allocationId, cancellationComment = '') => {
        try {
            setLoading(true)
            const resp = await postWithAuth('deleteAllocation', { id: allocationId, orgId: activeOrg.id, cancellationComment })

            if (resp && resp.err) {
                toast.error(resp.err)
                setLoading(false)
                return
            }

            showSuccess('Allocation moved to Cancelled Allocations')
            setIsAdding(false)
            setEditId('')
            setEditIncompleteId('')
            clearFields()
            setConfirmMessage()
            setDeleteAllocationPrompt(null)
            setDeleteAllocationComment('')
            setLoading(false)
            fetchAllocations()
            fetchCancelledAllocations()
        } catch (e) {
            toast.error(e.message)
            setLoading(false)
        }
    }

    const handleDeleteIncompleteAllocation = async (allocationId, cancellationComment = '') => {
        try {
            setLoading(true)
            const resp = await postWithAuth('deleteIncompleteAllocation', { id: allocationId, orgId: activeOrg.id, cancellationComment })

            if (resp && resp.err) {
                toast.error(resp.err)
                setLoading(false)
                return
            }

            showSuccess('Incomplete allocation moved to Cancelled Allocations')
            setIsAdding(false)
            setEditId('')
            setEditIncompleteId('')
            clearFields()
            setConfirmMessage()
            setDeleteAllocationPrompt(null)
            setDeleteAllocationComment('')
            setLoading(false)
            fetchIncompleteAllocations()
            fetchCancelledAllocations()
        } catch (e) {
            toast.error(e.message)
            setLoading(false)
        }
    }

    const handleSendMessage = async () => {

        try {
            const effectiveMessage = messageDraftRef.current.trim()
            if (!effectiveMessage) {
                toast.error('Message is required.')
                return
            }
            if (extraNumbers.trim()) {
                toast.error('Click Add to include the additional mobile number.')
                return
            }
            setLoading(true)
            const resp = await postWithAuth('sendSmsToWorkers', {
                    id: showSendMessage.id,
                    message: effectiveMessage,
                    orgId: activeOrg.id,
                    extraNumbers: additionalSmsNumbers,
                })

            if (resp && resp.err) {
                toast.error(resp.err)
                setLoading(false)
                return
            }

            const sentAllocationId = showSendMessage?.id
            setLoading(false)
            // Reached nobody: keep the dialog and the draft so the admin can try again.
            if (!reachedAnyone(resp?.data)) {
                const { message: detail } = describeMessageResult(resp?.data, workerId => workerNameById.get(workerId) || 'Worker')
                toast.error('Message not sent. ' + detail)
                return
            }
            setShowSendMessage(false)
            setMessage('')
            setExtraNumbers('')
            setAdditionalSmsNumbers([])
            setSuccessMessage(describeMessageResult(resp?.data, workerId => workerNameById.get(workerId) || 'Worker'))
            if (sentAllocationId) {
                Promise.allSettled([
                    fetchSmsLogs(sentAllocationId),
                    fetchAllocationActivityLogs(sentAllocationId),
                ])
            }
        } catch (e) {
            toast.error(e.message)
            setLoading(false)
        }
    }

    const addSmsContact = () => {
        const normalizedNumber = extraNumbers.replace(/\D/g, '')
        if (normalizedNumber.length !== 10) {
            toast.error('Mobile number must be 10 digits.')
            return
        }
        if (additionalSmsNumbers.includes(normalizedNumber)) {
            toast.error('This mobile number has already been added.')
            return
        }
        setAdditionalSmsNumbers(prev => [...prev, normalizedNumber])
        setExtraNumbers('')
    }

    const sendPreviousWorkersSms = async () => {
        if (!previousWorkersSmsMessage.trim() || selectedPreviousWorkerIds.length === 0) return
        try {
            setLoading(true)
            const resp = await postWithAuth('sendSmsToWorkerIds', {
                workerIds: selectedPreviousWorkerIds,
                message: previousWorkersSmsMessage,
                orgId: activeOrg.id,
                allocationId: activeEditId || activeEditIncompleteId || '',
            })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            if (!reachedAnyone(resp?.data)) {
                const { message: detail } = describeMessageResult(resp?.data, workerId => workerNameById.get(workerId) || 'Worker')
                toast.error('Message not sent. ' + detail)
                return
            }
            setShowPreviousWorkersSms(false)
            if (!activeEditId && !activeEditIncompleteId) {
                setPendingPreviousWorkersSmsLogs(previous => [...previous, {
                    message: previousWorkersSmsMessage,
                    workerIds: [...selectedPreviousWorkerIds],
                }])
            }
            setPreviousWorkersSmsMessage('')
            setSelectedPreviousWorkerIds([])
            setSuccessMessage(describeMessageResult(resp?.data, workerId => workerNameById.get(workerId) || 'Worker'))
        } catch (e) {
            toast.error(e.message)
        } finally {
            setLoading(false)
        }
    }

    const closeSendMessageModal = () => {
        setShowSendMessage(false)
        setMessage('')
        setExtraNumbers('')
        setAdditionalSmsNumbers([])
    }

    const clearFields = () => {
        setChosenClient('')
        setChosenSite('')
        setChosenWorkers([])
        setChosenWorkerAssignments([])
        setChosenAssets([])
        const nextDayStart = addDays(toBusinessWallDate(new Date()), 1)
        nextDayStart.setHours(7, 0, 0, 0)
        setDate(nextDayStart)
        setSignsDroppedToSite(false)
        setSignsOnsiteAlready(false)
        setInitialSignsDroppedToSite(false)
        setAssetRequired(false)
        setRequiredWorkers('')
        setScopeOfWorks('')
        setComments('')
        setAdminNotes('')
        setPendingAllocation(false)
        setNotifyWorkersBySms(false)
        setDocument([])
        setSelectedSiteDocumentUrls([])
        setDropMapMarker(false)
        setMapMarkerName('')
        setMapMarkerCoordinates([])
        setMapMarkerLink('')
        setMapMarkerSearchAddress('')
        setMapMarkerCenter(null)
        setMapMarkerAddress('')
        setSelectedSavedMarkerKey('')
        setMapViewMode('roadmap')
        setTeamLeaders([])
        resetAllocationLogs('')
        // SMS sent from a new-allocation form that was abandoned belong to no allocation.
        setPendingPreviousWorkersSmsLogs([])
        setCurrentAllocationNumber('')
        setLoadedAllocationRecord(null)
        setMoveCompletedAllocationPromptOpen(false)
        setEndDate('')
        setIsMultiDay(false)
        setMultiDaySchedule(buildEmptyMultiDaySchedule())
    }

    const activeEditId = formType === 'allocation' ? editId : ''
    const activeEditIncompleteId = formType === 'incomplete' ? editIncompleteId : ''
    const hasActiveEditRecord = !!(activeEditId || activeEditIncompleteId)
    const isAllocationCompletedRecord = (allocation) => {
        const chosenWorkerIds = Array.isArray(allocation?.chosenWorkers) ? allocation.chosenWorkers.filter(Boolean) : []
        if (!chosenWorkerIds.length || !allocation?.id) return false
        return chosenWorkerIds.every(workerId => fullyCompletedShiftKeys.has(`${allocation.id}-${workerId}`))
    }
    const canMarkPendingOnForm = !loadedAllocationRecord || !isAllocationCompletedRecord(loadedAllocationRecord)

    const resetAllocationLogs = (allocationId) => {
        logsAllocationIdRef.current = allocationId || ''
        setSmsLogs([])
        setActivityLogs([])
    }

    const fetchSmsLogs = async (allocationId) => {
        try {
            const resp = await postWithAuth('fetchAllocationSmsLogs', { id: allocationId, orgId: activeOrg.id })
            if (logsAllocationIdRef.current !== allocationId) return
            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }
            setSmsLogs(resp?.data || [])
        } catch (e) {
            if (logsAllocationIdRef.current === allocationId) toast.error(e.message)
        }
    }

    const fetchAllocationActivityLogs = async (allocationId) => {
        try {
            const resp = await postWithAuth('fetchAllocationActivityLogs', { id: allocationId, orgId: activeOrg.id })
            if (logsAllocationIdRef.current !== allocationId) return
            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }
            setActivityLogs(resp?.data || [])
        } catch (e) {
            if (logsAllocationIdRef.current === allocationId) toast.error(e.message)
        }
    }

    const openAllocationView = async (allocation) => {
        setLoading(true)
        try {
            const detail = allocation?.isIncomplete ? allocation : await loadAllocationDetail(allocation)
            resetAllocationLogs(detail.id)
            setViewAllocation({ ...detail, isIncomplete: !!allocation?.isIncomplete })
            fetchAllocationActivityLogs(detail.id)
            if (allocations.some(row => row.id === detail.id)) fetchSmsLogs(detail.id)
        } catch (error) {
            toast.error(error.message)
        } finally {
            setLoading(false)
        }
    }

    const handleAddAsset = () => {
        if (!pickAsset?.value || !pickWorker?.value) return
        setChosenAssets(current => [...current, { asset: pickAsset.value, worker: pickWorker.value }])
        setAssetRequired(false)
        setPickAsset('')
        setPickWorker('')
        setShowAddAsset(false)
    }

    const getAssetPickerLabel = (asset) => {
        if (!asset) return ''
        const type = asset.rateType || asset.assetType || 'Asset'
        const identity = [asset.make, asset.model].filter(Boolean).join(' ') || asset.machineId || ''
        const registration = asset.regoNo || ''
        return `${type}${identity ? ` — ${identity}` : ''}${registration ? ` (${registration})` : ''}`
    }

    const assignedAssetIds = new Set((chosenAssets || []).map(item => item.asset))
    const availablePickerAssets = assets.filter(asset => !assignedAssetIds.has(asset.id))
    const assetPickerWorkerIds = new Set(chosenWorkers.filter(Boolean).map(worker => worker.value))
    const availablePickerWorkers = workers.filter(worker => assetPickerWorkerIds.has(worker.id))
    const selectedPickerAsset = assets.find(asset => asset.id === pickAsset?.value)
    const selectedPickerWorker = workers.find(worker => worker.id === pickWorker?.value)
    const selectedPickerWorkerName = selectedPickerWorker
        ? formatName(`${selectedPickerWorker.firstName || ''} ${selectedPickerWorker.lastName || ''}`).trim()
        : ''

    const addPreviousWorkerToSelection = (worker) => {
        if (!worker?.id) return
        const alreadySelected = chosenWorkers.some(item => item.value === worker.id)
        if (alreadySelected) return
        setChosenWorkers(prev => [...prev, { label: worker.name, value: worker.id }])
    }

    const handleAddScopePhrase = async (phrase) => {
        const trimmed = phrase.trim()
        if (!trimmed) {
            throw new Error('Empty phrase')
        }
        if (scopePhrases.some(p => (p.phrase || p.text || '').toLowerCase() === trimmed.toLowerCase())) {
            toast.error('Phrase already exists.')
            throw new Error('Duplicate phrase')
        }
        try {
            const resp = await postWithAuth('saveScopePhrase', { phrase: trimmed, orgId: activeOrg.id })
            if (resp && resp.err) {
                toast.error(resp.err)
                throw new Error(resp.err)
            }
            if (resp && resp.data) {
                setScopePhrases(prev => [...prev, { id: resp.data, phrase: trimmed }])
            }
        } catch (e) {
            toast.error(e.message)
            throw e
        }
    }

    const handleRemoveScopePhrase = async (phrase) => {
        if (!phrase?.id) {
            return
        }
        try {
            const resp = await postWithAuth('deleteScopePhrase', { id: phrase.id, orgId: activeOrg.id })
            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }
            setScopePhrases(prev => prev.filter(p => p.id !== phrase.id))
        } catch (e) {
            toast.error(e.message)
        }
    }

    const handleUpdateScopePhrase = async (phrase) => {
        const trimmed = (phrase?.text || '').trim()
        if (!phrase?.id || !trimmed) {
            return
        }
        if (scopePhrases.some(p => p.id !== phrase.id && (p.phrase || p.text || '').toLowerCase() === trimmed.toLowerCase())) {
            toast.error('Phrase already exists.')
            return
        }
        try {
            const resp = await postWithAuth('saveScopePhrase', { id: phrase.id, phrase: trimmed, orgId: activeOrg.id })
            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }
            setScopePhrases(prev => prev.map(p => (p.id === phrase.id ? { ...p, phrase: trimmed } : p)))
        } catch (e) {
            toast.error(e.message)
        }
    }

    const day = d => new Date(d.getFullYear(), d.getMonth(), d.getDate())
    const activeFormAllocationId = isAdding && formType === 'allocation' ? editId : ''
    const activeFormIncompleteAllocationId = isAdding && formType === 'incomplete' ? editIncompleteId : ''
    const selectedWorkerWarningDate = useMemo(() => (date ? day(new Date(date)) : null), [date])
    const selectedWorkerIds = useMemo(() => chosenWorkers.map(worker => worker?.value).filter(Boolean), [chosenWorkers])
    const selectedWorkerLeaveWarnings = useMemo(() => {
        if (!selectedWorkerWarningDate) return []
        return selectedWorkerIds.flatMap(workerId => {
            const workerName = formatName(workerNameById.get(workerId) || 'Unknown worker')
            const leaves = workerLeavesByWorkerId.get(workerId) || []
            return leaves
                .filter(leave =>
                    !Number.isNaN(new Date(leave.startDate)) &&
                    !Number.isNaN(new Date(leave.endDate)) &&
                    selectedWorkerWarningDate >= day(new Date(leave.startDate)) &&
                    selectedWorkerWarningDate <= day(new Date(leave.endDate))
                )
                .map(leave => ({
                    id: `leave-${leave.id}-${workerId}`,
                    workerName,
                    startDate: leave.startDate,
                    endDate: leave.endDate,
                }))
        })
    }, [selectedWorkerWarningDate, selectedWorkerIds, workerLeavesByWorkerId, workerNameById])
    const allocationWarningRows = useMemo(
        () => [
            ...allocationRows.map(row => ({ ...row, warningType: 'allocation' })),
            ...incompleteRows.map(row => ({ ...row, warningType: 'incomplete' })),
        ],
        [allocationRows, incompleteRows]
    )
    const selectedWorkerAllocationWarnings = useMemo(() => {
        if (!selectedWorkerWarningDate) return []
        return allocationWarningRows
            .filter(row => {
                if (!isSameDay(new Date(row.date), selectedWorkerWarningDate)) return false
                if (row.warningType === 'allocation' && row.id === activeFormAllocationId) return false
                if (row.warningType === 'incomplete' && row.id === activeFormIncompleteAllocationId) return false
                return selectedWorkerIds.some(workerId => (row.chosenWorkers || []).includes(workerId))
            })
            .flatMap(row =>
                selectedWorkerIds
                    .filter(workerId => (row.chosenWorkers || []).includes(workerId))
                    .map(workerId => ({
                        id: `${row.warningType}-${row.id}-${workerId}`,
                        workerName: workerNameById.get(workerId) || 'Unknown worker',
                        client: row.client || '-',
                        site: row.site || '-',
                        date: row.date,
                        warningType: row.warningType,
                    }))
            )
    }, [selectedWorkerWarningDate, allocationWarningRows, activeFormAllocationId, activeFormIncompleteAllocationId, selectedWorkerIds, workerNameById])
    const hasMissingWorkerCapacity = useMemo(() => chosenWorkers.some(workerOption => {
        const workerInfo = workerById.get(workerOption.value)
        const workerTypeValues = Array.isArray(workerInfo?.workerType) ? workerInfo.workerType : []
        if (workerTypeValues.length <= 1) return false
        const assignment = chosenWorkerAssignments.find(item => item.workerId === workerOption.value)
        return !assignment?.capacity
    }), [chosenWorkers, chosenWorkerAssignments, workerById])
    const handleDownloadPastRange = () => {
        if (!pastStartDate || !pastEndDate) {
            setConfirmMessage({
                title: 'Warning',
                message: 'Please select both a start date and end date.',
                confirmLabel: 'OK',
                hideCancel: true,
            })
            return
        }
        const start = pastStartDate ? day(new Date(pastStartDate)) : null
        const end = pastEndDate ? day(new Date(pastEndDate)) : null
        if (start && end && start > end) {
            toast.error('End date must be the same as or after the start date.')
            return
        }
        const matching = allocationRows.filter(allocation => {
            const query = normalizeSearch(pastSearch.trim())
        if (query && ![allocation.allocationId, allocation.client, allocation.site, allocation.worker].some(value => normalizeSearch(String(value || '')).includes(query))) return false

            const allocationDate = day(new Date(allocation.date))
            if (start && allocationDate < start) return false
            if (end && allocationDate > end) return false
            if (pastFilterClient && allocation.chosenClient !== pastFilterClient.value) return false
            if (pastFilterWorker && !(allocation.chosenWorkers || []).includes(pastFilterWorker.value)) return false
            if (pastSite && allocation.chosenSite !== pastSite.value) return false
            return true
        })
        if (matching.length === 0) {
            toast.error('No allocations found for the selected criteria.')
            return
        }
        startAllocationDownloadQueue(matching)
    }
    const formatSmsLogDate = (timestamp) => {
        if (!timestamp) {
            return '-'
        }
        const logDate = new Date(timestamp)
        if (Number.isNaN(logDate.getTime())) return '-'
        const dateLabel = format(logDate, 'EEE, dd MMM yyyy').replace(/^Thu,/, 'Thur,')
        return (
            <span className='allocationActivityDateTime'>
                <span>{dateLabel}</span>
                <span>{formatTime(logDate)}</span>
            </span>
        )
    }

    const normalizedExtraNumber = extraNumbers.replace(/\D/g, '')
    const hasInvalidExtraNumber = extraNumbers.trim().length > 0 && normalizedExtraNumber.length !== 10

    const todayDate = useMemo(() => day(toBusinessWallDate(new Date())), [])
    const upcomingWorkerLeaves = useMemo(() => workerLeaves
        .map(leave => {
            const worker = workerById.get(leave.workerId)
            return {
                ...leave,
                workerName: worker ? formatName(`${worker.firstName || ''} ${worker.lastName || ''}`).trim() : 'Unknown',
            }
        })
        .filter(leave => {
            const endDate = leave.endDate ? day(new Date(leave.endDate)) : null
            return endDate && !Number.isNaN(endDate.getTime()) && endDate >= todayDate
        })
        .sort((first, second) => new Date(first.startDate).getTime() - new Date(second.startDate).getTime()), [workerLeaves, workerById, todayDate])
    const filteredUpcomingWorkerLeaves = useMemo(() => upcomingWorkerLeaves.filter(leave => {
        const query = normalizeSearch(String(availableWorkerSearch || '').trim())
        return !query || normalizeSearch(String(leave.workerName || '')).includes(query)
    }), [upcomingWorkerLeaves, availableWorkerSearch])
    const nextDayDate = useMemo(() => addDays(toBusinessWallDate(new Date()), 1), [])
    const availableWorkerTypeOptions = ['On Leave', 'On Site', 'Available', 'All']
    const onLeaveWorkers = useMemo(() => workers.filter(worker => upcomingWorkerLeaves.some(leave =>
        leave.workerId === worker.id && leave.startDate && leave.endDate &&
        day(nextDayDate) >= day(new Date(leave.startDate)) && day(nextDayDate) <= day(new Date(leave.endDate))
    )), [workers, upcomingWorkerLeaves, nextDayDate])
    const onSiteWorkers = useMemo(() => workers.filter(worker => [...allocations, ...incompleteAllocations].some(allocation =>
        isSameDay(new Date(allocation.date), nextDayDate) && (allocation.chosenWorkers || []).includes(worker.id)
    )), [workers, allocations, incompleteAllocations, nextDayDate])
    const currentlyAvailableWorkers = useMemo(() => workers.filter(worker =>
        !onLeaveWorkers.some(item => item.id === worker.id) && !onSiteWorkers.some(item => item.id === worker.id)
    ), [workers, onLeaveWorkers, onSiteWorkers])
    const displayedAvailableWorkers = useMemo(() => availableWorkerType === 'On Leave'
        ? onLeaveWorkers
        : availableWorkerType === 'On Site'
            ? onSiteWorkers
            : availableWorkerType === 'Available'
                ? currentlyAvailableWorkers
                : workers, [availableWorkerType, onLeaveWorkers, onSiteWorkers, currentlyAvailableWorkers, workers])
    const filteredDisplayedAvailableWorkers = useMemo(() => displayedAvailableWorkers.filter(worker => {
        const query = normalizeSearch(String(availableWorkerSearch || '').trim())
        if (!query) return true
        return normalizeSearch(formatName([`${worker.firstName || ''} ${worker.lastName || ''}`, ...(Array.isArray(worker.workerType) ? worker.workerType : [])]
            .join(' '))).includes(query)
    }), [displayedAvailableWorkers, availableWorkerSearch])
    const getWorkerOnSiteNames = (workerId) => [...allocations, ...incompleteAllocations]
        .filter(allocation => isSameDay(new Date(allocation.date), nextDayDate) && (allocation.chosenWorkers || []).includes(workerId))
        .map(allocation => sites.find(site => site.id === allocation.chosenSite)?.name)
        .filter(Boolean)
        .filter((siteName, index, siteNames) => siteNames.indexOf(siteName) === index)
        .join(', ')
    const isAllocationPast = allocation => {
        const allocationDate = day(new Date(allocation.date))
        if (allocationDate < todayDate) return true
        if (allocationDate > todayDate) return false
        const chosenWorkerIds = Array.isArray(allocation?.chosenWorkers) ? allocation.chosenWorkers.filter(Boolean) : []
        if (!chosenWorkerIds.length) return false
        return chosenWorkerIds.every(workerId => fullyCompletedShiftKeys.has(`${allocation.id}-${workerId}`))
    }
    const isAllocationIncompleteForOrdering = (allocation) => {
        return allocationNeedsAttention(allocation)
    }
    const sortAllocationsWithIncompleteFirst = (rows = []) => rows.slice().sort((first, second) => {
        const firstIncomplete = isAllocationIncompleteForOrdering(first) ? 1 : 0
        const secondIncomplete = isAllocationIncompleteForOrdering(second) ? 1 : 0
        if (firstIncomplete !== secondIncomplete) return secondIncomplete - firstIncomplete
        const firstDate = new Date(first.date).getTime()
        const secondDate = new Date(second.date).getTime()
        if (firstDate !== secondDate) return firstDate - secondDate
        return String(first.allocationId || first.id || '').localeCompare(String(second.allocationId || second.id || ''))
    })
    // The editor replaces the overview. Avoid rebuilding every hidden overview
    // filter after each worker, team-leader, or asset selection.
    const futureAllocations = isAdding ? [] : futureAllocationSort.sortedData.filter(allocation => {
        return !isAllocationPast(allocation)
    })
    const pastAllocations = isAdding ? [] : pastAllocationSort.sortedData.filter(allocation => {
        return isAllocationPast(allocation)
    })
    const siteDropdownOptions = (isAdding ? [] : sites.filter(r => !r.archived)).map(r => ({
        label: `${clientById.get(r.clientId)?.name || ''} - ${r.name}`,
        value: r.id
    }))
    const filterAllocationRows = (rows, { dateFilter, clientFilter, siteFilter, workerFilter, searchText }) => rows.filter(allocation => {
        const allocationDate = new Date(allocation.date)
        if (dateFilter && !isSameDay(allocationDate, new Date(dateFilter))) return false
        if (clientFilter && allocation.chosenClient !== clientFilter.value) return false
        if (siteFilter && allocation.chosenSite !== siteFilter.value) return false
        if (workerFilter && !(allocation.chosenWorkers || []).includes(workerFilter.value)) return false
        const queryTerms = normalizeSearch(String(searchText || '').trim()).replace(/[^a-z0-9]+/g, ' ').split(/\s+/).filter(Boolean)
        if (queryTerms.length) {
            const client = clients.find(row => row.id === allocation.chosenClient)
            const site = sites.find(row => row.id === allocation.chosenSite)
            const workerLabels = formatName((allocation.chosenWorkers || []).map(workerId => {
                const worker = workers.find(row => row.id === workerId)
                return worker ? `${worker.firstName || ''} ${worker.lastName || ''}`.trim() : workerId
            }).join(' '))
            let rawAllocationText = ''
            try {
                rawAllocationText = JSON.stringify(allocation)
            } catch {
                rawAllocationText = ''
            }
            const haystack = [
                client?.name,
                site?.name,
                site?.address,
                workerLabels,
                allocation.client,
                allocation.site,
                allocation.worker,
                allocation.scopeOfWorks,
                allocation.comments,
                allocation.mapMarkerAddress,
                allocation.mapMarkerName,
                allocation.id,
                allocation.date,
                rawAllocationText,
            ].map(value => String(value || '').toLowerCase()).join(' ').replace(/[^a-z0-9]+/g, ' ')
            if (!queryTerms.every(term => haystack.includes(term))) return false
        }
        return true
    })

    const toggleAllocationSelection = (allocationId) => {
        setSelectedAllocationIds(prev => (
            prev.includes(allocationId) ? prev.filter(id => id !== allocationId) : [...prev, allocationId]
        ))
    }

    const filteredPastAllocations = pastAllocations.filter(allocation => {
        const query = normalizeSearch(pastSearch.trim())
        if (query && ![allocation.allocationId, allocation.client, allocation.site, allocation.worker].some(value => normalizeSearch(String(value || '')).includes(query))) return false

        const allocationDate = day(new Date(allocation.date))
        const start = pastStartDate ? day(new Date(pastStartDate)) : null
        const end = pastEndDate ? day(new Date(pastEndDate)) : null
        if (start && allocationDate < start) return false
        if (end && allocationDate > end) return false
        if (pastFilterClient && allocation.chosenClient !== pastFilterClient.value) return false
        if (pastFilterWorker && !(allocation.chosenWorkers || []).includes(pastFilterWorker.value)) return false
        if (pastSite && allocation.chosenSite !== pastSite.value) return false
        return true
    })
    const pastPageSize = 20
    const pastPageCount = Math.max(1, Math.ceil(filteredPastAllocations.length / pastPageSize))
    const safePastPage = Math.min(pastPage, pastPageCount)
    const paginatedPastAllocations = filteredPastAllocations.slice((safePastPage - 1) * pastPageSize, safePastPage * pastPageSize)
    const paginatedPastAllocationIds = paginatedPastAllocations.map(allocation => allocation.id)
    const allPaginatedPastAllocationsSelected = paginatedPastAllocationIds.length > 0
        && paginatedPastAllocationIds.every(id => selectedAllocationIds.includes(id))
    const handleDownloadSelected = () => {
        const selectedAllocations = filteredPastAllocations.filter(allocation => selectedAllocationIds.includes(allocation.id))
        if (selectedAllocations.length === 0) {
            toast.error('No allocations selected.')
            return
        }
        setDownloadChoiceAllocations(selectedAllocations)
    }
    const currentDayDate = day(toBusinessWallDate(new Date()))
    const customDayDate = customAllocationDate ? day(new Date(customAllocationDate)) : day(nextDayDate)
    const viewRangeDays = allocationViewTab === 'week' ? 7 : 1
    const viewStartDate = allocationViewTab === 'today'
        ? currentDayDate
        : allocationViewTab === 'custom'
            ? customDayDate
            : day(nextDayDate)
    const viewEndDate = day(addDays(viewStartDate, viewRangeDays - 1))
    const isWithinViewRange = (value) => {
        const target = day(new Date(value))
        return target >= viewStartDate && target <= viewEndDate
    }
    const customWithoutDate = allocationViewTab === 'custom' && !customAllocationDate
    const customSearchesAllFuture = customWithoutDate && !!String(weekAllocationSearch || '').trim()
    const includeInAllocationView = value => {
        if (customWithoutDate) {
            return customSearchesAllFuture && day(new Date(value)) >= currentDayDate
        }
        return isWithinViewRange(value)
    }
    const viewAllocations = isAdding ? [] : [
        ...allocationRows
            .filter(r => includeInAllocationView(r.date))
            .map(allocation => ({ ...allocation, isIncomplete: false })),
        ...incompleteRows
            .filter(r => includeInAllocationView(r.date))
            .map(allocation => ({ ...allocation, isIncomplete: true }))
    ].sort((a, b) => new Date(a.date) - new Date(b.date))

    const derivedIncompleteAllocations = (isAdding ? [] : allocationRows)
        .filter(allocation => {
            const assignedWorkersCount = (allocation.chosenWorkers || []).length
            const requiredWorkersCount = Number(allocation.requiredWorkers) || 0
            return (requiredWorkersCount > 0 && assignedWorkersCount < requiredWorkersCount) || allocation.showAsIncomplete || allocation.assetRequired
        })
        .map(allocation => ({ ...allocation, derivedIncomplete: true }))
    const combinedIncompleteAllocations = isAdding ? [] : [
        ...incompleteRows.map(row => ({ ...row, derivedIncomplete: false })),
        ...derivedIncompleteAllocations
    ]
    const combinedIncompleteSort = useTableSort(combinedIncompleteAllocations, { key: 'date', direction: 'ascending' });
    const filteredViewAllocations = sortAllocationsWithIncompleteFirst(filterAllocationRows(viewAllocations, {
        dateFilter: allocationViewTab === 'custom' && customAllocationDate ? customAllocationDate : '',
        clientFilter: futureFilterClient,
        siteFilter: futureFilterSite,
        workerFilter: futureFilterWorker,
        searchText: weekAllocationSearch,
    }))
    const filteredIncompleteAllocations = filterAllocationRows(combinedIncompleteSort.sortedData, {
        searchText: incompleteFilterSearch,
    }).filter(allocation => {
        const allocationDate = day(new Date(allocation.date))
        const startDate = incompleteFilterStartDate ? day(new Date(incompleteFilterStartDate)) : null
        const endDate = incompleteFilterEndDate ? day(new Date(incompleteFilterEndDate)) : null
        if (startDate && allocationDate < startDate) return false
        if (endDate && allocationDate > endDate) return false
        return true
    })
    const incompletePageSize = 20
    const incompletePageCount = Math.max(1, Math.ceil(filteredIncompleteAllocations.length / incompletePageSize))
    const safeIncompletePage = Math.min(incompletePage, incompletePageCount)
    const paginatedIncompleteAllocations = filteredIncompleteAllocations.slice((safeIncompletePage - 1) * incompletePageSize, safeIncompletePage * incompletePageSize)
    const filteredFutureAllocations = sortAllocationsWithIncompleteFirst(filterAllocationRows(futureAllocations, {
        dateFilter: futureFilterDate,
        clientFilter: futureFilterClient,
        siteFilter: futureFilterSite,
        workerFilter: futureFilterWorker,
        searchText: weekAllocationSearch,
    }))
    const filteredFuturePageAllocations = sortAllocationsWithIncompleteFirst(filterAllocationRows(futureAllocations, {
        searchText: futurePageSearch,
    }).filter(allocation => {
        const allocationDate = day(new Date(allocation.date))
        const startDate = futurePageStartDate ? day(new Date(futurePageStartDate)) : null
        const endDate = futurePageEndDate ? day(new Date(futurePageEndDate)) : null
        if (startDate && allocationDate < startDate) return false
        if (endDate && allocationDate > endDate) return false
        return true
    }))
    const futurePageSize = 20
    const futurePageCount = Math.max(1, Math.ceil(filteredFuturePageAllocations.length / futurePageSize))
    const safeFuturePage = Math.min(futurePage, futurePageCount)
    const paginatedFuturePageAllocations = filteredFuturePageAllocations.slice((safeFuturePage - 1) * futurePageSize, safeFuturePage * futurePageSize)
    const kpiAllocations = isAdding || allocationViewTab === 'custom' ? [] : [...allocationRows, ...incompleteRows]
        .filter(allocation => isWithinViewRange(allocation.date))
    const scheduledKpiCount = kpiAllocations.length
    const deployedKpiCount = new Set(kpiAllocations.flatMap(allocation => allocation.chosenWorkers || [])).size
    const selectedPeriodHours = (isAdding ? [] : timeSheets).reduce((hours, timeSheet) => {
        if (!timeSheet?.clockIn || !timeSheet?.clockOut) return hours
        const shiftDate = new Date(timeSheet.date || timeSheet.clockIn)
        if (Number.isNaN(shiftDate.getTime()) || !isWithinViewRange(shiftDate)) return hours
        const elapsed = (new Date(timeSheet.clockOut) - new Date(timeSheet.clockIn)) / 3600000
        if (!Number.isFinite(elapsed) || elapsed <= 0) return hours
        const unpaidBreak = timeSheet.breakIncludedInPay === false ? (Number(timeSheet.breakMinutes) || 0) / 60 : 0
        return hours + Math.max(0, elapsed - unpaidBreak)
    }, 0).toFixed(2)
    const kpiAllocationLabel = allocationViewTab === 'today'
        ? "Today's Allocations"
        : allocationViewTab === 'next'
            ? 'Next Day Allocations'
            : 'Weekly Allocations'
    const kpiPeriodHint = allocationViewTab === 'today'
        ? 'Scheduled today'
        : allocationViewTab === 'next'
            ? 'Scheduled next day'
            : 'Scheduled this period'
    const rangeLabel = allocationViewTab === 'week'
        ? `${format(viewStartDate, 'd MMM')} – ${format(viewEndDate, 'd MMM')}`
        : format(viewStartDate, 'EEE, d MMMM yyyy')
    const getAllocationLocationLabel = (allocation) => {
        const hasMapMarker = !!allocation?.dropMapMarker
            || !!String(allocation?.mapMarkerAddress || allocation?.mapMarkerName || '').trim()
            || hasValidCoordinates(allocation?.mapMarkerCoordinates)
        if (hasMapMarker) {
            const markerAddress = String(allocation?.mapMarkerAddress || '').trim()
            if (markerAddress) return markerAddress
            const markerName = String(allocation?.mapMarkerName || '').trim()
            if (markerName) return markerName
            return null
        }
        return sites.find(site => site.id === allocation?.chosenSite)?.address || ''
    }
    const getAllocationCardLocationLabel = (allocation) => {
        const hasMapMarker = !!allocation?.dropMapMarker
            || !!String(allocation?.mapMarkerAddress || allocation?.mapMarkerName || '').trim()
            || hasValidCoordinates(allocation?.mapMarkerCoordinates)
        if (hasMapMarker) {
            const markerAddress = String(allocation?.mapMarkerAddress || '').trim()
            if (markerAddress) return markerAddress
            const markerName = String(allocation?.mapMarkerName || '').trim()
            if (markerName) return markerName
            return null
        }
        const siteAddress = sites.find(site => site.id === allocation?.chosenSite)?.address
        if (String(siteAddress || '').trim()) return siteAddress
        return ''
    }
    const getAllocationWorkerName = (allocation, workerId) => {
        const worker = workers.find(item => item.id === workerId)
        if (!worker) return workerId
        const isTeamLeader = (allocation.teamLeaders || []).map(item => item.value || item).includes(workerId)
        return formatName(`${worker.firstName || ''} ${worker.lastName || ''}`).trim() + (isTeamLeader ? ' (TL)' : '')
    }
    const editAllocation = (allocation) => {
        setViewAllocation(null)
        if (allocation?.isIncomplete) {
            handleViewIncomplete(allocation)
        } else {
            handleView(allocation)
        }
    }

    return <div>
        <Header />
        <LoadingPopup visible={loading || uploadingDocument || fetchingDownload || !!downloadAllocation} message={fetchingDownload || downloadAllocation ? "Downloading allocation..." : uploadingDocument ? "Uploading document..." : "Loading..."} />
        <div className='pageShell'>
            <Sidebar />
            <PageContent as="div" className='pageContent allocationPageContent'>
                <PageHeader title={<>Allocations</>} subtitle={<>Current, past, upcoming and cancelled allocations</>} />


                {isAdding ? <>
                    <div className='pageBody allocationNewModalBody'>
                        <div className='allocationFormShell allocationNewModal'>
                            <div className='allocationNewModalHeader'>
                            <div className='allocationFormTitleBlock'>
                                <h3 className='pageTitle'>{hasActiveEditRecord ? (formType === 'incomplete' ? 'Edit Incomplete Allocation' : 'Edit Allocation') : (formType === 'incomplete' ? 'New Incomplete Allocation' : 'New Allocation')}</h3>
                                {!hasActiveEditRecord && <p className='pageSubtitle'>{formType === 'incomplete' ? 'Create an incomplete allocation that can be staffed later.' : 'Create and staff a job site allocation'}</p>}
                                {hasActiveEditRecord && currentAllocationNumber && <p className='allocationFormHeaderMeta'><strong>Allocation ID:</strong> {currentAllocationNumber}</p>}
                                <div className="allocationFormToggles" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                                {canMarkPendingOnForm && (
                                    <FieldLabel choice className='allocationPendingToggle'>
                                        <UiCheckbox type='checkbox' checked={pendingAllocation} onChange={event => setPendingAllocation(event.target.checked)} />
                                        <span className='allocationPendingToggleTrack' aria-hidden='true'>
                                            <span className='allocationPendingToggleThumb' />
                                        </span>
                                        <span className='allocationPendingToggleText'>Pending</span>
                                    </FieldLabel>
                                )}
                                    <FieldLabel choice className="allocationPendingToggle">
                                        <UiCheckbox type="checkbox" checked={notifyWorkersBySms} disabled={loading} onChange={event => setNotifyWorkersBySms(event.target.checked)} />
                                        <span className="allocationPendingToggleTrack" aria-hidden="true"><span className="allocationPendingToggleThumb" /></span>
                                        <span className="allocationPendingToggleText">Also notify workers by SMS</span>
                                    </FieldLabel>
                                </div>
                            </div>
                                <div className='allocationNewModalHeaderActions'>
                                    <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton allocationCancelButton' onClick={() => { setIsAdding(false); setPendingPreviousWorkersSmsLogs([]) }}>Cancel</Button>
                                    <Button data-action-tone="save" variant="soft" size="md"  className='allocationSaveButton' disabled={loading || !chosenClient || !date || !requiredWorkers || hasMissingWorkerCapacity} onClick={() => handleSaveAllocation()}>Save</Button>
                                </div>
                            </div>
                            {currentAllocationNumber && !hasActiveEditRecord ? (
                                <div className='allocationFormMeta'>
                                    <strong>Allocation ID:</strong> {currentAllocationNumber}
                                </div>
                            ) : null}
                            <Section title={'Site'} className='width-100' variant='card'>
                                <div className='allocationClientRow'>
                                    <div className='allocationClientPrimaryFields'>
                                        <div className='allocationClientFieldGrid'>
                                            <div>
                                                <Dropdown
                                                    options={clientDropdownOptions}
                                                    value={chosenClient}
                                                    setValue={(value) => {
                                                        setChosenClient(value)
                                                        setChosenSite('')
                                                        setSelectedSiteDocumentUrls([])
                                                        setSavedLocationMarkers([])
                                                        clearMarkerSelection()
                                                    }}
                                                    label={'Client'}
                                                    placeholder={'Select client'}
                                                />
                                            </div>

                                            <div>
                                                <Dropdown
                                                    options={sites.filter(r => chosenClient && r.clientId === chosenClient.value).map(r => ({ label: r.name, value: r.id }))}
                                                    value={chosenSite}
                                                    setValue={handleSiteChange}
                                                    label={'Site'}
                                                    placeholder={'Select site'}
                                                />
                                                                                    <div className='allocationClientChecks'>
                                            <Checkbox value={dropMapMarker} setValue={(value) => {
                                                setDropMapMarker(value)
                                                if (!value) {
                                                    clearMarkerSelection()
                                                }
                                            }} label={'Drop a location marker'} />
                                        </div>
                                            </div>
                                        </div>


                                        {dropMapMarker && (
                                            <div className='allocationEmbeddedPanel'>
                                                <div className='allocationEmbeddedPanelHeader'>Location Marker</div>
                                                <div className="allocationMarkerEmbeddedLayout">
                                                    <div className="allocationMarkerSavedColumn">
                                                        <div className="markerPanelHeader">
                                                            <h4 className="allocationMarkerColumnTitle">Saved Markers</h4>
                                                            <Button variant="secondary" size="md"
                                                                type="button"
                                                                className="secondaryButton markerClearButton"
                                                                onClick={clearMarkerSelection}
                                                            >
                                                                Clear
                                                            </Button>
                                                        </div>
                                                        <div className="markerHistoryList markerHistoryListEmbedded">
                                                            {savedMapMarkers.length === 0 ? (
                                                                <div className="markerEmptyState">No saved markers for this client and site yet.</div>
                                                            ) : savedMapMarkers.map(marker => (
                                                                <button
                                                                    key={marker.key}
                                                                     type="button"
                                                                     className={`markerHistoryItem ${selectedSavedMarkerKey === marker.key ? 'markerHistoryItemActive' : ''}`}
                                                                     onClick={() => setSelectedSavedMarkerKey(marker.key)}
                                                                 >
                                                                     <span>{marker.name}</span>
                                                                 </button>
                                                             ))}
                                                         </div>
                                                         <Button variant="secondary" size="md"
                                                             type="button"
                                                             className="markerUseLocationButton"
                                                             disabled={!selectedSavedMarker}
                                                             onClick={() => applySavedMarker(selectedSavedMarker)}
                                                         >
                                                             Use Location
                                                         </Button>
                                                     </div>

                                                    <div className="allocationMarkerMainColumn">
                                                        <div className="allocationMarkerMainHeader">
                                                            <h4 className="allocationMarkerColumnTitle">New Marker</h4>
                                                            <div className="markerViewToggle">
                                                                <Button variant="secondary" size="md"
                                                                    type="button"
                                                                    className={mapViewMode === 'roadmap' ? 'markerViewButtonActive' : 'markerViewButton'}
                                                                    onClick={() => setMapViewMode('roadmap')}
                                                                >
                                                                    Map
                                                                </Button>
                                                                <Button variant="secondary" size="md"
                                                                    type="button"
                                                                    className={mapViewMode === 'satellite' ? 'markerViewButtonActive' : 'markerViewButton'}
                                                                    onClick={() => setMapViewMode('satellite')}
                                                                >
                                                                    Satellite
                                                                </Button>
                                                            </div>
                                                        </div>

                                                        <Input disabled={false} value={mapMarkerName} setValue={handleMarkerNameChange} deferCommit onDraftChange={value => { mapMarkerNameDraftRef.current = value }} placeholder={'Marker name'} label={'Marker Name'} />
                                                        <FieldLabel className="fieldLabel">Address Search</FieldLabel>
                                                        <Autocomplete
                                                            onLoad={ref => {
                                                                markerSearchBoxRef.current = ref
                                                            }}
                                                            onPlaceChanged={handleMarkerPlacesChanged}
                                                            options={{
                                                                componentRestrictions: { country: 'au' },
                                                                fields: ['formatted_address', 'geometry', 'name', 'vicinity'],
                                                            }}
                                                        >
                                                            <LocationSearchInput
                                                                key={`marker-address-${chosenSite?.value || 'site'}-${dropMapMarker ? 'enabled' : 'disabled'}`}
                                                                className='clientAutocompleteField'
                                                                type='text'
                                                                placeholder='Enter an address or landmark'
                                                                address={mapMarkerSearchAddress}
                                                                aria-label='Address Search'
                                                            />
                                                        </Autocomplete>
                                                         <div className="allocationMarkerMapWrap">
                                                             <AllocationMarkerMap
                                                                 center={mapMarkerCenter || getMapCenter(selectedSite?.coordinates || [])}
                                                                 markerCoordinates={mapMarkerCoordinates}
                                                                 height="170px"
                                                                 zoom={hasValidCoordinates(mapMarkerCoordinates) ? 18 : 13}
                                                                 mapTypeId={mapViewMode}
                                                                 onMapClick={handleMarkerMapClick}
                                                             />
                                                         </div>
                                                         <div className="markerMapActions">
                                                             {hasValidCoordinates(mapMarkerCoordinates) ? (
                                                                 <a href={mapMarkerLink || buildGoogleMapsLink(mapMarkerCoordinates)} target="_blank" rel="noreferrer">
                                                                     Open in Google Maps
                                                                 </a>
                                                             ) : <span />}
                                                             <Button data-action-tone="save" variant="soft" size="md"
                                                                 type="button"
                                                                className="markerSaveButton saveWithIcon"
                                                                 disabled={savingMapMarker || !hasValidCoordinates(mapMarkerCoordinates)}
                                                                 onClick={handleSaveMapMarker}
                                                             >
                                                                 {savingMapMarker ? 'Saving...' : 'Save Marker'}
                                                             </Button>
                                                         </div>
                                                     </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className='allocationClientDateColumn'>
                                        <div className="allocationDateField">
                                            <MyCalendar noMaxDate disabled={false} startDate={date} setStartDate={setDate} label={'Start Date'} timeIntervals={15} dateFormat={'EEE, d MMM yyyy HH:mm'} />
                                        </div>
                                        <div className='allocationClientChecks allocationClientDateChecks'>
                                            <Checkbox value={isMultiDay} setValue={(value) => {
                                                setIsMultiDay(value)
                                                if (!value) {
                                                    setEndDate('')
                                                    setMultiDaySchedule(prev => prev.map(day => ({ ...day, enabled: false, time: '', manuallySet: false })))
                                                }
                                            }} label={'Multiple Days'} />
                                        </div>
                                        {isMultiDay && (
                                            <>
                                                <div className="allocationDateField">
                                                    <MyCalendar showDateOnly noMaxDate minDate={date || new Date()} disabled={false} startDate={endDate} setStartDate={setEndDate} label={'End Date'} />
                                                </div>
                                                <div className="allocationWeeklyCard">
                                                    <div className="allocationWeeklyCardTitle">Weekly Start Times</div>
                                                    <div className="allocationWeekdaySchedule allocationWeekdayScheduleStacked">
                                                        {multiDaySchedule.map(day => (
                                                            <div key={`multi-day-${day.dayIndex}`} className={day.enabled ? 'allocationWeekdayCard allocationWeekdayCardActive' : 'allocationWeekdayCard'}>
                                                                <FieldLabel choice className='allocationWeekdayToggle'>
                                                                    <UiCheckbox
                                                                        type="checkbox"
                                                                        checked={day.enabled}
                                                                        onChange={() => toggleMultiDaySchedule(day.dayIndex)}
                                                                    />
                                                                    <span className='allocationWeekdayToggleLabel'>{day.label}</span>
                                                                </FieldLabel>
                                                                <div className="allocationWeekdayTimeField">
                                                                    <Dropdown
                                                                        options={timeOptions}
                                                                        disabled={!day.enabled}
                                                                        value={getDropdownValue(day.time, timeOptions)}
                                                                        setValue={(value) => setMultiDaySchedule(prev => prev.map(item => item.dayIndex === day.dayIndex ? { ...item, time: value?.value || '', manuallySet: true } : item))}
                                                                        label={''}
                                                                        placeholder={'Time'}
                                                                    />
                                                                    <ClockIcon className="allocationWeekdayClockIcon" style={{ width: 14, height: 14 }} />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Section>

                            <Section title={'Workers and Assets'} className='width-100' variant='card'>
                                <div className='allocationWorkersLayout'>
                                    <div className='allocationWorkersTopRow'>
                                        <SectionCard className='allocationWorkerPanel allocationWorkerPanelCard'>
                                            <Dropdown options={requiredWorkerOptions} value={requiredWorkers} setValue={setRequiredWorkers} label={'Required Workers'} />
                                        </SectionCard>
                                        <SectionCard className='allocationWorkerPanel allocationWorkerPanelCard'>
                                            <Dropdown
                                                isMulti
                                                options={workerDropdownOptions.map(option => ({ ...option, isDisabled: (selectedSite?.blockedWorkerIds || []).includes(option.value) }))}
                                                value={chosenWorkers}
                                                setValue={setChosenWorkers}
                                                label={'Workers'}
                                                placeholder={'Add worker...'}
                                                controlShouldRenderValue={false}
                                            />
                                            <div className='allocationSelectionBox'>
                                                {chosenWorkers.length === 0 ? (
                                                    <span className='allocationSelectionPlaceholder'>Add workers</span>
                                                ) : chosenWorkers.map(worker => (
                                                    <button
                                                        key={`selected-worker-${worker.value}`}
                                                        type='button'
                                                        className='allocationSelectionChip'
                                                        onClick={() => setChosenWorkers(prev => prev.filter(item => item.value !== worker.value))}
                                                    >
                                                        <span className='allocationSelectionChipLabel'>{worker.label}</span>
                                                        <span className='allocationSelectionChipRemove' aria-hidden='true'>&times;</span>
                                                    </button>
                                                ))}
                                            </div>
                                            {!!date && (selectedWorkerLeaveWarnings.length > 0 || selectedWorkerAllocationWarnings.length > 0) && (
                                                <div className='allocationWarningBox'>
                                                    {selectedWorkerLeaveWarnings.map(warning => (
                                                        <div key={warning.id} style={{ marginBottom: '6px' }}>
                                                            Worker on leave: {formatName(warning.workerName)} is on leave from {format(new Date(warning.startDate), 'EEE, d MMM yyyy')} to {format(new Date(warning.endDate), 'EEE, d MMM yyyy')}.
                                                        </div>
                                                    ))}
                                                    {selectedWorkerAllocationWarnings.map(warning => (
                                                        <div key={warning.id} style={{ marginBottom: '6px' }}>
                                                            {formatName(warning.workerName)} is already allocated:<br />
                                                            {format(new Date(warning.date), 'EEE, d MMM yyyy')}<br />{formatTime(new Date(warning.date))}<br />
                                                            {warning.client} / {warning.site}.
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </SectionCard>
                                    </div>

                                    <div className='allocationWorkersBottomRow'>
                                        <SectionCard title={<>Previous Workers</>} className='allocationWorkerPanel allocationWorkerPanelCard allocationPreviousWorkersColumn'>

                                            <div className='allocationInfoPanel allocationScrollablePanel allocationPreviousWorkersPanel'>
                                                <div className='allocationPreviousWorkersTableHeader'>
                                                    <span><UsersIcon />Worker</span>
                                                    <span><CalendarIcon />Last-used Day</span>
                                                </div>
                                                {!chosenSite?.value ? (
                                                    <div className='allocationEmptyState'>Choose a site to view previous workers.</div>
                                                ) : previousSiteWorkers.length === 0 ? (
                                                    <div className='allocationEmptyState'>No previous workers for this site.</div>
                                                ) : previousSiteWorkers.map(worker => (
                                                    <button
                                                        key={`previous-site-worker-${worker.id}`}
                                                        type="button"
                                                        className='textButton allocationPreviousWorkerRow'
                                                        onClick={() => addPreviousWorkerToSelection(worker)}
                                                    >
                                                        <span className='allocationPreviousWorkerName'>{formatName(worker.name)}</span>
                                                        <div className='allocationPreviousWorkerDate'>
                                                            {worker.lastUsedAt ? format(new Date(worker.lastUsedAt), 'EEE, d MMM yyyy') : '-'}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                            <div className='allocationPreviousWorkersSmsActionRow'>
                                                <Button variant="secondary" size="md"
                                                    type='button'
                                                    className='allocationPreviousWorkersSmsButton'
                                                    aria-label='Message Previous Workers'
                                                    title='Message Previous Workers'
                                                    disabled={previousSiteWorkers.length === 0}
                                                    onClick={() => {
                                                        setSelectedPreviousWorkerIds([])
                                                        setPreviousWorkersSmsMessage(
                                                            `Client name: ${chosenClient?.label || ''}\n` +
                                                            `Site address: ${selectedSite?.address || ''}\n` +
                                                            `Start time: ${date ? formatTime(new Date(date)) : ''}`
                                                        )
                                                        setShowPreviousWorkersSms(true)
                                                    }}
                                                >
                                                    <SmsIcon />Previous Workers
                                                </Button>
                                            </div>
                                        </SectionCard>

                                        {chosenWorkers.length > 0 && (
                                            <SectionCard title={<>Allocated Workers</>} className='allocationWorkerPanel allocationWorkerPanelCard allocationAllocatedWorkersColumn'>

                                                <div className='allocationInfoPanel allocationAllocatedWorkersPanel'>
                                                    {chosenWorkers.map(worker => {
                                                        const workerInfo = workers.find(item => item.id === worker.value)
                                                        const workerTypeValues = Array.isArray(workerInfo?.workerType) ? workerInfo.workerType : []
                                                        const assignment = chosenWorkerAssignments.find(item => item.workerId === worker.value)
                                                        return (
                                                            <div key={`capacity-${worker.value}`} className='allocationAllocatedWorkerRow'>
                                                                <div className='allocationAllocatedWorkerName'>{worker.label}</div>
                                                                <div className='allocationAllocatedWorkerCapacity'>
                                                                    <Dropdown
                                                                        options={workerTypeValues.map(type => ({ label: type, value: type }))}
                                                                        value={getDropdownValue(assignment?.capacity || getDefaultWorkerCapacity(workerInfo), workerTypeValues.map(type => ({ label: type, value: type })))}
                                                                        setValue={value => setChosenWorkerAssignments(prev => prev.map(item => item.workerId === worker.value ? { ...item, capacity: value?.value || '' } : item))}
                                                                        placeholder={workerTypeValues.length > 1 ? 'Select capacity' : ''}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </SectionCard>
                                        )}

                                        {chosenWorkers.length > 0 && (
                                            <SectionCard className='allocationWorkerPanel allocationWorkerPanelCard allocationTeamLeadersColumn'>
                                                <Dropdown
                                                    isMulti
                                                    options={chosenWorkers}
                                                    value={teamLeaders}
                                                    setValue={setTeamLeaders}
                                                    label={'Team Leaders'}
                                                    placeholder={'Assign a team leader'}
                                                />
                                            </SectionCard>
                                        )}
                                    </div>

                                    <div className='allocationAssetsSection'>
                                        <div className='allocationAssetHeader'>
                                            <div className='allocationAssetsHeadingRow'>
                                                <div className='allocationAssetOptionsGroup'>
                                                    <FieldLabel className='allocationSubPanelLabel'>Assets</FieldLabel>
                                                    <div className='allocationAssetChecks'>
                                                        <Checkbox value={assetRequired} setValue={setAssetRequired} label={'Asset required'} />
                                                        <Checkbox value={signsDroppedToSite} setValue={setSignsDroppedToSite} label={'Equipment dropped to site'} />
                                                        <Checkbox value={signsOnsiteAlready} setValue={setSignsOnsiteAlready} label={'Equipment onsite already'} />
                                                    </div>
                                                </div>
                                                <Button type='button' variant="create" size="md" className='allocationAssetAddButton' onClick={() => { setPickAsset(''); setPickWorker(''); setShowAddAsset(true) }}>
                                                    <PlusIcon />
                                                    Asset
                                                </Button>
                                            </div>
                                        </div>

                                        {chosenAssets.length > 0 ? (
                                            <Table emptyItems="assets" className='allocationInlineTable'>
                                                <tbody>
                                                    <TableRow>
                                                        <TableHeaderCell>Asset</TableHeaderCell>
                                                        <TableHeaderCell>Worker</TableHeaderCell>
                                                        <TableHeaderCell>Action</TableHeaderCell>
                                                    </TableRow>
                                                    {
                                                        chosenAssets.map((asset, index) => <TableRow key={`asset-${index}`}>
                                                            <TableCell>{index + 1}. {getAssetPickerLabel(assets.find(item => item.id === asset.asset)) || getDropdownLabel(asset.asset, assets.map(r => ({ label: r.machineId, value: r.id })))}</TableCell>
                                                            <TableCell>{!asset.worker ? '' : getDropdownLabel(asset.worker, workers.map(r => ({ label: formatName(formatName(r.firstName) + ' ' + formatName(r.lastName)), value: r.id })))}</TableCell>
                                                            <TableCell>
                                                                <Button data-action-tone="delete" variant="danger" size="icon"
                                                                    type='button'
                                                                    className='recordActionIcon allocationInlineAssetDeleteButton'
                                                                    title='Delete asset'
                                                                    aria-label='Delete asset'
                                                                    onClick={() => setConfirmMessage({
                                                                        title: 'Remove asset?',
                                                                        message: 'Are you sure you want to remove this asset from the allocation?',
                                                                        confirmLabel: 'Remove',
                                                                        action: () => {
                                                                            setChosenAssets(current => current.filter((r, idx) => idx !== index))
                                                                            showSuccess('Asset removed from the allocation')
                                                                        },
                                                                    })}
                                                                >
                                                                    <TrashIcon />
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>)
                                                    }
                                                </tbody>
                                            </Table>
                                        ) : null}
                                    </div>
                                </div>
                            </Section>

                            <ModalComponent
                                showModal={showAddAsset}
                                setShowModal={setShowAddAsset}
                                customMaxWidth='760px'
                                customContentStyle={{ padding: 0, width: '92%', overflowY: 'hidden' }}
                            >
                                <div className='allocationAssetPickerModal'>
                                    <div className='allocationAssetPickerHeader'>
                                        <h2 className='modalTitle'>Add Asset</h2>
                                        <p className='modalSubtitle'>Select one asset and one worker to assign it to.</p>
                                    </div>

                                    <div className='allocationAssetPickerBody'>
                                        <div className='allocationAssetPickerGrid'>
                                            <section className='allocationAssetPickerPanel'>
                                                <div className='allocationAssetPickerPanelHeader'>
                                                    <span>Assets</span>
                                                    <span className={pickAsset ? 'allocationAssetPickerCheck allocationAssetPickerCheckActive' : 'allocationAssetPickerCheck'} aria-hidden='true'>✓</span>
                                                </div>
                                                <div className='allocationAssetPickerList' role='radiogroup' aria-label='Assets'>
                                                    {availablePickerAssets.map(asset => {
                                                        const label = getAssetPickerLabel(asset)
                                                        const selected = pickAsset?.value === asset.id
                                                        return (
                                                            <FieldLabel choice key={asset.id} className={selected ? 'allocationAssetPickerOption allocationAssetPickerOptionSelected' : 'allocationAssetPickerOption'}>
                                                                <UiCheckbox
                                                                    type='radio'
                                                                    name='allocation-asset'
                                                                    checked={selected}
                                                                    onChange={() => setPickAsset({ label, value: asset.id })}
                                                                />
                                                                <span>{label}</span>
                                                            </FieldLabel>
                                                        )
                                                    })}
                                                    {availablePickerAssets.length === 0 && <div className='allocationAssetPickerEmpty'>No unassigned assets available.</div>}
                                                </div>
                                            </section>

                                            <section className='allocationAssetPickerPanel'>
                                                <div className='allocationAssetPickerPanelHeader'>
                                                    <span>Workers</span>
                                                    <span className={pickWorker ? 'allocationAssetPickerCheck allocationAssetPickerCheckActive' : 'allocationAssetPickerCheck'} aria-hidden='true'>✓</span>
                                                </div>
                                                <div className='allocationAssetPickerList' role='radiogroup' aria-label='Workers'>
                                                    {availablePickerWorkers.map(worker => {
                                                        const workerName = formatName(`${formatName(worker.firstName) || ''} ${formatName(worker.lastName) || ''}`.trim())
                                                        const selected = pickWorker?.value === worker.id
                                                        return (
                                                            <FieldLabel choice key={worker.id} className={selected ? 'allocationAssetPickerOption allocationAssetPickerOptionSelected' : 'allocationAssetPickerOption'}>
                                                                <UiCheckbox
                                                                    type='radio'
                                                                    name='allocation-asset-worker'
                                                                    checked={selected}
                                                                    onChange={() => setPickWorker({ label: workerName, value: worker.id })}
                                                                />
                                                                <span>{formatName(workerName)}</span>
                                                            </FieldLabel>
                                                        )
                                                    })}
                                                    {availablePickerWorkers.length === 0 && <div className='allocationAssetPickerEmpty'>Select workers in the allocation first.</div>}
                                                </div>
                                            </section>
                                        </div>
                                    </div>

                                    <div className='allocationAssetPickerFooter'>
                                        <div className='allocationAssetPickerSummary'>
                                            {selectedPickerAsset && selectedPickerWorker
                                                ? `${getAssetPickerLabel(selectedPickerAsset)} → ${selectedPickerWorkerName}`
                                                : 'Select an asset and a worker.'}
                                        </div>
                                        <div className='allocationAssetPickerActions'>
                                            <Button type='button' variant="secondary" size="md" className='secondaryButton' onClick={() => setShowAddAsset(false)}>Cancel</Button>
                                            <Button type='button' variant="create" size="md" className='allocationInlineSaveButton' disabled={!pickAsset || !pickWorker} onClick={handleAddAsset}>
                                                <PlusIcon />
                                                Add Asset
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </ModalComponent>

                            <Section title={'Details'} className='width-100' variant='card'>
                                <ScopeOfWorks
                                    disabled={false}
                                    value={scopeOfWorks}
                                    setValue={setScopeOfWorks}
                                    commitOnBlurOnly
                                    onDraftChange={value => { scopeOfWorksDraftRef.current = value }}
                                    placeholder={'Scope of works'}
                                    label={'Scope of Works'}
                                    required
                                    hideFieldLabel
                                    quickPhrases={scopePhrases}
                                    allowManage
                                    onAddPhrase={handleAddScopePhrase}
                                    onRemovePhrase={handleRemoveScopePhrase}
                                    onUpdatePhrase={handleUpdateScopePhrase}
                                    sideSlot={
                                        <div className='allocationAdminComments'>
                                            <FieldLabel className='scopeFieldLabel scopeSectionLabel' htmlFor='allocation-admin-comments'>Admin Comments</FieldLabel>
                                            <DraftTextarea key={adminNotesSeed.revision} id='allocation-admin-comments' className='scopeTextarea' placeholder='These comments can only be viewed by admin team' initialValue={adminNotesSeed.value} draftRef={adminNotesDraftRef} />
                                        </div>
                                    }
                                />
                                {/* <MyDropzone left key={document} disabled={false} storageLocation={`AllocationDocuments/${randomString()}`} label={'Document'} customMessage={'Click here/Drop files here to upload'} editing={true} errors={[]} allowPdf name={'document'} image={document} updateImage={(doc) => setDocument(doc)} /> */}
                                <div className='allocationDetailsDocumentsDivider' />
                                <div className='allocationDocumentGrid'>
                                    <div>
                                        <FieldLabel className='allocationInfoSectionLabel'>Site Documents</FieldLabel>
                                        <div className='allocationInfoPanel'>
                                            {chosenSite?.value ? (
                                                siteDocuments.length > 0 ? (
                                                    <Table emptyItems="documents">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableHeaderCell>Select</TableHeaderCell>
                                                                <TableHeaderCell>Document Name</TableHeaderCell>
                                                                <TableHeaderCell>Type/Note</TableHeaderCell>
                                                                <TableHeaderCell>Date</TableHeaderCell>
                                                                <TableHeaderCell>Open</TableHeaderCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <tbody>
                                                            {siteDocuments.map((row, index) => (
                                                                <TableRow key={`site-document-${index}`}>
                                                                    <TableCell>
                                                                        <UiCheckbox
                                                                            type="checkbox"
                                                                            checked={selectedSiteDocumentUrls.includes(row.picture?.url)}
                                                                            onChange={() => {
                                                                                const url = row.picture?.url
                                                                                if (!url) return
                                                                                setSelectedSiteDocumentUrls(prev => prev.includes(url) ? prev.filter(item => item !== url) : [...prev, url])
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>{getDocumentDisplayName(row)}</TableCell>
                                                                    <TableCell>{row.note || '-'}</TableCell>
                                                                    <TableCell>{row.date ? format(new Date(row.date), 'EEE, d MMM yyyy') : '-'}</TableCell>
                                                                    {/* picture can be absent (the checkbox above guards for it), and an
                                                                        unguarded row.picture.url threw and took the whole page down. */}
                                                                    <TableCell>{getAttachmentUrl(row)
                                                                        ? <Button variant="secondary" size="sm"
                                                                            type='button'
                                                                            className='allocationDocumentViewAction'
                                                                            onClick={() => openPreviewDocumentModal(row, getDocumentDisplayName(row))}
                                                                        >
                                                                            Open
                                                                        </Button>
                                                                        : '-'}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                ) : (
                                                    <Table><tbody><TableEmpty items="allocations" /></tbody></Table>
                                                )
                                            ) : (
                                                <p className='allocationInfoEmptyText'>Select a site to view site documents.</p>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <MyDropzoneMulti
                                            allowPdf
                                            hidePreviews
                                            setUploading={setUploadingDocument}
                                            storageLocation={`AllocationDocuments/${randomString()}`}
                                            label={'Upload Document'}
                                            customMessage={'Click here/Drop files here to upload'}
                                            editing={true}
                                            errors={[]}
                                            name={'document'}
                                            images={document || []}
                                            updateImages={(doc) => setDocument(doc)}
                                        />
                                        <FieldLabel className='allocationInfoSectionLabel'>Uploaded Documents</FieldLabel>
                                        {uploadedAllocationDocuments.length > 0 ? (
                                            <div className='allocationInfoPanel allocationUploadedDocumentsPanel'>
                                                <Table emptyItems="documents" className='allocationUploadedDocumentsTable'>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableHeaderCell>Document Name</TableHeaderCell>
                                                            <TableHeaderCell>Actions</TableHeaderCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <tbody>
                                                        {uploadedAllocationDocuments.map((row, index) => {
                                                            return (
                                                                <TableRow key={`uploaded-allocation-document-${index}`}>
                                                                    <TableCell>{getDocumentDisplayName(row)}</TableCell>
                                                                    <TableCell>
                                                                        <div className='allocationDocumentActions'>
                                                                            {getAttachmentUrl(row) ? (
                                                                                <Button variant="secondary" size="sm"
                                                                                    type='button'
                                                                                    className='allocationDocumentViewAction'
                                                                                    onClick={() => openPreviewDocumentModal(row, getDocumentDisplayName(row))}
                                                                                >
                                                                                    View
                                                                                </Button>
                                                                            ) : <span>-</span>}
                                                                            <Button data-action-tone="delete" variant="danger" size="sm"
                                                                                type='button'
                                                                                className='textButton textButtonDanger'
                                                                                onClick={() => setConfirmMessage({
                                                                                    message: `Delete "${getDocumentDisplayName(row)}" from this allocation?`,
                                                                                    action: () => setDocument(previous => normalizeDocumentArray(previous).filter(item => item !== row)),
                                                                                })}
                                                                            >
                                                                                Delete
                                                                            </Button>
                                                                        </div>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <Table><tbody><TableEmpty items="documents" /></tbody></Table>
                                        )}
                                    </div>
                                </div>
                            </Section>

                            {hasActiveEditRecord && (
                                <Section title={'Activity Log'} className='width-100' variant='card'>
                                    <div className='allocationActivityGrid'>
                                        <div>
                                            <h4 className='allocationActivityHeading'>Allocation Activity</h4>
                                            {activityLogs.length === 0 ? (
                                                <p className='allocationInfoEmptyText'>No activity recorded.</p>
                                            ) : (
                                                <Table emptyItems="activity entries">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableHeaderCell>Date/Time</TableHeaderCell>
                                                            <TableHeaderCell>Action</TableHeaderCell>
                                                            <TableHeaderCell>User</TableHeaderCell>
                                                            <TableHeaderCell>Details</TableHeaderCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <tbody>
                                                        {activityLogs.map(log => (
                                                            <TableRow key={log.id}>
                                                                <TableCell>{formatSmsLogDate(log.createdAt)}</TableCell>
                                                                    <TableCell>{getActivityActionLabel(log.type)}</TableCell>
                                                                <TableCell>{formatName(log.userName) || '-'}</TableCell>
                                                                <TableCell className='allocationPreWrapCell'>
                                                                    {log.message || '-'}
                                                                    {log.extraNumbers && log.extraNumbers.length > 0 ? ` (Extra: ${log.extraNumbers.join(', ')})` : ''}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className='allocationActivityHeading'>Message History</h4>
                                            {smsLogs.length === 0 ? (
                                                <p className='allocationInfoEmptyText'>No messages sent.</p>
                                            ) : (
                                                <Table emptyItems="messages">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableHeaderCell>Date/Time</TableHeaderCell>
                                                            <TableHeaderCell>Sent By</TableHeaderCell>
                                                            <TableHeaderCell>Message</TableHeaderCell>
                                                            <TableHeaderCell>Extra Numbers</TableHeaderCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <tbody>
                                                        {smsLogs.map(log => (
                                                            <TableRow key={log.id}>
                                                                <TableCell>{formatSmsLogDate(log.sentAt)}</TableCell>
                                                                <TableCell>{formatName(log.sentByName) || '-'}</TableCell>
                                                                <TableCell className='allocationPreWrapCell'>{log.message}</TableCell>
                                                                <TableCell>{Array.isArray(log.extraNumbers) && log.extraNumbers.length > 0 ? log.extraNumbers.join(', ') : '-'}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            )}
                                        </div>
                                    </div>
                                </Section>
                            )}

                            <div className='allocationActionBar allocationNewModalFooter'>
                            <div>
                                {hasActiveEditRecord && (
                                    <Button data-action-tone="delete" variant="danger" size="md"
                                        className='allocationDangerButton'
                                        disabled={loading}
                                        onClick={() => openDeleteAllocationPrompt(
                                            activeEditId || activeEditIncompleteId,
                                            activeEditId ? 'allocation' : 'incomplete'
                                        )}
                                    >
                                        Delete
                                    </Button>
                                )}
                            </div>
                                <div />
                            </div>
                        </div>
                    </div>

                </> : <>


                    <div className='pageBody'>
                        <div className={activeOverviewSection === 'allocations' ? 'allocationOverviewLayout' : 'allocationOverviewLayout allocationOverviewLayoutFull'}>
                            <div style={{ minWidth: 0 }}>
                                {activeOverviewSection === 'allocations' && (
                                    <Section
                                        title={'Allocations'}
                                        className='width-100 allocationTabSection'
                                        variant='card'
                                    >
                                        <div className='allocationViewControls allocationViewControlsStacked'>
                                            <div className='allocationViewTopRow'>
                                                <div className='allocationSummaryRow allocationSummaryFeatureBox'>
                                                    <div className='allocationSummaryBadge'><AllocationCalendarIcon /></div>
                                                    <div>
                                                        <div className={allocationViewTab === 'custom' ? 'allocationSummaryTitle allocationSummaryTitleCustom' : 'allocationSummaryTitle'}>
                                                            {allocationViewTab === 'today' ? 'Today' : allocationViewTab === 'next' ? 'Next Day' : allocationViewTab === 'custom' ? 'Custom Search' : 'This Week'}
                                                            {allocationViewTab !== 'custom' && <span className='allocationSummaryDate'>{rangeLabel}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='allocationToggleDateGroup'>
                                                    <div className='allocationSegment'>
                                                    {[
                                                        { key: 'today', label: 'Today' },
                                                        { key: 'next', label: 'Next Day' },
                                                        { key: 'week', label: '1 Week' },
                                                        { key: 'custom', label: 'Custom' },
                                                    ].map(tab => (
                                                        <button
                                                            key={tab.key}
                                                            className={allocationViewTab === tab.key ? 'allocationSegmentButton allocationSegmentButtonActive' : 'allocationSegmentButton'}
                                                            onClick={() => {
                                                                setAllocationViewTab(tab.key)
                                                                if (tab.key !== 'custom') setCustomAllocationDate('')
                                                            }}
                                                        >
                                                            <span className='allocationSegmentIcon'><AllocationViewIcon type={tab.key} /></span>
                                                            <span>{tab.label}</span>
                                                        </button>
                                                    ))}
                                                    </div>

                                                <div className='allocationOverviewViewControl allocationOverviewViewControlInline'>
                                                        <FieldLabel htmlFor='allocation-view-current'>View:</FieldLabel>
                                                        <UiSelect id='allocation-view-current' value={activeOverviewSection} onChange={event => setActiveOverviewSection(event.target.value)}>
                                                            <option value='allocations'>Allocations</option>
                                                            <option value='incomplete'>Incomplete</option>
                                                            <option value='future'>Future</option>
                                                            <option value='past'>Past</option>
                                                            <option value='cancelled'>Cancelled</option>
                                                        </UiSelect>
                                                    </div>
</div>

                                            </div>
                                            {allocationViewTab === 'custom' && (
                                                <div className='allocationCustomDateField allocationCustomSearchDateField'>
                                                    <MyCalendar
                                                        showDateOnly
                                                        noMaxDate
                                                        dateFormat='EEE, d MMM yyyy'
                                                        disabled={false}
                                                        startDate={customAllocationDate}
                                                        setStartDate={setCustomAllocationDate}
                                                        label={''}
                                                    />
                                                </div>
                                            )}
                                            <div className='allocationSearchRow allocationSearchRowBottom'>
                                                <div className='allocationSearchFieldWithIcon'>

                                                    <SearchInput disabled={false} value={weekAllocationSearch} onChange={event => (setWeekAllocationSearch)(event.target.value)} placeholder={'Search'} />
                                                </div>
                                            <Button variant="create" size="md" className='allocationNewButton allocationPageNewButton' onClick={() => { setIsAdding(true); setEditId(''); setEditIncompleteId(''); setFormType('allocation'); clearFields() }}>
                                                        <PlusIcon />
                                                        Allocation
                                                    </Button>
</div>
                                        </div>

                                        <div className='allocationCardsDivider' />
                                        {filteredViewAllocations.length > 0 ? (
                                            <div className='allocationCardGrid'>
                                                {filteredViewAllocations.map(allocation => {
                                                    const assignedWorkersCount = (allocation.chosenWorkers || []).length
                                                    const requiredWorkersCount = Number(allocation.requiredWorkers) || 0
                                                    const needsAttention = allocationNeedsAttention(allocation)
                                                    const isCompleted = isAllocationCompletedRecord(allocation)
                                                    const isPending = !!allocation.pending && !isCompleted
                                                    const showCompletedStyle = allocationViewTab === 'today' && isCompleted
                                                    const showStatusBadge = !isCompleted && (isPending || needsAttention)
                                                    const cardClassName = showCompletedStyle
                                                        ? 'allocationOverviewCard allocationOverviewCardCompleted'
                                                        : isPending
                                                            ? 'allocationOverviewCard allocationOverviewCardPending'
                                                            : needsAttention
                                                            ? 'allocationOverviewCard allocationOverviewCardNeedsAttention'
                                                            : 'allocationOverviewCard'
                                                    return <div key={allocation.id} className={cardClassName}>
                                                        <div className='allocationOverviewCardTopRow'>
                                                            <div className='allocationOverviewCardField'><strong>Allocation ID:</strong> {allocation.allocationId || allocation.id}</div>
                                                            {showCompletedStyle && <span className='allocationOverviewCompleteBadge' aria-label='Completed' title='Completed'><CheckIcon /></span>}
                                                        </div>
                                                        <div className='allocationOverviewCardField allocationOverviewCardIconField'><CalendarIcon title='Date' />{format(new Date(allocation.date), 'EEE, d MMM yyyy')}</div>
                                                        <div className='allocationOverviewCardField allocationOverviewCardIconField'><ClockIcon title='Time' />{formatTime(new Date(allocation.date))}</div>
                                                        <div className='allocationOverviewCardField allocationOverviewCardIconField'><Building2Icon title='Client' />{allocation.client}</div>
                                                        <div className='allocationOverviewCardField allocationOverviewCardIconField'><MapPinIcon title='Address' />{getAllocationCardLocationLabel(allocation) === null ? '—' : (formatAllocationCardAddress(getAllocationCardLocationLabel(allocation)) || '—')}</div>
                                                        <div className='allocationOverviewCardField allocationOverviewCardIconField'><UsersIcon title='Required Workers' />{requiredWorkersCount || '-'}</div>
                                                        <div className='allocationOverviewCardField allocationOverviewWorkersHeading'><strong>Workers:</strong></div>
                                                        <div className='allocationOverviewWorkers'>
{!(allocation.chosenWorkers || []).some(Boolean) && <span className='noWorkersAssigned'>No workers assigned</span>}
                                                            {Array.from({
                                                                length: Math.max(
                                                                    requiredWorkersCount,
                                                                    (allocation.chosenWorkers || []).length
                                                                )
                                                            }, (_, workerIndex) => {
                                                                const workerId = (allocation.chosenWorkers || [])[workerIndex]
                                                                if (!workerId) return null
                                                                const accepted = acceptedShiftStatusMap.get(`${allocation.id}-${workerId}`)
                                                                return (
                                                                    <div
                                                                        key={workerId}
                                                                        className={accepted ? 'allocationOverviewWorker' : 'allocationOverviewWorker allocationOverviewWorkerPending'}
                                                                        title={accepted ? undefined : 'Shift not yet accepted'}
                                                                    >
                                                                    {workerIndex + 1}. {getAllocationWorkerName(allocation, workerId)}
                                                                    </div>
                                                                )
                                                            })}
                                                            {!(requiredWorkersCount || (allocation.chosenWorkers || []).length) && <div className='allocationOverviewCardMuted'>—</div>}
                                                        </div>
                                                        {showStatusBadge && <span className={isPending ? 'dashboardIncompleteBadge dashboardPendingBadge' : 'dashboardIncompleteBadge'}>{isPending ? 'Pending' : 'Incomplete'}</span>}
                                                        <div className='allocationOverviewFooter'>
                                                            <Button data-action-tone="edit" variant="secondary" size="sm"  type="button" className="allocationOverviewTextButton allocationEditWhiteButton" onClick={() => editAllocation(allocation)}>Edit</Button>
                                                            <Button variant="secondary" size="sm" type="button" className="allocationOverviewPrimaryButton allocationOverviewViewButton" onClick={() => openAllocationView(allocation)}>View</Button>
                                                            <Button variant="secondary" size="sm" type="button" className="allocationOverviewPrimaryButton allocationOverviewSmsButton" disabled={loading || !!allocation.isIncomplete} title={allocation.isIncomplete ? 'Complete this allocation before messaging its workers' : undefined} onClick={() => setShowSendMessage(allocation)}>Message</Button>
                                                        </div>
                                                    </div>
                                                })}
                                            </div>
                                        ) : !initialLoading ? (
                                            <div className='allocationEmptyBlock'>No allocations scheduled for this period.</div>
                                        ) : null}

                                        {activeOverviewSection === 'future' && <div id='future-allocations' className='allocationOverviewFutureSection'>
                                            <div className='allocationGroupTitle'>Future Allocations</div>
                                            {filteredFutureAllocations.length > 0 ? (
                                                <div className='allocationTableWrap allocationOverviewFutureWrap'>
                                                    <Table emptyItems="allocations" className='workersDirectoryTable workerProfileTable allocationOverviewFutureTable'>
                                                        <colgroup>
                                                            <col className='allocationFutureDateColumn' />
                                                            <col className='allocationFutureIdColumn' />
                                                            <col className='allocationFutureClientColumn' />
                                                            <col className='allocationFutureSiteColumn' />
                                                            <col className='allocationFutureWorkersColumn' />
                                                            <col className='allocationFutureActionColumn' />
                                                        </colgroup>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableHeaderCell>Date/Time</TableHeaderCell>
                                                                <TableHeaderCell>Allocation ID</TableHeaderCell>
                                                                <TableHeaderCell>Client</TableHeaderCell>
                                                                <TableHeaderCell>Site</TableHeaderCell>
                                                                <TableHeaderCell>Workers</TableHeaderCell>
                                                                <TableHeaderCell>Action</TableHeaderCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <tbody>
                                                            {filteredFutureAllocations.map(allocation => (
                                                                <TableRow key={allocation.id} className={allocation.pending ? 'allocationRowPending' : allocation.showAsIncomplete ? 'allocationRowIncomplete' : ''}>
                                                                    <TableCell onClick={() => handleView(allocation)} className="hover">
                                                                        <span className='allocationDateLink allocationFutureDateLink'>
                                                                            <span>{format(new Date(allocation.date), 'EEE, d MMM yyyy')}</span>
                                                                            <span>{formatTime(new Date(allocation.date))}</span>
                                                                        </span>
                                                                    </TableCell>
                                                                    <TableCell>{allocation.allocationId}</TableCell>
                                                                    <TableCell>{allocation.client}</TableCell>
                                                                    <TableCell>{allocation.site}</TableCell>
                                                                    <TableCell>{allocation.worker || <span style={{ color: '#64748b' }}>—</span>}</TableCell>
                                                                    <TableCell className='allocationFutureActions'>
                                                                        <Button variant="secondary" size="icon" className="recordActionIcon allocationFutureActionIcon" disabled={loading} onClick={() => setShowContactAllocation(allocation)} aria-label='Contact worker' title='Contact'><PhoneIcon /></Button>
                                                                        <Button variant="secondary" size="icon" className="recordActionIcon allocationFutureActionIcon" disabled={loading || !!allocation.isIncomplete} onClick={() => setShowSendMessage(allocation)} aria-label='Message workers' title={allocation.isIncomplete ? 'Complete this allocation before messaging its workers' : 'Message'}><MessageSquareIcon /></Button>
                                                                        <Button data-action-tone="delete" variant="danger" size="icon"  className="recordActionIcon allocationFutureActionIcon allocationFutureDeleteIcon" disabled={loading} onClick={() => openDeleteAllocationPrompt(allocation.id)} aria-label='Delete allocation' title='Delete'><TrashIcon /></Button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                <Table><tbody><TableEmpty items="allocations" /></tbody></Table>
                                            )}
                                        </div>}
                                    </Section>
                                )}

                                {activeOverviewSection === 'future' && (
                                    <Section title={'Future Allocations'} className='width-100 allocationTabSection' variant='card'>
                                        <div className='allocationSectionViewRow'><div className='allocationOverviewViewControl'><FieldLabel htmlFor={`allocation-view-${activeOverviewSection}`}>View:</FieldLabel><UiSelect id={`allocation-view-${activeOverviewSection}`} value={activeOverviewSection} onChange={event => setActiveOverviewSection(event.target.value)}><option value='allocations'>Allocations</option><option value='incomplete'>Incomplete</option><option value='future'>Future</option><option value='past'>Past</option><option value='cancelled'>Cancelled</option></UiSelect></div></div>
                                        <div className='allocationFilterRow futureAllocationFilters futureAllocationPageFilters'>
                                            <div className='allocationFilterField'>
                                                <MyCalendar showDateOnly noMaxDate startDate={futurePageStartDate} setStartDate={setFuturePageStartDate} label='Start Date' placeholder='Start date' dateFormat='EEE, d MMM yyyy' />
                                            </div>
                                            <div className='allocationFilterField'>
                                                <MyCalendar showDateOnly noMaxDate startDate={futurePageEndDate} setStartDate={setFuturePageEndDate} label='End Date' placeholder='End date' dateFormat='EEE, d MMM yyyy' />
                                            </div>
                                            <Button variant="secondary" size="md" className='secondaryButton allocationFilterAction futureAllocationDateClearAction' onClick={() => { setFuturePageStartDate(''); setFuturePageEndDate('') }}>Clear Dates</Button>
                                            <div className='allocationFilterField futureAllocationSearchField allocationSearchFieldWithIcon'>

                                                <SearchInput disabled={false} value={futurePageSearch} onChange={event => (setFuturePageSearch)(event.target.value)} placeholder='Search' />
                                            </div>
                                        </div>
                                        <div className='allocationCardsDivider' />
                                        {filteredFuturePageAllocations.length > 0 ? (
                                            <div className='allocationTableWrap allocationOverviewFutureWrap'>
                                                <Table emptyItems="allocations" className='workersDirectoryTable workerProfileTable allocationOverviewFutureTable'>
                                                    <colgroup>
                                                        <col className='allocationFutureDateColumn' />
                                                        <col className='allocationFutureIdColumn' />
                                                        <col className='allocationFutureClientColumn' />
                                                        <col className='allocationFutureSiteColumn' />
                                                        <col className='allocationFutureWorkersColumn' />
                                                        <col className='allocationFutureActionColumn' />
                                                    </colgroup>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableHeaderCell>Date/Time</TableHeaderCell>
                                                            <TableHeaderCell>Allocation ID</TableHeaderCell>
                                                            <TableHeaderCell>Client</TableHeaderCell>
                                                            <TableHeaderCell>Site</TableHeaderCell>
                                                            <TableHeaderCell>Workers</TableHeaderCell>
                                                            <TableHeaderCell>Action</TableHeaderCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <tbody>
                                                        {paginatedFuturePageAllocations.map(allocation => (
                                                            <TableRow key={allocation.id} className={allocation.pending ? 'allocationRowPending' : allocation.showAsIncomplete ? 'allocationRowIncomplete' : ''}>
                                                                <TableCell onClick={() => handleView(allocation)} className='hover'>
                                                                    <span className='allocationDateLink allocationFutureDateLink'>
                                                                        <span>{format(new Date(allocation.date), 'EEE, d MMM yyyy')}</span>
                                                                        <span>{formatTime(new Date(allocation.date))}</span>
                                                                    </span>
                                                                </TableCell>
                                                                <TableCell>{allocation.allocationId}</TableCell>
                                                                <TableCell>{allocation.client}</TableCell>
                                                                <TableCell>{allocation.site}</TableCell>
                                                                <TableCell>{allocation.worker || <span style={{ color: '#64748b' }}>—</span>}</TableCell>
                                                                <TableCell className='allocationFutureActions'>
                                                                    <Button variant="secondary" size="icon" className='recordActionIcon allocationFutureActionIcon' disabled={loading} onClick={() => setShowContactAllocation(allocation)} aria-label='Contact worker' title='Contact'><PhoneIcon /></Button>
                                                                    <Button variant="secondary" size="icon" className='recordActionIcon allocationFutureActionIcon' disabled={loading || !!allocation.isIncomplete} onClick={() => setShowSendMessage(allocation)} aria-label='Message workers' title={allocation.isIncomplete ? 'Complete this allocation before messaging its workers' : 'Message'}><MessageSquareIcon /></Button>
                                                                    <Button data-action-tone="delete" variant="danger" size="icon"  className='recordActionIcon allocationFutureActionIcon allocationFutureDeleteIcon' disabled={loading} onClick={() => openDeleteAllocationPrompt(allocation.id)} aria-label='Delete allocation' title='Delete'><TrashIcon /></Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                            ) : (
                                                <Table><tbody><TableEmpty items="allocations" /></tbody></Table>
                                            )}
                                            {filteredFuturePageAllocations.length > 0 && (
                                                <div className='invoicePagination'>
                                                    <Button variant="secondary" size="md" type='button' disabled={safeFuturePage === 1} onClick={() => setFuturePage(page => Math.max(1, page - 1))}>Previous</Button>
                                                    <span>Page {safeFuturePage} of {futurePageCount}</span>
                                                    <Button variant="secondary" size="md" type='button' disabled={safeFuturePage === futurePageCount} onClick={() => setFuturePage(page => Math.min(futurePageCount, page + 1))}>Next</Button>
                                                </div>
                                            )}
                                    </Section>
                                )}

                                {activeOverviewSection === 'incomplete' && (
                                    <Section
                                        title={'Incomplete Allocations'}
                                        className='width-100 allocationTabSection'
                                        variant='card'
                                    >
                                        <div className='allocationSectionViewRow'><div className='allocationOverviewViewControl'><FieldLabel htmlFor={`allocation-view-${activeOverviewSection}`}>View:</FieldLabel><UiSelect id={`allocation-view-${activeOverviewSection}`} value={activeOverviewSection} onChange={event => setActiveOverviewSection(event.target.value)}><option value='allocations'>Allocations</option><option value='incomplete'>Incomplete</option><option value='future'>Future</option><option value='past'>Past</option><option value='cancelled'>Cancelled</option></UiSelect></div></div>
                                        <div className='allocationFilterRow futureAllocationFilters incompleteAllocationPageFilters'>
                                            <div className='allocationFilterField'>
                                                <MyCalendar showDateOnly noMaxDate startDate={incompleteFilterStartDate} setStartDate={setIncompleteFilterStartDate} label='Start Date' placeholder='Start date' dateFormat='EEE, d MMM yyyy' />
                                            </div>
                                            <div className='allocationFilterField'>
                                                <MyCalendar showDateOnly noMaxDate startDate={incompleteFilterEndDate} setStartDate={setIncompleteFilterEndDate} label='End Date' placeholder='End date' dateFormat='EEE, d MMM yyyy' />
                                            </div>
                                            <Button variant="secondary" size="md" className='secondaryButton allocationFilterAction futureAllocationDateClearAction' onClick={() => { setIncompleteFilterStartDate(''); setIncompleteFilterEndDate('') }}>Clear Dates</Button>
                                            <div className='allocationFilterField futureAllocationSearchField allocationSearchFieldWithIcon'>

                                                <SearchInput disabled={false} value={incompleteFilterSearch} onChange={event => (setIncompleteFilterSearch)(event.target.value)} placeholder='Search' />
                                            </div>
                                        </div>
                                        <div className='allocationCardsDivider' />
                                        <div className='allocationTableWrap allocationIncompleteTableWrap'>
                                        <Table emptyItems="allocations" className='workersDirectoryTable workerProfileTable allocationOverviewFutureTable allocationIncompleteTable'>
                                            <colgroup>
                                                <col className='allocationIncompleteDateColumn' />
                                                <col className='allocationIncompleteClientColumn' />
                                                <col className='allocationIncompleteSiteColumn' />
                                                <col className='allocationIncompleteWorkersColumn' />
                                                <col className='allocationIncompleteRequiredColumn' />
                                                <col className='allocationIncompleteActionColumn' />
                                            </colgroup>
                                            <TableHead>
                                                <TableRow>
                                                    <TableHeaderCell {...combinedIncompleteSort.getSortableHeaderProps('date')}>
                                                        Date/Time
                                                    </TableHeaderCell>
                                                    <TableHeaderCell {...combinedIncompleteSort.getSortableHeaderProps('client')}>
                                                        Client
                                                    </TableHeaderCell>
                                                    <TableHeaderCell {...combinedIncompleteSort.getSortableHeaderProps('site')}>
                                                        Site
                                                    </TableHeaderCell>
                                                    <TableHeaderCell {...combinedIncompleteSort.getSortableHeaderProps('worker')}>
                                                        Workers
                                                    </TableHeaderCell>
                                                    <TableHeaderCell>
                                                        Required Workers
                                                    </TableHeaderCell>
                                                    <TableHeaderCell>Action</TableHeaderCell>
                                                </TableRow>
                                            </TableHead>
                                            <tbody>
                                                {paginatedIncompleteAllocations.map(allocation => {
                                                    const isDerived = allocation.derivedIncomplete
                                                    const rowClassName = allocation.pending ? 'allocationRowPending' : (!isDerived ? 'allocationRowIncomplete' : '')
                                                    return (
                                                        <TableRow key={`${allocation.id}-${isDerived ? 'alloc' : 'incomplete'}`} className={rowClassName}>
                                                            <TableCell onClick={() => isDerived ? handleView(allocation) : handleViewIncomplete(allocation)} className="hover">
                                                                <span className='allocationDateLink allocationFutureDateLink'>
                                                                    <span>{format(new Date(allocation.date), 'EEE, d MMM yyyy')}</span>
                                                                    <span>{formatTime(new Date(allocation.date))}</span>
                                                                </span>
                                                            </TableCell>
                                                            <TableCell>{allocation.client}</TableCell>
                                                            <TableCell>{allocation.site}</TableCell>
                                                            <TableCell>{allocation.worker}</TableCell>
                                                            <TableCell>{allocation.requiredWorkers || ''}</TableCell>
                                                            <TableCell>
                                                                {/* <button className="textButton" disabled={loading} onClick={() => handleMarkIncompleteCompleted(allocation.id)}>Mark Completed</button> */}
                                                                <Button data-action-tone="delete" variant="danger" size="icon"
                                                                    className="recordActionIcon allocationIncompleteDeleteButton"
                                                                    disabled={loading}
                                                                    onClick={() => openDeleteAllocationPrompt(allocation.id, isDerived ? 'allocation' : 'incomplete')}
                                                                    aria-label='Delete incomplete allocation'
                                                                    title='Delete'
                                                                >
                                                                    <TrashIcon />
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                })}
                                            </tbody>
                                        </Table>
                                        </div>
                                        {filteredIncompleteAllocations.length > 0 && (
                                            <div className='invoicePagination'>
                                                <Button variant="secondary" size="md" type='button' disabled={safeIncompletePage === 1} onClick={() => setIncompletePage(page => Math.max(1, page - 1))}>Previous</Button>
                                                <span>Page {safeIncompletePage} of {incompletePageCount}</span>
                                                <Button variant="secondary" size="md" type='button' disabled={safeIncompletePage === incompletePageCount} onClick={() => setIncompletePage(page => Math.min(incompletePageCount, page + 1))}>Next</Button>
                                            </div>
                                        )}
                                    </Section>
                                )}

                                {activeOverviewSection === 'past' && (
                                    <Section
                                        title={'Past Allocations'}
                                        className='width-100 pastAllocationsSection allocationTabSection'
                                        variant='card'
                                    >
                                        <div className='allocationSectionViewRow'><div className='allocationOverviewViewControl'><FieldLabel htmlFor={`allocation-view-${activeOverviewSection}`}>View:</FieldLabel><UiSelect id={`allocation-view-${activeOverviewSection}`} value={activeOverviewSection} onChange={event => setActiveOverviewSection(event.target.value)}><option value='allocations'>Allocations</option><option value='incomplete'>Incomplete</option><option value='future'>Future</option><option value='past'>Past</option><option value='cancelled'>Cancelled</option></UiSelect></div></div>
                                        <div className='allocationFilterRow futureAllocationFilters incompleteAllocationPageFilters'>
                                            <div className='allocationFilterField'>
                                                <MyCalendar showDateOnly noMaxDate startDate={pastStartDate} setStartDate={setPastStartDate} label={'Start Date'} placeholder={'Start date'} dateFormat='EEE, d MMM yyyy' />
                                            </div>
                                            <div className='allocationFilterField'>
                                                <MyCalendar showDateOnly noMaxDate startDate={pastEndDate} setStartDate={setPastEndDate} label={'End Date'} placeholder={'End date'} dateFormat='EEE, d MMM yyyy' />
                                            </div>
                                            <Button variant="secondary" size="md" className='secondaryButton allocationFilterAction futureAllocationDateClearAction' onClick={() => { setPastStartDate(''); setPastEndDate(''); setSelectedAllocationIds([]) }}>Clear Dates</Button>
                                            <div className='allocationFilterField futureAllocationSearchField allocationSearchFieldWithIcon'>
                                                <SearchInput value={pastSearch} onChange={event => setPastSearch(event.target.value)} placeholder='Search' />
                                            </div>
                                        </div>
                                        <div className='allocationFilterRow pastAllocationExtraFilters'>
                                            <div className='allocationFilterField'>
                                                <Dropdown clearable options={clients.map(r => ({ label: r.name, value: r.id }))} value={pastFilterClient} setValue={(value) => { setPastFilterClient(value); setPastSite('') }} placeholder={'All clients'} aria-label='Filter by client' />
                                            </div>
                                            <div className='allocationFilterField'>
                                                <Dropdown
                                                    clearable
                                                    options={siteDropdownOptions.filter(option => !pastFilterClient || sites.find(site => site.id === option.value)?.clientId === pastFilterClient.value)}
                                                    value={pastSite}
                                                    setValue={setPastSite}
                                                    placeholder={'All sites'}
                                                    aria-label='Filter by site'
                                                />
                                            </div>
                                            <div className='allocationFilterField'>
                                                <Dropdown clearable options={workerDropdownOptions} value={pastFilterWorker} setValue={setPastFilterWorker} placeholder={'All workers'} aria-label='Filter by worker' />
                                            </div>

                                            <Button variant="secondary" size="md" className='allocationFilterAction pastAllocationDownloadAction' disabled={loading} onClick={handleDownloadPastRange}>Download</Button>
                                            <Button variant="secondary" size="md" className='secondaryButton allocationFilterAction pastAllocationClearFiltersAction' onClick={() => { setPastFilterClient(''); setPastFilterWorker(''); setPastSite(''); setPastSearch(''); setSelectedAllocationIds([]) }}>Clear Filters</Button>
                                        </div>

                                        <div className='allocationMonthRow'>
                                            <Button variant="secondary" size="md"
                                                className="secondaryButton pastAllocationDownloadSelectedAction"
                                                disabled={selectedAllocationIds.length === 0 || loading}
                                                onClick={handleDownloadSelected}
                                            >
                                                Download Selected{selectedAllocationIds.length > 0 ? ` (${selectedAllocationIds.length})` : ''}
                                            </Button>
                                        </div>

                                        <div className='allocationCardsDivider' />
                                        <div className='allocationTableWrap pastAllocationsTableWrap'>
                                        <Table emptyItems="allocations" className='workersDirectoryTable workerProfileTable pastAllocationsTable'>
                                            <colgroup>
                                                <col className='pastAllocationDateColumn' />
                                                <col className='pastAllocationIdColumn' />
                                                <col className='pastAllocationClientColumn' />
                                                <col className='pastAllocationSiteColumn' />
                                                <col className='pastAllocationWorkersColumn' />
                                                <col className='pastAllocationActionColumn' />
                                                <col className='pastAllocationSelectColumn' />
                                            </colgroup>
                                            <TableHead>
                                                <TableRow>
                                                    <TableHeaderCell {...pastAllocationSort.getSortableHeaderProps('date')}>
                                                        Date/Time
                                                    </TableHeaderCell>
                                                    <TableHeaderCell>
                                                        Allocation ID
                                                    </TableHeaderCell>
                                                    <TableHeaderCell {...pastAllocationSort.getSortableHeaderProps('client')}>
                                                        Client
                                                    </TableHeaderCell>
                                                    <TableHeaderCell {...pastAllocationSort.getSortableHeaderProps('site')}>
                                                        Site
                                                    </TableHeaderCell>
                                                    <TableHeaderCell {...pastAllocationSort.getSortableHeaderProps('worker')}>
                                                        Workers
                                                    </TableHeaderCell>
                                                    <TableHeaderCell>Action</TableHeaderCell>
                                                    <TableHeaderCell>
                                                        <FieldLabel choice className='pastAllocationSelectAll'>
                                                            <span>Select<br />All</span>
                                                            <UiCheckbox
                                                                type='checkbox'
                                                                checked={allPaginatedPastAllocationsSelected}
                                                                onChange={() => setSelectedAllocationIds(previous => (
                                                                    allPaginatedPastAllocationsSelected
                                                                        ? previous.filter(id => !paginatedPastAllocationIds.includes(id))
                                                                        : [...new Set([...previous, ...paginatedPastAllocationIds])]
                                                                ))}
                                                            />
                                                        </FieldLabel>
                                                    </TableHeaderCell>
                                                </TableRow>
                                            </TableHead>
                                            <tbody>
                                                {paginatedPastAllocations.map(allocation => <TableRow key={allocation.id}>
                                                    <TableCell className="hover">
                                                        <Link
                                                            to={`/allocations?allocationId=${allocation.id}`}
                                                            className='allocationDateLink pastAllocationDateLink'
                                                        >
                                                            <span>{format(new Date(allocation.date), 'EEE, d MMM yyyy')}</span>
                                                            <span>{formatTime(new Date(allocation.date))}</span>
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>{allocation.allocationId}</TableCell>
                                                    <TableCell>{allocation.client}</TableCell>
                                                    <TableCell>{allocation.site}</TableCell>
                                                    <TableCell>{allocation.worker}</TableCell>
                                                    <TableCell className='pastAllocationActions'>
                                                        <Button variant="secondary" size="icon" className="recordActionIcon pastAllocationActionIcon" disabled={loading} onClick={() => { startAllocationDownloadQueue([allocation]) }} aria-label='Download allocation' title='Download'><DownloadIcon /></Button>
                                                        {!userInfo?.isStaff && (
                                                            <Button data-action-tone="delete" variant="danger" size="icon"  className="recordActionIcon pastAllocationActionIcon pastAllocationDeleteIcon" disabled={loading} onClick={() => openDeleteAllocationPrompt(allocation.id)} aria-label='Delete allocation' title='Delete'><TrashIcon /></Button>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <UiCheckbox
                                                            type="checkbox"
                                                            checked={selectedAllocationIds.includes(allocation.id)}
                                                            onChange={() => toggleAllocationSelection(allocation.id)}
                                                        />
                                                    </TableCell>
                                                </TableRow>)}
                                            </tbody>
                                        </Table>
                                        </div>
                                        {filteredPastAllocations.length > 0 && (
                                            <div className='invoicePagination'>
                                                <Button variant="secondary" size="md" type='button' disabled={safePastPage === 1} onClick={() => setPastPage(page => Math.max(1, page - 1))}>Previous</Button>
                                                <span>Page {safePastPage} of {pastPageCount}</span>
                                                <Button variant="secondary" size="md" type='button' disabled={safePastPage === pastPageCount} onClick={() => setPastPage(page => Math.min(pastPageCount, page + 1))}>Next</Button>
                                            </div>
                                        )}
                                    </Section>
                                )}

                                {activeOverviewSection === 'cancelled' && (
                                    <Section title={'Cancelled Allocations'} className='width-100 allocationTabSection' variant='card'>
                                        <div className='allocationSectionViewRow'><div className='allocationOverviewViewControl'><FieldLabel htmlFor={`allocation-view-${activeOverviewSection}`}>View:</FieldLabel><UiSelect id={`allocation-view-${activeOverviewSection}`} value={activeOverviewSection} onChange={event => setActiveOverviewSection(event.target.value)}><option value='allocations'>Allocations</option><option value='incomplete'>Incomplete</option><option value='future'>Future</option><option value='past'>Past</option><option value='cancelled'>Cancelled</option></UiSelect></div></div>
                                        <div className='allocationFilterRow futureAllocationFilters incompleteAllocationPageFilters'>
                                            <div className='allocationFilterField'>
                                                <MyCalendar showDateOnly noMaxDate startDate={cancelledFilterStartDate} setStartDate={setCancelledFilterStartDate} label='Start Date' placeholder='Start date' dateFormat='EEE, d MMM yyyy' />
                                            </div>
                                            <div className='allocationFilterField'>
                                                <MyCalendar showDateOnly noMaxDate startDate={cancelledFilterEndDate} setStartDate={setCancelledFilterEndDate} label='End Date' placeholder='End date' dateFormat='EEE, d MMM yyyy' />
                                            </div>
                                            <Button variant="secondary" size="md" className='secondaryButton allocationFilterAction futureAllocationDateClearAction' onClick={() => { setCancelledFilterStartDate(''); setCancelledFilterEndDate('') }}>Clear Dates</Button>
                                            <div className='allocationFilterField futureAllocationSearchField allocationSearchFieldWithIcon'>

                                                <SearchInput disabled={false} value={cancelledAllocationSearch} onChange={event => (setCancelledAllocationSearch)(event.target.value)} placeholder='Search' />
                                            </div>
                                        </div>
                                        <div className='allocationCardsDivider' />
                                        {filteredCancelledAllocationRows.length > 0 ? (
                                            <div className='allocationTableWrap pastAllocationsTableWrap'>
                                                <Table emptyItems="allocations" className='workersDirectoryTable workerProfileTable pastAllocationsTable cancelledAllocationsTable'>
                                                    <colgroup>
                                                        <col className='cancelledAllocationDateColumn' />
                                                        <col className='cancelledAllocationIdColumn' />
                                                        <col className='cancelledAllocationClientColumn' />
                                                        <col className='cancelledAllocationSiteColumn' />
                                                        <col className='cancelledAllocationWorkersColumn' />
                                                        <col className='cancelledAllocationCommentColumn' />
                                                        <col className='cancelledAllocationStatusColumn' />
                                                    </colgroup>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableHeaderCell>Date/Time</TableHeaderCell>
                                                            <TableHeaderCell>Allocation ID</TableHeaderCell>
                                                            <TableHeaderCell>Client</TableHeaderCell>
                                                            <TableHeaderCell>Site</TableHeaderCell>
                                                            <TableHeaderCell>Workers</TableHeaderCell>
                                                            <TableHeaderCell>Comment</TableHeaderCell>
                                                            <TableHeaderCell>Cancelled</TableHeaderCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <tbody>
                                                        {paginatedCancelledAllocationRows.map(allocation => (
                                                            <TableRow key={allocation.id}>
                                                                <TableCell>
                                                                    {allocation.date ? (
                                                                        <span className='allocationDateLink allocationFutureDateLink'>
                                                                            <span>{format(new Date(allocation.date), 'EEE, d MMM yyyy')}</span>
                                                                            <span>{formatTime(new Date(allocation.date))}</span>
                                                                        </span>
                                                                    ) : '-'}
                                                                </TableCell>
                                                                <TableCell>{allocation.allocationId || '-'}</TableCell>
                                                                <TableCell>{allocation.client || '-'}</TableCell>
                                                                <TableCell>{allocation.site || '-'}</TableCell>
                                                                <TableCell>{allocation.worker || '-'}</TableCell>
                                                                <TableCell className='allocationPreWrapCell'>{allocation.cancellationComment || '-'}</TableCell>
                                                                <TableCell>{allocation.cancelledAt ? formatSmsLogDate(allocation.cancelledAt) : 'Cancelled'}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                            ) : (
                                                <Table><tbody><TableEmpty items="allocations" /></tbody></Table>
                                            )}
                                            {filteredCancelledAllocationRows.length > 0 && (
                                                <div className='invoicePagination'>
                                                    <Button variant="secondary" size="md" type='button' disabled={safeCancelledPage === 1} onClick={() => setCancelledPage(page => Math.max(1, page - 1))}>Previous</Button>
                                                    <span>Page {safeCancelledPage} of {cancelledPageCount}</span>
                                                    <Button variant="secondary" size="md" type='button' disabled={safeCancelledPage === cancelledPageCount} onClick={() => setCancelledPage(page => Math.min(cancelledPageCount, page + 1))}>Next</Button>
                                                </div>
                                            )}
                                    </Section>
                                )}
                            </div>

                            {activeOverviewSection === 'allocations' && (
                                <div className='allocationOverviewAside'>
                                    {allocationViewTab !== 'custom' && <div className='dashboardKpiCompactGrid'>
                                        <StatCard label={<>{kpiAllocationLabel}</>} value={<>{scheduledKpiCount}</>} tone="blue" sublabel={<>{kpiPeriodHint}</>} icon={<><MapPinIcon /></>} />
                                        <StatCard label={<>Workers Deployed</>} value={<>{deployedKpiCount}</>} tone="blue" sublabel={<>{kpiPeriodHint}</>} icon={<><UsersIcon /></>} />
                                        {allocationViewTab === 'today' && <StatCard label={<>Today's Hours</>} value={<>{selectedPeriodHours}</>} tone="blue" sublabel={<>Recorded today</>} icon={<><ClockIcon /></>} />}
                                    </div>}

                                    <SectionCard className='cardSurface' style={{ overflow: 'hidden' }} title={<>Worker Status</>}>

                                        <div className='workerAvailabilityBody'>
                                            <div className='workerAvailabilitySearchField'>

                                                <SearchInput disabled={false} value={availableWorkerSearch} onChange={event => (setAvailableWorkerSearch)(event.target.value)} placeholder={'Search workers or capacity'} />
                                            </div>
                                            <div className='workerAvailabilityFilters'>
                                                {availableWorkerTypeOptions.map(type => (
                                                    <FilterChip active={availableWorkerType === type}
                                                        key={type}
                                                        className={availableWorkerType === type ? 'workerAvailabilityFilter workerAvailabilityFilterActive' : 'workerAvailabilityFilter'}
                                                        onClick={() => setAvailableWorkerType(type)}
                                                    >
                                                        {type}
                                                    </FilterChip>
                                                ))}
                                            </div>
                                        </div>
                                        <div style={{ maxHeight: '640px', overflow: 'auto' }}>
                                        {availableWorkerType === 'On Leave' && (
                                            <div className='workerAvailabilityTableHeader'>
                                                <span>Worker</span>
                                                <span>Period</span>
                                            </div>
                                        )}
                                            <ul className='workerAvailabilityList'>
                                                {availableWorkerType === 'On Leave' ? (
                                                    filteredUpcomingWorkerLeaves.length > 0 ? filteredUpcomingWorkerLeaves.map(leave => (
                                                        <li key={leave.id} className='workerAvailabilityItem'>
                                                            <div className='workerAvailabilityInfo'>
                                                                <div className='workerAvailabilityName'>
                                                                    <span>{formatName(leave.workerName)}</span>
                                                                </div>
                                                            </div>
                                                            <div className='workerAvailabilityPeriod'>{formatLeavePeriod(leave.startDate, leave.endDate)}</div>
                                                        </li>
                                                    )) : <li className='workerAvailabilityItem'><div className='allocationEmptyState'>No upcoming leaves match this filter.</div></li>
                                                ) : (
                                                    filteredDisplayedAvailableWorkers.length > 0 ? filteredDisplayedAvailableWorkers.map(worker => (
                                                        <li key={worker.id} className='workerAvailabilityItem'>
                                                            <div className='workerAvailabilityInfo'>
                                                                <div className='workerAvailabilityName'>
                                                                    <span>{formatName(worker.firstName)} {formatName(worker.lastName)}</span>
                                                                </div>
                                                                {availableWorkerType === 'On Site' && <div className='workerAvailabilitySubtext' style={{ color: '#64748b' }}>Site: {getWorkerOnSiteNames(worker.id) || '-'}</div>}
                                                                {availableWorkerType === 'All' && <div className='workerAvailabilitySubtext' style={{ color: '#64748b' }}>{Array.isArray(worker.workerType) && worker.workerType.length > 0 ? worker.workerType.join(', ') : 'Available worker'}</div>}
                                                            </div>
                                                            <StatusBadge className='workerAvailabilityStatus'>

                                                                <span>{availableWorkerType === 'On Site' ? 'On Site' : 'Available'}</span>
                                                            </StatusBadge>
                                                        </li>
                                                    )) : <li className='workerAvailabilityItem'><div className='allocationEmptyState'>No workers match this filter.</div></li>
                                                )}
                                            </ul>
                                        </div>
                                    </SectionCard>
                                </div>
                            )}
                        </div>
                    </div>

                    {downloadAllocation && (
                        <AllocationPdfGenerator
                            allocation={downloadAllocation}
                            timesheets={downloadAllocationTimeSheets}
                            workers={workers}
                            clients={clients}
                            sites={sites}
                            assets={assets}
                            logo={activeOrgInfo?.logo?.url}
                            onComplete={downloadMode === 'multiple' ? advanceAllocationDownloadQueue : undefined}
                            onBuffer={downloadMode === 'combined' ? collectAndAdvanceAllocationDownload : undefined}
                            onError={handleAllocationDownloadError}
                        />
                    )}

                </>}

                <ModalComponent
                    showModal={downloadChoiceAllocations.length > 0}
                    setShowModal={(show) => { if (!show) setDownloadChoiceAllocations([]) }}
                    customMaxWidth="520px"
                >
                    <div className='allocationConfirmMessage'>
                        <h2 className='modalTitle'>Download selected allocations</h2>
                        <p className='modalSubtitle'>Choose one combined PDF in date order, or download each allocation as a separate PDF.</p>
                        <div className='modalFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => setDownloadChoiceAllocations([])}>Cancel</Button>
                            <Button variant="secondary" size="md" className='secondaryButton' onClick={() => {
                                const selected = downloadChoiceAllocations
                                setDownloadChoiceAllocations([])
                                startAllocationDownloadQueue(selected, 'multiple')
                            }}>Multiple PDFs</Button>
                            <Button variant="secondary" size="md" className='saveWithIcon' onClick={() => {
                                const selected = downloadChoiceAllocations
                                setDownloadChoiceAllocations([])
                                startAllocationDownloadQueue(selected, 'combined')
                            }}>Single Combined PDF</Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!viewAllocation}
                    setShowModal={setViewAllocation}
                    customMaxWidth="1100px"
                    customContentStyle={{ maxHeight: '94dvh', overflow: 'hidden', padding: 0, backgroundColor: '#f8fafc', borderRadius: '16px', width: 'calc(100vw - 32px)', minWidth: 0 }}
                    customOverlayStyle={{ backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)' }}
                >
                    {viewAllocation && (() => {
                        const documents = normalizeDocumentArray(viewAllocation.document)
                        const teamLeaderIds = (viewAllocation.teamLeaders || []).map(item => item?.value || item)
                        const assignmentMap = new Map((viewAllocation.chosenWorkerAssignments || []).map(item => [item.workerId, item.capacity]))
                        const hasMapMarker = !!viewAllocation.dropMapMarker
                            || !!String(viewAllocation.mapMarkerAddress || viewAllocation.mapMarkerName || '').trim()
                            || hasValidCoordinates(viewAllocation.mapMarkerCoordinates)
                        const locationLabel = getAllocationLocationLabel(viewAllocation)
                        const mapsLink = viewAllocation.mapMarkerLink || buildGoogleMapsLink(viewAllocation.mapMarkerCoordinates || [])
                        const selectedSite = sites.find(site => site.id === viewAllocation.chosenSite)
                        const siteSupervisorName = `${selectedSite?.managerFirstName || ''} ${selectedSite?.managerLastName || ''}`.trim() || '-'
                        const siteSupervisorContact = selectedSite?.managerContactNo || '-'
                        const siteSupervisorPhoneLink = siteSupervisorContact !== '-' ? `tel:${String(siteSupervisorContact).replace(/[^\d+]/g, '')}` : ''
                        const assignedWorkersCount = Array.isArray(viewAllocation.chosenWorkers) ? viewAllocation.chosenWorkers.length : 0
                        const requiredWorkersCount = Number(viewAllocation.requiredWorkers) || 0
                        const isCompleted = isAllocationCompletedRecord(viewAllocation)
                        const isPending = !!viewAllocation.pending && !isCompleted
                        const isIncomplete = allocationNeedsAttention(viewAllocation)
                        const allocationDateLabel = viewAllocation.date
                            ? format(new Date(viewAllocation.date), 'EEE, d MMM yyyy')
                            : '-'
                        const allocationTimeLabel = viewAllocation.date ? formatTime(new Date(viewAllocation.date)) : ''

                        return <div className='allocationDetailModal'>
                            <div className='allocationDetailHeader'>
                                <div className='allocationDetailHeadingBlock'>
                                    <h2 className='allocationDetailTitle'>Allocation Details</h2>
                                </div>
                                <div className='allocationDetailHeaderActions'>
                                    <Button data-action-tone="edit" variant="secondary" size="md"  type='button' className="allocationDetailFooterButton allocationDetailFooterButtonPrimary" onClick={() => editAllocation(viewAllocation)}>Edit</Button>
                                    <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className="secondaryButton allocationDetailFooterButton" onClick={() => setViewAllocation(null)}>Close</Button>
                                </div>
                            </div>

                            <div className='allocationDetailBody'>
                                <SectionCard className='allocationDetailCard' title={<>Job</>} actions={<><span className={`allocationDetailStatus ${isPending ? 'allocationDetailStatusPending' : isIncomplete ? 'allocationDetailStatusIncomplete' : 'allocationDetailStatusComplete'}`}>
                                    {isPending ? 'Pending' : isIncomplete ? 'Incomplete' : 'Complete'}
                                </span></>}>

                                    <div className={`allocationDetailJobGrid ${isMobileViewport ? 'allocationDetailJobGridSingle' : ''}`}>
                                        <div className='allocationDetailItem'><span className='allocationDetailLabel'>Allocation ID</span>{viewAllocation.allocationId || viewAllocation.id || '-'}</div>
                                        <div className='allocationDetailItem'>
                                            <span className='allocationDetailLabel'>Date/Time</span>
                                            <span>{allocationDateLabel}<br />{allocationTimeLabel}</span>
                                        </div>
                                        <div className='allocationDetailItem'><span className='allocationDetailLabel'>Client Name</span>{viewAllocation.client || '-'}</div>
                                        <div className='allocationDetailItem'><span className='allocationDetailLabel'>Site Name</span>{viewAllocation.site || '-'}</div>
                                        <div className='allocationDetailItem allocationDetailJobSiteAddress'>
                                            <span className='allocationDetailLabel'>Location Address</span>
                                            {mapsLink ? <a href={mapsLink} target="_blank" rel="noreferrer" className='allocationDetailLink'>{locationLabel || 'Open location'}</a> : (locationLabel || '-')}
                                        </div>
                                        <div className='allocationDetailItem allocationDetailJobMapMarker'>
                                            <span className='allocationDetailLabel'>Map Marker</span>
                                            {hasMapMarker ? (viewAllocation.mapMarkerName || 'Yes') : 'No'}
                                        </div>
                                        <div className='allocationDetailItem allocationDetailItemWide'>
                                            <span className='allocationDetailLabel'>Site Supervisor</span>
                                            <span className='allocationDetailSupervisorLine'>
                                                <span>{siteSupervisorName}</span>
                                                {siteSupervisorPhoneLink ? <a href={siteSupervisorPhoneLink} className='allocationDetailLink'>{siteSupervisorContact}</a> : <span>-</span>}
                                            </span>
                                        </div>
                                        <div className='allocationDetailItem allocationDetailItemWide'>
                                            <span className='allocationDetailLabel'>Required Workers</span>
                                            {requiredWorkersCount || '-'}
                                        </div>
                                    </div>
                                </SectionCard>

                                <SectionCard className='allocationDetailCard allocationDetailWorkersAssetsCard' title={<>Workers &amp; Assets</>}>

                                    <div className={`allocationDetailWorkersAssetsGrid ${isMobileViewport ? 'allocationDetailWorkersAssetsGridSingle' : ''}`}>
                                        <div className='allocationDetailWorkersColumn'>
                                            <h4 className='allocationDetailSubsectionTitle'>Worker</h4>
                                            {assignedWorkersCount > 0 ? (
                                                <div className='allocationDetailWorkerStack'>
                                                    {(viewAllocation.chosenWorkers || []).map(workerId => {
                                                        const worker = workers.find(item => item.id === workerId)
                                                        const workerName = formatName(worker ? `${formatName(worker.firstName) || ''} ${formatName(worker.lastName) || ''}`.trim() : workerId)
                                                        const isLeader = teamLeaderIds.includes(workerId)
                                                        return (
                                                            <div key={`view-worker-${workerId}`} className='allocationDetailWorkerPanel'>
                                                                <div className='allocationDetailWorkerPanelName'>
                                                                    {formatName(workerName)}
                                                                    {isLeader ? ' (TL)' : ''}
                                                                </div>
                                                                {worker?.contactNo && <a className='allocationDetailWorkerMobile' href={`tel:${worker.contactNo}`}>{formatPhone(worker.contactNo)}</a>}
                                                                {assignmentMap.get(workerId) && <div className='allocationDetailWorkerPanelSubtext'>{assignmentMap.get(workerId)}</div>}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            ) : (
                                                <p className='allocationDetailEmptyText'>No workers allocated</p>
                                            )}
                                            <div className='allocationDetailSectionMeta allocationDetailWorkerCount'>
                                                Workers: {assignedWorkersCount}/{requiredWorkersCount || assignedWorkersCount} Allocated
                                            </div>
                                        </div>

                                        <div className='allocationDetailAssetsColumn'>
                                            <h4 className='allocationDetailSubsectionTitle'>Assets</h4>
                                            {(viewAllocation.chosenAssets || []).length > 0 ? (
                                                <div className='allocationDetailAssetStack'>
                                                    {viewAllocation.chosenAssets.map((chosenAsset, index) => {
                                                        const asset = assets.find(item => item.id === chosenAsset.asset)
                                                        const assignedWorker = workers.find(item => item.id === chosenAsset.worker)
                                                        return (
                                                            <div key={`view-asset-${chosenAsset.asset}-${index}`} className='allocationDetailAssetPanel'>
                                                                <div className='allocationDetailAssetPanelTitle'>
                                                                    {assignedWorker ? formatName(`${`${formatName(assignedWorker.firstName) || ''} ${formatName(assignedWorker.lastName) || ''}`.trim()}`) : 'No worker assigned'}
                                                                </div>
                                                                <div className='allocationDetailAssetPanelText'>
                                                                    {getAssetPickerLabel(asset) || chosenAsset.asset || 'Asset'}
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            ) : (
                                                <p className='allocationDetailEmptyText'>No assets assigned</p>
                                            )}
                                            <div className='allocationDetailFlagRow'>
                                                <div className='allocationDetailFlagItem'>
                                                    <span className='allocationDetailLabel'>Signs Dropped</span>
                                                    {viewAllocation.signsDroppedToSite ? 'Yes' : 'No'}
                                                </div>
                                                <div className='allocationDetailFlagItem'>
                                                    <span className='allocationDetailLabel'>Signs Onsite Already</span>
                                                    {viewAllocation.signsOnsiteAlready ? 'Yes' : 'No'}
                                                </div>
                                                <div className='allocationDetailFlagItem'>
                                                    <span className='allocationDetailLabel'>Asset Required</span>
                                                    {viewAllocation.assetRequired ? 'Yes' : 'No'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </SectionCard>

                                <SectionCard className='allocationDetailCard' title={<>Details</>}>

                                    <div className={`allocationDetailNotesGrid ${isMobileViewport ? 'allocationDetailNotesGridSingle' : ''}`}>
                                        <div className='allocationDetailItem allocationDetailItemWide'>
                                            <span className='allocationDetailLabel'>Scope of Works</span>
                                            <div className='allocationPreWrapCell'>{viewAllocation.scopeOfWorks || '-'}</div>
                                        </div>
                                        <div className='allocationDetailItem'>
                                            <span className='allocationDetailLabel'>Admin Notes</span>
                                            <div className='allocationPreWrapCell'>{viewAllocation.adminNotes || viewAllocation.comments || '-'}</div>
                                        </div>
                                        <div className='allocationDetailItem'>
                                            <div className='allocationDetailDocumentHeaders'>
                                                <span className='allocationDetailLabel'>Documents</span>
                                                <span className='allocationDetailLabel'>Action</span>
                                            </div>
                                            {documents.length > 0 ? (
                                                <div className='allocationDetailDocumentList'>
                                                    {documents.map((item, index) => {
                                                        const url = getAttachmentUrl(item)
                                                        const label = item.name || getDocumentFileName(item)
                                                        return url ? (
                                                            <div key={`view-document-${index}`} className='allocationDetailDocumentRow'>
                                                                <span className='allocationDetailDocumentName'>{label}</span>
                                                                <Button variant="secondary" size="md"
                                                                    type='button'
                                                                    className='allocationDetailDocumentViewButton'
                                                                    onClick={() => openPreviewDocumentModal(item, label)}
                                                                >
                                                                    View
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <span key={`view-document-${index}`}>{label}</span>
                                                        )
                                                    })}
                                                </div>
                                            ) : <div className='allocationInfoEmptyText'>No documents attached</div>}
                                        </div>
                                    </div>
                                    <div className='allocationDetailFlagRow'>
                                        {viewAllocation.endDate && <div className='allocationDetailFlagItem'>
                                            <span className='allocationDetailLabel'>End Date</span>
                                            {format(new Date(viewAllocation.endDate), 'EEE, d MMM yyyy')}
                                        </div>}
                                    </div>
                                </SectionCard>

                                <SectionCard className='allocationDetailCard' title={<>Activity Log</>}>

                                    <div className='allocationActivityGrid'>
                                        <div>
                                            <h4 className='allocationActivityHeading'>Allocation Activity</h4>
                                            {activityLogs.length === 0 ? (
                                                <p className='allocationInfoEmptyText'>No activity recorded.</p>
                                            ) : (
                                                <Table emptyItems="activity entries">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableHeaderCell>Date/Time</TableHeaderCell>
                                                            <TableHeaderCell>Action</TableHeaderCell>
                                                            <TableHeaderCell>User</TableHeaderCell>
                                                            <TableHeaderCell>Details</TableHeaderCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <tbody>
                                                        {activityLogs.map(log => (
                                                            <TableRow key={`view-activity-${log.id}`}>
                                                                <TableCell>{formatSmsLogDate(log.createdAt)}</TableCell>
                                                                    <TableCell>{getActivityActionLabel(log.type)}</TableCell>
                                                                <TableCell>{formatName(log.userName) || '-'}</TableCell>
                                                                <TableCell className='allocationPreWrapCell'>
                                                                    {log.message || '-'}
                                                                    {log.extraNumbers && log.extraNumbers.length > 0 ? ` (Extra: ${log.extraNumbers.join(', ')})` : ''}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className='allocationActivityHeading'>Message History</h4>
                                            {smsLogs.length === 0 ? (
                                                <p className='allocationInfoEmptyText'>No messages sent.</p>
                                            ) : (
                                                <Table emptyItems="messages">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableHeaderCell>Date/Time</TableHeaderCell>
                                                            <TableHeaderCell>Sent By</TableHeaderCell>
                                                            <TableHeaderCell>Message</TableHeaderCell>
                                                            <TableHeaderCell>Extra Numbers</TableHeaderCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <tbody>
                                                        {smsLogs.map(log => (
                                                            <TableRow key={`view-sms-${log.id}`}>
                                                                <TableCell>{formatSmsLogDate(log.sentAt)}</TableCell>
                                                                <TableCell>{formatName(log.sentByName) || '-'}</TableCell>
                                                                <TableCell className='allocationPreWrapCell'>{log.message}</TableCell>
                                                                <TableCell>{Array.isArray(log.extraNumbers) && log.extraNumbers.length > 0 ? log.extraNumbers.join(', ') : '-'}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            )}
                                        </div>
                                    </div>
                                </SectionCard>
                            </div>


                        </div>
                    })()}
                </ModalComponent>

                <ModalComponent
                    showModal={showPreviousWorkersSms}
                    setShowModal={(open) => {
                        setShowPreviousWorkersSms(open)
                        if (!open) {
                            setPreviousWorkersSmsMessage('')
                            setSelectedPreviousWorkerIds([])
                        }
                    }}
                    customMaxWidth='460px'
                    customContentStyle={{ width: 'calc(100% - 32px)', minWidth: 0, padding: 0, overflow: 'hidden' }}>
                    <div className='sendSmsModal'>
                        <div className='sendSmsHeader'>
                            <div>
                                <h2 className='sendSmsTitle'>Message Previous Workers</h2>
                                {currentAllocationNumber && <div className='sendSmsAllocation'>Allocation ID: {currentAllocationNumber}</div>}
                                <div className='sendSmsAllocation'>Site: {chosenSite?.label || 'Selected site'}</div>
                            </div>
                        </div>
                        <div className='sendSmsBody'>
                            <p className='sendSmsChannelNote'>Workers get this as a notification in the managd app.</p>
                            <button
                                type='button'
                                className={previousAppWorkers.length > 0 && selectedPreviousWorkerIds.length === previousAppWorkers.length ? 'previousWorkersSmsPill previousWorkersSmsPillActive previousWorkersSmsSelectAll' : 'previousWorkersSmsPill previousWorkersSmsSelectAll'}
                                aria-pressed={previousAppWorkers.length > 0 && selectedPreviousWorkerIds.length === previousAppWorkers.length}
                                disabled={previousAppWorkers.length === 0}
                                onClick={() => setSelectedPreviousWorkerIds(
                                    previousAppWorkers.length > 0 && selectedPreviousWorkerIds.length === previousAppWorkers.length
                                        ? []
                                        : previousAppWorkers.map(worker => worker.id)
                                )}
                            >
                                Select All
                            </button>
                            <div className='previousWorkersSmsList'>
                                {previousSiteWorkers.map(worker => {
                                    const selected = selectedPreviousWorkerIds.includes(worker.id)
                                    return (
                                        <button
                                            key={worker.id}
                                            type='button'
                                            className={selected ? 'previousWorkersSmsPill previousWorkersSmsPillActive' : 'previousWorkersSmsPill'}
                                            aria-pressed={selected}
                                            disabled={!worker.inApp}
                                            title={worker.inApp ? undefined : "No managd app, so they won't receive this"}
                                            onClick={() => setSelectedPreviousWorkerIds(previous => selected
                                                ? previous.filter(id => id !== worker.id)
                                                : [...previous, worker.id])}
                                        >
                                            <span>{formatName(worker.name)}{worker.inApp ? '' : ' · No app'}</span>
                                        </button>
                                    )
                                })}
                            </div>
                            <FieldLabel className='sendSmsFieldLabel' htmlFor='previous-workers-sms-message'>Message</FieldLabel>
                            <Textarea
                                id='previous-workers-sms-message'
                                className='sendSmsMessageInput'
                                disabled={loading}
                                value={previousWorkersSmsMessage}
                                maxLength={MESSAGE_LIMIT}
                                onChange={event => setPreviousWorkersSmsMessage(event.target.value)}
                                placeholder='Type your message...'
                            />
                            <p className='sendSmsCounter' aria-live='polite'>{previousWorkersSmsMessage.length}/{MESSAGE_LIMIT}</p>
                        </div>
                        <div className='sendSmsFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton sendSmsFooterButton' onClick={() => { setShowPreviousWorkersSms(false); setPreviousWorkersSmsMessage(''); setSelectedPreviousWorkerIds([]) }}>Cancel</Button>
                            <Button variant="secondary" size="md" type='button' className='sendSmsFooterButton' disabled={loading || !previousWorkersSmsMessage.trim() || selectedPreviousWorkerIds.length === 0} onClick={sendPreviousWorkersSms}>
                                Send ({selectedPreviousWorkerIds.length})
                            </Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!showSendMessage}
                    setShowModal={closeSendMessageModal}
                    customMaxWidth='460px'
                    customContentStyle={{ width: 'calc(100% - 32px)', minWidth: 0, padding: 0, overflow: 'hidden' }}>
                    <div className='sendSmsModal'>
                        <div className='sendSmsHeader'>
                            <div>
                                <h2 className='sendSmsTitle'>Message Workers</h2>
                                <div className='sendSmsAllocation sendSmsAllocationDetails'>
                                    <div>Allocation ID: {showSendMessage?.allocationId || showSendMessage?.id || '-'}</div>
                                    <div>Client: {showSendMessage?.client || '-'}</div>
                                </div>
                            </div>
                        </div>

                        <div className='sendSmsBody'>
                            <p className='sendSmsChannelNote'>Workers get this as a notification in the managd app.</p>
                            <div className='sendSmsRecipients'>
                                {getAllocationContactRows(showSendMessage).map(worker => (
                                    <span key={worker.id} className='sendSmsRecipientChip'>
                                        {formatName(worker.name)} · {worker.inApp ? 'managd app' : worker.inApp === null ? 'archived or awaiting review: gets it if they have the app' : "No app, won't receive this"}
                                    </span>
                                ))}
                                {additionalSmsNumbers.map(number => (
                                    <span key={number} className='sendSmsRecipientChip sendSmsAdditionalChip'>
                                        {number}
                                        <Button variant="secondary" size="icon"
                                            type='button'
                                            aria-label={`Remove ${number}`}
                                            onClick={() => setAdditionalSmsNumbers(prev => prev.filter(item => item !== number))}>
                                            ×
                                        </Button>
                                    </span>
                                ))}
                            </div>

                            {showSendMessage && getAllocationContactRows(showSendMessage).length > 0 && !getAllocationContactRows(showSendMessage).some(worker => worker.inApp !== false) && <p role='status'>None of these workers have the managd app, so none of them will receive this.</p>}
                            <FieldLabel className='sendSmsFieldLabel' htmlFor='allocation-sms-message'>Message</FieldLabel>
                            <BufferedTextarea
                                id='allocation-sms-message'
                                className='sendSmsMessageInput'
                                disabled={loading}
                                value={message}
                                onCommit={setMessage}
                                onDraftChange={value => { messageDraftRef.current = value }}
                                maxLength={MESSAGE_LIMIT}
                                counter
                                placeholder='Type your message...'
                            />

                            <FieldLabel className='sendSmsFieldLabel' htmlFor='allocation-sms-contact'>Also text these numbers</FieldLabel>
                            <p className='sendSmsHelp'>For people without the app, such as a site supervisor. They get this as an SMS.</p>
                            <div className='sendSmsAddContactRow'>
                                <div>
                                    <UiInput
                                        id='allocation-sms-contact'
                                        inputMode='tel'
                                        disabled={loading}
                                        value={extraNumbers}
                                        onChange={event => setExtraNumbers(event.target.value)}
                                        onKeyDown={event => {
                                            if (event.key === 'Enter') {
                                                event.preventDefault()
                                                addSmsContact()
                                            }
                                        }}
                                        placeholder='Add a mobile number'
                                    />
                                    {hasInvalidExtraNumber && <p className='sendSmsContactError'>Mobile number must be 10 digits.</p>}
                                </div>
                                <Button variant="create" size="md"
                                    type='button'
                                    className='secondaryButton sendSmsAddButton'
                                    disabled={loading || hasInvalidExtraNumber || !extraNumbers.trim()}
                                    onClick={addSmsContact}>
                                    <span aria-hidden='true'>+</span> Add
                                </Button>
                            </div>
                        </div>

                        <div className='sendSmsFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton sendSmsFooterButton' onClick={closeSendMessageModal}>Cancel</Button>
                            <Button variant="secondary" size="md"
                                className='sendSmsFooterButton'
                                disabled={loading || extraNumbers.trim().length > 0 || getAllocationContactRows(showSendMessage).filter(worker => worker.inApp !== false).length + additionalSmsNumbers.length === 0}
                                onClick={handleSendMessage}>
                                Send ({getAllocationContactRows(showSendMessage).filter(worker => worker.inApp !== false).length + additionalSmsNumbers.length})
                            </Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!showContactAllocation}
                    setShowModal={setShowContactAllocation}
                    customMaxWidth='420px'
                    customContentStyle={{ width: 'calc(100% - 32px)', minWidth: 0, padding: 0, overflow: 'hidden' }}>
                    <div className='contactWorkersModal'>
                        <div className='contactWorkersHeader'>
                            <div>
                                <h2 className='contactWorkersTitle'>Contact Workers</h2>
                                <div className='contactWorkersAllocationId'>
                                    Allocation ID: {showContactAllocation?.allocationId || showContactAllocation?.id || '-'}
                                </div>
                                <div className='contactWorkersClient'>Client: {showContactAllocation?.client || '-'}</div>
                            </div>
                        </div>

                        <div className='contactWorkersBody'>
                            {getAllocationContactRows(showContactAllocation).length > 0 ? getAllocationContactRows(showContactAllocation).map(worker => (
                                <div key={worker.id} className='contactWorkerCard'>
                                    <div className='contactWorkerDetails'>
                                        <div className='contactWorkerName'>
                                            {formatName(worker.name)}{worker.isTeamLeader ? ' (TL)' : ''}
                                        </div>
                                        {worker.contactNo ? (
                                            <a className='contactWorkerNumber' href={`tel:${worker.contactNo}`}>{formatPhone(worker.contactNo)}</a>
                                        ) : (
                                            <Link className='contactWorkerAddNumber' to={`/workers?workerId=${worker.id}`}>
                                                <span aria-hidden='true'>+</span> Add mobile number
                                            </Link>
                                        )}
                                    </div>
                                    <Button variant="secondary" size="md"
                                        type='button'
                                        className='contactWorkerCopyButton'
                                        disabled={!worker.contactNo}
                                        onClick={() => copyContactNumber(worker.contactNo)}>
                                        <svg viewBox='0 0 24 24' aria-hidden='true'>
                                            <rect x='8' y='8' width='11' height='11' rx='1.5' />
                                            <path d='M16 8V5H5v11h3' />
                                        </svg>
                                        Copy
                                    </Button>
                                </div>
                            )) : (
                                <p className='contactWorkersEmpty'>No workers assigned.</p>
                            )}
                        </div>

                        <div className='contactWorkersFooter'>
                            <Button variant="secondary" size="md" className='secondaryButton contactWorkersFooterButton' onClick={() => setShowContactAllocation(false)}>Cancel</Button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!previewDocument}
                    setShowModal={setPreviewDocument}
                    customMaxWidth='900px'
                    customContentStyle={{ width: 'calc(100% - 32px)', minWidth: 0, padding: 0, overflow: 'hidden' }}
                >
                    <div className='allocationDocumentPreviewModal'>
                        <div className='allocationDocumentPreviewHeader'>
                            <h2 className='modalTitle'>{previewDocument?.name || 'Document'}</h2>
                            <Button variant="secondary" size="md"
                                type='button'
                                className='secondaryButton'
                                onClick={() => setPreviewDocument(null)}
                            >
                                Cancel
                            </Button>
                        </div>
                        <div className='allocationDocumentPreviewBody'>
                            {/\.(png|jpe?g|gif|webp|bmp|svg)(?:\?|$)/i.test(previewDocument?.url || '') ? (
                                <img src={previewDocument?.url} alt={previewDocument?.name || 'Document preview'} />
                            ) : (
                                <iframe title={previewDocument?.name || 'Document preview'} src={previewDocument?.url} />
                            )}
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent showModal={moveCompletedAllocationPromptOpen} setShowModal={setMoveCompletedAllocationPromptOpen}>
                    <h2 className='modalTitle'>Move workers with this allocation?</h2>
                    <p className='modalSubtitle'>If you choose No, the current allocation will be cancelled for the assigned workers and a new incomplete allocation will be created on the new date without workers.</p>
                    <div className='modalFooter'>
                        <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => setMoveCompletedAllocationPromptOpen(false)}>Cancel</Button>
                        <Button variant="secondary" size="md" className='secondaryButton' disabled={loading} onClick={() => handleSaveAllocation({ moveWorkersDecision: 'no' })}>No</Button>
                        <Button variant="secondary" size="md" disabled={loading} onClick={() => handleSaveAllocation({ moveWorkersDecision: 'yes' })}>Yes</Button>
                    </div>
                </ModalComponent>

                <ModalComponent
                    showModal={!!deleteAllocationPrompt}
                    setShowModal={setDeleteAllocationPrompt}
                    customMaxWidth='440px'
                    customContentStyle={{ minWidth: 0, width: 'min(440px, calc(100vw - 32px))', padding: '20px' }}
                >
                    <div className='allocationDeletePrompt'>
                        <h2 className='modalTitle'>Delete allocation?</h2>
                        <p className='modalSubtitle'>Add a cancellation comment. The allocation will move to Cancelled Allocations.</p>
                        <FieldLabel className='fieldLabel' htmlFor='allocation-delete-comment'>Comment</FieldLabel>
                        <BufferedTextarea
                            id='allocation-delete-comment'
                            className='allocationDeleteComment'
                            value={deleteAllocationComment}
                            onCommit={setDeleteAllocationComment}
                            onDraftChange={value => { deleteAllocationCommentDraftRef.current = value }}
                            placeholder='Enter cancellation reason'
                            autoFocus
                        />
                        <div className='modalFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"
                                type='button'
                                className='secondaryButton'
                                disabled={loading}
                                onClick={() => {
                                    setDeleteAllocationPrompt(null)
                                    setDeleteAllocationComment('')
                                }}
                            >
                                Cancel
                            </Button>
                            <Button data-action-tone="delete" variant="danger" size="md"
                                type='button'
                                className='modalDangerButton'
                                disabled={loading}
                                onClick={() => {
                                    const comment = deleteAllocationCommentDraftRef.current.trim()
                                    if (!comment) {
                                        toast.error('Cancellation comment is required.')
                                        return
                                    }
                                    const allocationId = deleteAllocationPrompt?.id
                                    const allocationType = deleteAllocationPrompt?.type
                                    setDeleteAllocationPrompt(null)
                                    setConfirmMessage({
                                        title: 'Are you sure?',
                                        message: 'Are you sure you want to delete this allocation? It will be moved to Cancelled Allocations.',
                                        confirmLabel: 'Yes, delete',
                                        action: () => allocationType === 'incomplete'
                                            ? handleDeleteIncompleteAllocation(allocationId, comment)
                                            : handleDeleteAllocation(allocationId, comment),
                                    })
                                }}
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                </ModalComponent>

                <CompactSuccessModal success={successMessage} setSuccess={setSuccessMessage} />

                <ModalComponent
                    showModal={!!confirmMessage}
                    setShowModal={setConfirmMessage}
                    customMaxWidth='300px'
                    customContentStyle={{ minWidth: 0, width: 'min(300px, calc(100vw - 32px))', padding: '14px' }}
                >
                    <div className='allocationConfirmMessage'>
                        <h2 className='modalTitle'>{confirmMessage?.title || 'Are you sure?'}</h2>
                        <p className='modalSubtitle'>{confirmMessage?.message}</p>
                        <div className='modalFooter'>
                            {!confirmMessage?.hideCancel && <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={() => setConfirmMessage(null)}>Cancel</Button>}
                            <Button variant="secondary" size="md" className='modalDangerButton' disabled={loading} onClick={async () => { await confirmMessage?.action?.(); setConfirmMessage(null) }}>{confirmMessage?.confirmLabel || 'Yes'}</Button>
                        </div>
                    </div>
                </ModalComponent>
            </PageContent>
        </div>
    </div >
}

export default Allocations
